import { apiRequest } from '../api/client';
import { snapshotStructure, type StructuralSnapshot } from './structuralDelta';
import * as Me from '../api/me';
import * as Todos from '../api/todos';
import * as Writes from './mapWrites';
import * as Undo from './undo';
import * as Presence from './presence';
import { S, announce, type DirectoryUser, type DirectoryGroup, type DocWorkflowEntry } from './state';
import { handles } from './handles';
import { getStoreState } from '../store';
import {
  applySidebar,
  dismissHint,
  refreshCursor,
  updateHint,
  syncAiDocFilterDot,
  applyLiveVerifyCapability,
  syncMinimapButton,
} from './chrome';
import { requestTopbarFit } from '../features/topbar/topbarFit';
import { SECTION_TAGS, SECTION_TODOS } from '../features/popup/sections';
import { readViewRecord, writeDurablePrefs, writeViewRecord } from '../store/persistence';
// Boot sequences the app; these are the parts of it regions ask for by name. The cycle is
// benign — nothing here is read while either module is evaluating.
import { attemptLogin, enterApp, establishSession, logout, setLocale } from './boot';
// The history controller (plan 1.6). Same benign cycle as boot's: the router dispatches
// through these actions, these actions tell it the location moved — and neither side reads
// the other while evaluating.
import * as Router from './router';
import * as Docs from '../lib/docs';
import * as TagsLib from '../lib/tags';
import * as Search from '../lib/search';
import * as Vis from '../lib/visibility';
import { anyExpanded as anyExpandedIn, buildGraph, withParent } from '../lib/graph';
import {
  buildHiddenSets,
  canAssignTags as canAssignTagsFor,
  canDocBtn as canDocBtnFor,
  canEditAllDocs as canEditAllDocsFor,
  canEditLinks as canEditLinksFor,
  canEditMap as canEditMapFor,
  canEditTags as canEditTagsFor,
  canEnterEdit as canEnterEditFor,
  hasRole as hasRoleIn,
  canViewLogs as canViewLogsFor,
  canViewTodos as canViewTodosFor,
  ROLES,
} from '../lib/permissions';
import * as Resp from '../lib/responsibility';
import * as Mode from '../lib/mode';
import { canAiAdminHere, capAi as capAiIn } from '../lib/capabilities';
import type { CrossLink, DocId, DocPerm, DocPlan, LayoutIntent, MapNode, NodeId, ProjectPlan, SearchScope, TagId, WorkflowState } from '../lib/types';
import { rollup } from '../lib/projects';
import { BRANCH_COLORS } from '../lib/color';
import { dayKeyOf } from '../lib/todos';
import { CAMERA_MS, type CameraOpOptions } from '../lib/camera';
import { anchors, awaitElement, beginSequence, fly, prefade, restPerch, type Anchor } from './flight';
import type { NodeHealthCounts } from '../lib/health';
import { SEARCH_DOC_KINDS, isDashboardScreen, toUrl, type DashboardScreen, type PermissionTab, type SearchDocKind } from '../lib/location';
import type { MenuTarget } from '../lib/contextMenu';
import { ROUTER_BASENAME } from '../config';

/**
 * WHAT THE APP DOES — the successor to infostation.html's script.
 *
 * Every function the incumbent had that was not a region, a storage format or a request, kept
 * under the name it had. That is not nostalgia: about fifty call sites across the ported
 * regions and the regression suite say `render()`, `openDoc()`, `applyPermissions()`,
 * `toggleCollapsed()`, and a rename would have been a second change riding along with the flip.
 *
 * ── WHAT IS NOT DUPLICATED ───────────────────────────────────────────────────────────────
 * The incumbent carried its own copies of the pure predicates. This file carries none: every
 * one of them is the lifted version in `client/src/lib/`, BOUND to live state here.
 * `childrenOf(id)` is `buildGraph(S.edges).childrenOf(id)`; `docUrlFor(id)` is
 * `Docs.docUrlFor(...)`. There is one implementation of each in the app now, and
 * `e2e/15-core-parity.spec.ts` is what proved each of them identical to the original before the
 * original was deleted.
 *
 * ── WHAT STAYS MUTATION-IN-PLACE, AND WHY ────────────────────────────────────────────────
 * `S.nodes[id].x = …` rather than a new node object. The drag path moves a subtree on every
 * pointer frame and the layout writes measured-derived positions back for 152 nodes at a time;
 * allocating a tree per frame is the thing `REACT-REWRITE-PLAN.md` exists to avoid. What makes
 * that safe is that the CONTAINERS are replaced on every publish (see `state.ts`), so identity
 * still means "this changed" to every consumer.
 *
 * ── THE WRITE PATHS ARE ELSEWHERE ────────────────────────────────────────────────────────
 * Requests are `app/mapWrites.ts`'s, storage is `store/persistence.ts`'s, the undo stack is
 * `app/undo.ts`'s and the socket is `app/presence.ts`'s. What is here is the part that knows
 * what the map IS: which fields go in a payload, what an undo entry has to restore, and what a
 * given edit means.
 */

/* ==================================================================== small helpers === */

/** A colour ramp per top-level branch, cycled by `S.nid` as nodes are created (lib/color.ts). */
const COLORS: readonly string[] = BRANCH_COLORS;
const TAG_COLORS = [
  '#5b8cff', '#1fb6a8', '#f0663f', '#e05a9c', '#8b6bff',
  '#3aa655', '#e0982a', '#e0574f', '#2bb0d8', '#c56bd8',
];

const COLW = 250;
const ROW = 58;

/** The four grounding sources a node can override, tri-state: null inherits. */
export const CONTEXT_SOURCE_KEYS = [
  'useExistingDoc',
  'useNeighborDocs',
  'useSemanticGrounding',
  'useLinkedDocs',
] as const;

/* ============================================================== permissions & gates === */

export const hasRole = (r: string): boolean => hasRoleIn(S.perm, r);
/**
 * The edit-MODE gate (V0.37): any participant brick, or the AI operator. This replaced
 * `canEdit()` — "may edit" stopped being one question when the bricks landed, and each
 * affordance inside the mode now asks its own precise one below. The page scope still
 * publishes this under the name `canEdit`, because that is the suite's contract and this IS
 * the real gate that name now means.
 */
export const canEnterEdit = (): boolean => canEnterEditFor(S.perm);
export const canEditMap = (): boolean => canEditMapFor(S.perm);
export const canEditTags = (): boolean => canEditTagsFor(S.perm);
export const canAssignTags = (): boolean => canAssignTagsFor(S.perm);
export const canEditLinks = (): boolean => canEditLinksFor(S.perm);
export const canEditAllDocs = (): boolean => canEditAllDocsFor(S.perm);
export const canUserPerms = (): boolean => hasRole(ROLES.userPermissions);
/**
 * The Backups tab (V0.48). A bare role check, and deliberately NOT implied by `canUserPerms` —
 * a restore replaces every account and document in the installation, so it is granted on
 * purpose. See `lib/permissions.ts` and `server/src/auth/roles.ts`.
 */
export const canBackupAdmin = (): boolean => hasRole(ROLES.backupAdmin);
export const capAi = (name: Parameters<typeof capAiIn>[1]): boolean => capAiIn(S.caps, name);
/**
 * AI-admin surfaces need the role AND something for them to drive.
 *
 * Doc generation is the headline capability; targets and live verification have their own,
 * checked where they are shown. Both gates, always — an air-gapped build must show a feature
 * as ABSENT rather than present-and-erroring.
 */
export const canAiAdmin = (): boolean => hasRole(ROLES.aiAdmin) && capAi('docgen');
export const canLiveVerify = (): boolean => hasRole(ROLES.aiAdmin) && capAi('liveVerify');
export const canChat = (): boolean => hasRole(ROLES.chat) && capAi('chat');
export const canPresence = (): boolean => hasRole(ROLES.presence);
/** May this person read the action log (V0.49)? Standalone — SHOW_EDIT does not imply it. */
export const canViewLogs = (): boolean => canViewLogsFor(S.perm);
export const canDocBtn = (kind: DocPerm): boolean => canDocBtnFor(S.perm, kind);

/**
 * The responsibility predicates (`lib/responsibility.ts`), bound to live state. `S` itself
 * satisfies `ResponsibilityState` structurally, so the binding is the identity — stated once
 * here so no caller builds its own partial view of the state.
 */
export const canEditDocHere = (docId: DocId, locale: string): boolean => Resp.canEditDoc(S, docId, locale);
export const canEditPromptHere = (nodeId: NodeId): boolean => Resp.canEditPrompt(S, nodeId);
export const canAssignHere = (nodeId: NodeId): boolean => Resp.canAssign(S, nodeId);
/** May THIS user create a document on this node (V0.42): the client mirror of the scoped
 *  route's gate, `EDIT_MAP ∨ mayAssignNode`. Drives whether a dotted planned pill acts. */
export const canCreateDocOn = (nodeId: NodeId): boolean => canEditMap() || canAssignHere(nodeId);

/**
 * The doc view's DERIVED shape (V0.53.5, `lib/mode.ts`), bound to live state: is the editor on
 * screen? Edit mode, a page, and this user's scope on it in the current locale. `S` satisfies
 * `ModeState` structurally, so the binding is the identity — the same arrangement as `Resp`.
 */
export const docEditorActive = (): boolean => Mode.docEditorActive(S);

/**
 * "This user's reach changed, so the MAP they are entitled to may have changed too."
 *
 * A sink rather than a direct call, because `app/mapSync.ts` owns the refetch and nothing in
 * this file may import it — the one-way rule that file's header states. `boot.ts` wires the two
 * together, exactly as it does for every other subsystem.
 */
let onPermissionsChanged: (() => void) | null = null;
export function configurePermissionSync(fn: () => void): void {
  onPermissionsChanged = fn;
}

/**
 * The two fields the map payload is a function of, as one comparable string.
 *
 * Deliberately NOT `roles` or `docButtons`: the server projects the map from `hiddenNodes` and
 * `hiddenTags` alone (see `auth/visibilityCore.ts`, which takes only those), so these two are a
 * COMPLETE invalidation key. Widen what the projection depends on and this quietly stops being
 * one — which is why the server-side type is narrow on purpose.
 */
function hiddenKey(perm: typeof S.perm): string {
  const nodes = [...(perm?.hiddenNodes ?? [])].sort().join(',');
  const tags = [...(perm?.hiddenTags ?? [])].sort().join(',');
  return nodes + '|' + tags;
}

/** Recompute the resolved hidden sets from `perm`. Hidden TAGS expand to their descendants. */
export function refreshHiddenSets(): void {
  const hidden = buildHiddenSets(S.perm, S.tagEdges);
  S.hiddenNodeSet = new Set(hidden.nodes);
  S.hiddenTagSet = new Set(hidden.tags);
}

/** A node is permission-hidden if it, or any ancestor, is a hidden branch root. */
export function permHidden(id: NodeId): boolean {
  if (!S.hiddenNodeSet.size) return false;
  let cur: NodeId | undefined = id;
  let guard = 0;
  while (cur !== undefined && guard++ < 10_000) {
    if (S.hiddenNodeSet.has(cur)) return true;
    cur = S.edges[cur];
  }
  return false;
}

export const tagHidden = (id: TagId): boolean => S.hiddenTagSet.has(id);

/* ==================================================================== graph helpers === */

export const childrenOf = (id: NodeId): NodeId[] => [...buildGraph(S.edges).childrenOf(id)];
export const descendants = (id: NodeId): NodeId[] => buildGraph(S.edges).descendants(id);

const visContext = () => ({
  nodes: S.nodes,
  edges: S.edges,
  hidden: { nodes: S.hiddenNodeSet, tags: S.hiddenTagSet },
  forceOpen: S.forceOpen,
});

export const isHidden = (id: NodeId): boolean => Vis.isHidden(id, visContext());

/* ================================================================== the sync points === */

/**
 * "The state moved, show it."
 *
 * The theme class stays here because it is on `<body>`, which no region owns. Both other names
 * mean exactly the same thing and exist because their call sites do: `drawWires()` is what the
 * links toggle and the cross-link menu say, `renderTags()` is what twenty tag edits say.
 */
export function render(): void {
  document.body.classList.toggle('light', S.isLight);
  announce();
}
export function drawWires(): void {
  announce();
}
export function renderTags(): void {
  announce();
}

/* ================================================================ the map's surface === */

/**
 * `autoLayout()` / `resolveCollisions()` — queue a layout pass, saying WHY.
 *
 * The intent is a required argument (plan Part 2). `x/y/side/depth` are shared server state,
 * and canonical layout is a function of the collapse state — so a collapse moves every
 * sibling's coordinates exactly as a drag does, and only the caller knows which of the two
 * this is. `'structural'` publishes what moved; `'view'` keeps it to this browser.
 */
export const autoLayout = (intent: LayoutIntent): void => handles.map?.relayout(intent);
/** Re-derive whether the documentation rows are showing (V0.42.2). Both inputs — the zoom
 *  threshold and the `docsPinned` toggle — are combined in `evaluateDocsExpand()`, which this
 *  asks the map to re-run after the toggle moves. */
export const syncDocsExpand = (): void => handles.map?.syncDocsExpand();
export const resolveCollisions = (anchorId: NodeId | null, intent: LayoutIntent): void =>
  handles.map?.settle(anchorId ?? null, intent);
export const fit = (opts?: CameraOpOptions): void => handles.map?.fit(opts);
export const centerOnNodes = (ids: NodeId[], opts?: CameraOpOptions): void =>
  handles.map?.centerOnNodes(ids ?? [], opts);
export function zoomBtn(f: number): void {
  handles.map?.zoomBy(f);
  saveLocal({ quiet: true }); // a zoom is view-only, and moving the view is not a save
}
export const mapGetCamera = (): { x: number; y: number; k: number } =>
  handles.map?.getCamera() ?? { x: 0, y: 0, k: 1 };
export const mapSetCamera = (
  v: { x: number; y: number; k: number },
  opts?: { animate?: boolean },
): void => handles.map?.setCamera(v, opts);

/** Is any branch open? The toggle's whole state, bound to live state — see `lib/graph.ts`. */
export const anyExpanded = (): boolean => anyExpandedIn(S.nodes, S.edges);

/* ======================================================================= persistence === */

/**
 * Everything a save does that stays in THIS BROWSER: durable prefs, the "Saved" badge, and
 * this user's day-scoped view record.
 *
 * THE SPLIT (V0.34, plan Part 1). `save()` used to be one function, and twelve of its 47 call
 * sites are pure per-user view state — panning, zooming, collapsing, the theme, the sidebar.
 * Each of them ended in a debounced `PUT /api/map` carrying the whole structural map, which
 * bumped `dataVersion` for everybody else; their next write then came back 409 and the page
 * reloaded under them. Moving the camera was enough to do that to a colleague.
 *
 * THE "Saved" TOAST STAYS HERE, on purpose. It has never meant "the server has your edit" —
 * it fires 800 ms before any request, and fired identically when that request later 409'd.
 * `e2e/17-bridge-and-toast.spec.ts` drives it with the theme toggle, which is one of the
 * twelve, and passes unedited because the toast belongs to this half.
 *
 * `quiet` PERSISTS EXACTLY THE SAME THINGS AND SAYS NOTHING (V0.49.2). The badge answers one
 * question — "the thing you just changed has been kept" — and NAVIGATION changes nothing to
 * keep. Panning the map, zooming it, and opening or closing a branch move where this person
 * is looking; they alter no content, they are not on the wire, and nobody asked for them to
 * be saved. Badging them taught the opposite of what the badge is for: it fired constantly,
 * for nothing, which is how a real save stops being noticed. The view record is still written
 * on every one of them, so a reload still lands where the reader was.
 *
 * This is the same correction V0.46 made for the animated fit ("badging the same gesture
 * twice, 600 ms apart, reads as a second save that never happened") and V0.48.1 made for the
 * links toggle, generalised to the rest of the navigation gestures rather than fixed one at
 * a time.
 */
export function saveLocal(opts?: { quiet?: boolean }): void {
  try {
    writeDurablePrefs({
      isLight: S.isLight,
      sidebarOpen: S.sidebarOpen,
      sidebarWidth: S.sidebarWidth,
      editing: S.editing,
      searchScope: S.searchScope,
      minimapOn: S.minimapOn,
      minimapBright: S.minimapBright,
    });
    if (!opts?.quiet) handles.toast?.show();
  } catch {
    /* a lost preference must never break a save */
  }
  saveViewState();
}

/**
 * The single choke point every EDIT funnels through: everything `saveLocal()` does, plus the
 * debounced structural sync.
 *
 * It keeps its name for the ~35 genuine edit sites and the ~100 `callAction('save')` strings.
 * A caller that is not changing shared map content wants `saveLocal()` instead.
 */
export function save(): void {
  saveLocal();
  scheduleServerSync();
}

/**
 * The branches this user has open — the EXCEPTIONS to "collapsed", which is the only half of
 * the collapse state that is ever stored.
 *
 * Its own function because two callers have to agree on it: the persisted view record below,
 * and the in-memory capture a quiet refresh takes before replacing the map under itself. Two
 * hand-written loops that drifted apart would restore a different tree than they saved.
 */
export function openBranchIds(): NodeId[] {
  const openIds: NodeId[] = [];
  for (const id in S.nodes) {
    const n = S.nodes[id];
    if (!n.root && !n.collapsed && childrenOf(id).length) openIds.push(id);
  }
  return openIds;
}

/**
 * Persist this user's view state.
 *
 * DELIBERATELY not part of the server sync and deliberately without a "Saved" badge: this is
 * personal view state, never map content, and it is called on every search keystroke — routing
 * it through the durable save would mean a `PUT /api/map` per character typed.
 */
export function saveViewState(): void {
  if (!S.currentUser || !S.viewStateReady) return;
  writeViewRecord(S.currentUser.id, {
    // WHILE A FILTER IS ACTIVE THIS RECORDS WHERE THE READER ACTUALLY WAS (V0.46), not the
    // frame the filter put them in. The framing camera is the answer to a question, and it
    // is thrown away the moment the question is withdrawn — persisting it would mean a
    // reload landed somewhere nobody chose, and clearing the search afterwards had nowhere
    // to go back to.
    view: (filterActive() && filterReturnCamera) || mapGetCamera(),
    // Only the EXCEPTIONS to "collapsed": an id unknown on restore is ignored, so a node added
    // since the last visit defaults to collapsed rather than springing open.
    openIds: openBranchIds(),
    activeTags: [...S.activeTags],
    searchQuery: S.searchQuery,
    aiDocMode: S.aiDocMode,
    heatmapOn: S.heatmapOn,
    docsPinned: S.docsPinned,
    activeDocStatus: S.activeDocStatus,
    // The EXCEPTIONS only — see ViewRecord.collapsedSections for why the shut ones are what
    // is stored rather than the open ones.
    collapsedSections: [...S.collapsedSections],
    // …and the popup's folds, whose default is the OTHER way round, so its exceptions are the
    // OPENED ones. Two lists, each named for what it holds — see ViewRecord.openSections.
    openSections: [...S.openSections],
  });
}

/**
 * Restore this user's view, but only within the same work day.
 *
 * Returns `{restored, camera}` separately because the caller's `fit()` decision is about the
 * camera alone — an in-day record with an unusable camera must still centre.
 *
 * MUST run after `loadMapFromServer()` (which re-collapses everything) and after
 * `applyPermissions()` (which populates the hidden sets this validates against).
 */
export function restoreViewState(): { restored: boolean; camera: boolean } {
  const NONE = { restored: false, camera: false };
  if (!S.currentUser) return NONE;
  // Answers null for a record from a previous work day, and DELETES it as it reads — so "is
  // there a record" and "will it be used" cannot become two questions.
  const rec = readViewRecord(S.currentUser.id);
  if (!rec) return NONE;

  // THE PRECEDENCE RULE (plan 1.6): URL > stored view state > defaults. A filter the arrival
  // URL names EXPLICITLY — present-and-empty included, that is the whole absent-vs-empty
  // distinction — is not restored: the link's filters must win over the recipient's own, and
  // an absent param is exactly what lets the stored value keep applying.
  const fromUrl = Router.urlFilterOverrides();

  // Expanded branches. Anything deleted or now permission-hidden is simply skipped.
  for (const id of rec.openIds ?? []) {
    if (S.nodes[id] && !S.nodes[id].root && !permHidden(id)) S.nodes[id].collapsed = false;
  }
  // Tag filter — validated tag by tag: one deleted (or now group-hidden) since yesterday must
  // not take the rest of the selection with it.
  if (!fromUrl.tag) {
    const restoredTags = (rec.activeTags ?? []).filter((t) => S.tags[t] && !tagHidden(t));
    if (restoredTags.length) S.activeTags = new Set(restoredTags);
  }
  // Search text. Only state is set here; applySearch() runs back in enterApp() once the tree
  // has been rendered, since it re-lays the layout.
  if (!fromUrl.q && rec.searchQuery) {
    S.searchQuery = rec.searchQuery;
    S.searchText = rec.searchQuery;
  }
  // AI-doc mode and its stage filter. setAiDocMode() self-gates on editing && canAiAdmin(),
  // so a user who has lost the role gets neither. The stored value is validated against the
  // CURRENT stage table: a record written before V0.38 may carry a retired docStatus value
  // ('proofread', 'doc_generated'…), and an unknown key restores as "no filter" rather than
  // a filter nothing can match — the VIEWSTORE is day-scoped, so such values age out anyway.
  if (rec.aiDocMode) {
    setAiDocMode(true);
    if (
      S.aiDocMode &&
      !fromUrl.status &&
      rec.activeDocStatus &&
      Docs.PIPELINE_LABEL[rec.activeDocStatus]
    ) {
      S.activeDocStatus = rec.activeDocStatus;
      syncAiDocFilterDot();
    }
  }
  // The clearance heatmap, same rules as aiDocMode above: setHeatmap() self-gates on
  // editing && canEnterEdit(), and a `?heat=` the URL names explicitly — empty included —
  // wins over the stored value (absent-vs-empty, the codec's rule).
  if (rec.heatmapOn && !fromUrl.heat) setHeatmap(true);
  // The documentation-row pin, day-scoped beside it. Restored AFTER the heatmap so a stored
  // `docsPinned: false` is not overwritten by the heatmap's own switch-on above.
  if (rec.docsPinned !== undefined) setDocsPinned(!!rec.docsPinned);
  // Camera. The clamp lives in the store's camera slice, which is what `mapSetCamera` goes
  // through — the three copies of it that used to sit here, at the wheel and at the pinch
  // handler are one.
  const v = rec.view;
  const camera = !!v && [v.x, v.y, v.k].every((n) => typeof n === 'number' && isFinite(n));
  if (camera) mapSetCamera({ x: v.x, y: v.y, k: v.k });

  // Folded sections (V0.50). No validation against a list of known keys, deliberately: an
  // unknown key is a section this build no longer has, and it costs nothing to carry until the
  // record ages out at 04:00 — whereas a hardcoded list here would be a second copy of which
  // sections exist, free to disagree with the components that draw them.
  if (rec.collapsedSections?.length) S.collapsedSections = new Set(rec.collapsedSections);
  // The popup's opened folds (V0.51), same reasoning and the same absence of validation.
  if (rec.openSections?.length) S.openSections = new Set(rec.openSections);

  // forceOpen is derived, never persisted.
  S.forceOpen = computeForceOpen();
  return { restored: true, camera };
}

/* ====================================================================== server sync === */

let serverSyncTimer: ReturnType<typeof setTimeout> | null = null;

export function scheduleServerSync(): void {
  if (!S.currentUser) return; // no session yet
  if (serverSyncTimer) clearTimeout(serverSyncTimer);
  serverSyncTimer = setTimeout(() => {
    // Nulled FIRST, before anything else can happen: this handle is also the answer to "is
    // a write still owed", and a timer that has already fired must not go on saying yes.
    serverSyncTimer = null;
    void syncToServer();
  }, 800);
}

/** Is an edit still sitting in the debounce? A quiet refresh must not cut across one. */
export const serverSyncPending = (): boolean => serverSyncTimer !== null;

/**
 * Drop the debounce without running it. Used when the session has ended: whatever was owed
 * belongs to a session that no longer exists, and after re-login the map is reloaded from the
 * server anyway — so flushing it would push stale values back over the server's own.
 *
 * Returns whether anything was actually waiting, so the caller can SAY so.
 */
export function cancelServerSync(): boolean {
  if (!serverSyncTimer) return false;
  clearTimeout(serverSyncTimer);
  serverSyncTimer = null;
  return true;
}

/**
 * Run the debounced sync NOW and wait for it.
 *
 * The debounce is right for a drag or a rename — a keystroke should not be a request. It is
 * wrong for pressing Save, which is a person saying "that's done": the write sat in an
 * 800 ms timer, so leaving the page immediately afterwards lost it, and the browser's
 * "Leave site?" prompt was the only hint.
 *
 * Also what lets `refreshBlockedBy`'s 'edits-owed' clear promptly rather than on the timer.
 */
export async function flushNow(): Promise<void> {
  if (serverSyncTimer) {
    clearTimeout(serverSyncTimer);
    serverSyncTimer = null;
  }
  await syncToServer();
}

export async function syncToServer(): Promise<void> {
  // What is owed is decided ONCE, here, rather than by each flush as it is reached: the
  // structural resync of this map takes ~500 ms, so three sequential drains would answer that
  // question at three moments spread across seconds of network time. An edit made from now on
  // belongs to the NEXT sync, always.
  const owed = Writes.drainOwed();
  try {
    await pushStructuralSync();
    await flushDirtyTranslations(owed);
    await flushDirtyAiFields(owed);
  } catch (e) {
    console.error('Server sync failed:', e);
  }
}

export const pushStructuralSync = (): Promise<void> => Writes.pushStructuralSync();

/**
 * The live map, read into the snapshot a structural delta is diffed against.
 *
 * This replaced `structuralSyncBody()`, which built the WHOLE map as a payload on every
 * save. What travels now is the difference between this snapshot and the baseline — "what
 * the server has because of us" — which `app/structuralDelta.ts` computes and
 * `app/mapWrites.ts` holds. Read at FLUSH time, not at request time, because a trailing run
 * has to send state as it is when it runs.
 *
 * `snapshotStructure` takes the state as a PARAMETER rather than importing `S`, which is what
 * keeps that module pure and unit-testable. This one line is where it is bound to the app.
 */
export const structuralSnapshot = (): StructuralSnapshot => snapshotStructure(S);

export const flushDirtyTranslations = (owed?: Writes.OwedWork): Promise<void> =>
  Writes.flushDirtyTranslations(
    S.currentLocale,
    (id) => {
      const n = S.nodes[id];
      return n ? { label: n.label, desc: n.desc || '' } : null;
    },
    (id) => S.tags[id]?.name ?? null,
    owed,
    adoptNodeSlug,
  );

/**
 * A rename came back carrying a NEW slug — take it.
 *
 * A node created through the UI is called "New node" and gets the slug that implies
 * (`new-node-7`). Naming it re-slugs it, ONCE, server-side (V0.45) — so the response to a
 * translation PUT is the one moment this side can learn its real address. Without this the
 * URL kept showing the placeholder, or the cuid fallback, for a node whose name is right
 * there on screen, until a reload.
 *
 * `S.nodes` is REPLACED, not just mutated: `router.ts` caches `nodeIdBySlug` on that
 * container's identity, and only `loadMapFromServer()` replaces it otherwise — so a mutation
 * alone would leave the reverse lookup answering about the old slug.
 */
export function adoptNodeSlug(id: NodeId, response: unknown): void {
  const body = response as { slug?: unknown; slugDrifted?: unknown; slugFeedChanged?: unknown } | null;
  const n = S.nodes[id];
  if (!n) return;

  /**
   * THE MARK, from the rename's own response.
   *
   * `slugDrifted` normally arrives on `GET /api/map`, and a client never refetches the map for
   * its own write — the response's `dataVersion` is adopted here and `mapSync` self-suppresses
   * on it, which is what stops everybody refreshing after their own save. So renaming a node
   * left the "!" absent until a reload: the fact existed on the server and had no way home.
   * It rides the response now (see routes/nodes.ts) and is applied whether or not the slug
   * moved, because a rename that leaves the address behind is exactly the case that needs it.
   */
  if (typeof body?.slugDrifted === 'boolean' && n.slugDrifted !== body.slugDrifted) {
    n.slugDrifted = body.slugDrifted;
    render();
  }

  /**
   * AND THE BELL — on what the SERVER says it wrote, never on the mark moving.
   *
   * The badge is re-asked at four moments: sign-in, window focus, a doc save, and here. Never
   * a timer — no polling is a design rule, not an optimisation — so a rename that enqueues a
   * `slug_stale` row has to say so on its own response, or the count waits for a focus or a
   * reload. The route awaits the write before answering, so what this reads is already true.
   *
   * The first attempt keyed this on the mark APPEARING, which is a different question and the
   * wrong one. It missed both directions that were then reported: renaming an ALREADY-drifted
   * node enqueues a fresh row and moves no mark, and renaming one BACK to a matching name
   * deletes rows while moving the mark the other way. `slugFeedChanged` is the server
   * answering the only question a badge has — did the count move.
   */
  if (body?.slugFeedChanged === true) void refreshFeedBadge();

  const slug = body?.slug;
  if (typeof slug !== 'string' || !slug || n.slug === slug) return;
  n.slug = slug;
  S.nodes = { ...S.nodes };
  render();
  // REPLACE, never push: it is the same place, newly addressable.
  Router.restateUrl();
}

/**
 * AI-admin-only fields ride their own `PUT /api/nodes/:id`.
 *
 * Deliberately kept out of the bulk resync: that payload resends every node's current values
 * on every edit, which would wrongly trip the server's AI_ADMIN check for a plain SHOW_EDIT
 * user doing something unrelated.
 */
export const flushDirtyAiFields = (owed?: Writes.OwedWork): Promise<void> =>
  Writes.flushDirtyAiFields((id) => {
    const n = S.nodes[id];
    if (!n) return null;
    // The responsible family is aiPrompt ALONE since V0.38 — docStatus died with the
    // workflow unification, and a body still carrying it is a 400.
    const body: Writes.AiFieldsBody = {
      aiPrompt: n.aiPrompt || null,
    };
    // The MECHANICS family goes only where the server would accept it (V0.37): the presence
    // of any of these keys routes the body through the AI_ADMIN gate, so a responsible whose
    // edit is only a prompt must not carry them — even unchanged, they would 403 the write.
    if (hasRole(ROLES.aiAdmin)) {
      body.liveProbeEnabled = !!n.liveProbeEnabled;
      body.appTargetId = n.appTargetId || null;
      body.appPath = n.appPath || null;
      // Context overrides send `null` (inherit) as a real null — `||`-style defaulting here
      // would silently turn every inherit into an explicit false.
      for (const k of CONTEXT_SOURCE_KEYS) {
        const v = (n as unknown as Record<string, unknown>)[k];
        body[k] = v === true || v === false ? v : null;
      }
    }
    return body;
  }, owed);

/**
 * Replace one node's responsibles (V0.37) — an IMMEDIATE act via its own endpoint, never a
 * dirty set: the popup's Save calls this beside its other flushes, the server bumps
 * `dataVersion` (assignments ride the map payload), and the adopted response is the truth.
 * A refusal is SPOKEN — the 400 for an ineligible assignee names the person, and swallowing
 * that sentence would leave the picker looking saved.
 */
export async function setNodeResponsibles(
  id: NodeId,
  userIds: string[],
  groupIds: string[] = [],
): Promise<boolean> {
  const n = S.nodes[id];
  if (!n) return false;
  try {
    const res = await Writes.putNodeResponsibles(id, userIds, groupIds);
    n.responsibles = res.responsibles;
    n.responsibleGroups = res.responsibleGroups;
    render();
    return true;
  } catch (e) {
    const msg = (e as { body?: { error?: string } } | null)?.body?.error;
    alert(msg || 'Could not update the responsibles for this node.');
    render();
    return false;
  }
}

/**
 * Write one node's documentation plan (V0.38) — `setNodeResponsibles`'s contract exactly:
 * an immediate act on its own endpoint, the returned value (and `dataVersion`, adopted inside
 * `putNodeDocPlan`) is the truth, and a refusal is SPOKEN rather than swallowed.
 */
export async function setDocPlan(id: NodeId, plan: DocPlan | null): Promise<boolean> {
  const n = S.nodes[id];
  if (!n) return false;
  try {
    const res = await Writes.putNodeDocPlan(id, plan);
    n.expectedDocs = res.expectedDocs;
    render();
    return true;
  } catch (e) {
    const msg = (e as { body?: { error?: string } } | null)?.body?.error;
    alert(msg || 'Could not update the documentation plan for this node.');
    render();
    return false;
  }
}

/**
 * Mark a node as a project, change its dates, or unmark it (V0.50.3) — `setDocPlan`'s
 * contract exactly: an immediate act on its own endpoint, the returned value (and
 * `dataVersion`, adopted inside `putNodeProject`) is the truth, a refusal is SPOKEN. Then the
 * to-do counter moves, because the Projects tab, the To-dos section and the heatmap figures all
 * re-read on it — one counter rather than three surfaces told separately.
 */
export async function setNodeProject(id: NodeId, plan: ProjectPlan | null): Promise<boolean> {
  const n = S.nodes[id];
  if (!n) return false;
  try {
    const res = await Writes.putNodeProject(id, plan);
    n.project = res.project;
    notifyTodosChanged();
    render();
    return true;
  } catch (e) {
    const msg = (e as { body?: { error?: string } } | null)?.body?.error;
    alert(msg || 'Could not update the project mark for this node.');
    render();
    return false;
  }
}

/**
 * Take a project mark another read has just seen (the To-dos section's `GET /api/nodes/:id/todos`
 * carries `project` beside the list): if it differs from what the map payload said, the payload
 * is stale — a colleague marked or unmarked since — and this node adopts the newer fact without
 * waiting for the next map reload. A no-op when they agree.
 */
export function adoptNodeProject(id: NodeId, project: ProjectPlan | null): void {
  const n = S.nodes[id];
  if (!n) return;
  const have = n.project ?? null;
  if (JSON.stringify(have) === JSON.stringify(project)) return;
  n.project = project;
  render();
}

/**
 * The figures behind the map's heatmap underline (V0.50.3): per project node, its subtree's
 * to-dos done / total, rolled up through `lib/projects.ts` from `GET /api/projects` — the
 * Projects tab's own read, so the bar and the tab can never disagree. Self-gates on VIEW_TODOS
 * like `loadMyTodos()`: without the permission there are no figures, and the map draws the mark
 * alone. The container is REPLACED, never mutated, so a publish can see it moved.
 */
export async function loadProjectProgress(): Promise<void> {
  if (!canViewTodosFor(S.perm)) {
    if (Object.keys(S.projectProgress).length) {
      S.projectProgress = {};
      render();
    }
    return;
  }
  try {
    const { projects, todos } = await Todos.fetchProjects();
    const today = dayKeyOf(new Date());
    const next: Record<string, { done: number; total: number }> = {};
    for (const p of projects) {
      const r = rollup(S.edges, p.nodeId, todos, today).get(p.nodeId);
      if (r) next[p.nodeId] = { done: r.subtree.done, total: r.subtree.total };
    }
    S.projectProgress = next;
  } catch {
    // Signed out mid-flight, offline, or a server that predates V0.50.2: the mark stands alone.
  }
  render();
}

/**
 * Write one node's documentation location inside its application (V0.46) — `setDocPlan`'s
 * contract, minus the version: `docPath` is not on the `/api/map` payload, so nothing else in
 * the room is rendering it and there is no `dataVersion` to adopt. A refusal is SPOKEN.
 *
 * NOT MIRRORED INTO `S.nodes`, deliberately. The map payload does not carry `docPath`, so
 * there is no field to keep in step — the popup's editor holds the value it loaded and the
 * server holds the truth, which is exactly one copy each. Inventing a client-side mirror would
 * make a node object that disagrees with `/api/map` about what a node has.
 */
export async function setDocPath(id: NodeId, docPath: string | null): Promise<boolean> {
  if (!S.nodes[id]) return false;
  try {
    await Writes.putNodeDocPath(id, docPath);
    return true;
  } catch (e) {
    const msg = (e as { body?: { error?: string } } | null)?.body?.error;
    alert(msg || 'Could not update the documentation location for this node.');
    return false;
  }
}

/**
 * Persist a plan DRAFT, but only if it actually differs from the stored one (V0.43.2).
 *
 * ONE HOME FOR THE COMPARISON. `savePopup` has always asked "did the drawn editor change the
 * plan?" before writing, because the plan rides its own immediate endpoint rather than the
 * debounced structural delta — an unconditional write would bump `dataVersion` for every Save
 * of a node whose plan nobody touched. The node panel's rows are about to need the same
 * question from a second place, so it lives here rather than being written twice and drifting.
 *
 * Returns whether it is now SAFE TO ACT on the plan: true when the write landed or was
 * unnecessary, false when the server refused it. `setDocPlan` has already spoken the refusal,
 * so a caller that gets false should simply not proceed — it must not create a document
 * against a promise the server declined to record.
 */
export async function commitDocPlan(id: NodeId, plan: DocPlan | null): Promise<boolean> {
  const n = S.nodes[id];
  if (!n) return false;
  if (JSON.stringify(plan ?? null) === JSON.stringify(n.expectedDocs ?? null)) return true;
  return setDocPlan(id, plan);
}

/**
 * Replace one doc page+locale's responsibles (V0.38) — the WorkflowControl popover's save.
 * The adopted answer lands on the `docWorkflow` entry so the popover, the pill dots and the
 * mirror all read the write back without a refetch. The stored `responsibles` list is the
 * server's vocabulary — eligible users with groups EXPANDED — so the direct users from the
 * response are widened by the chosen groups' eligible members from the directory, the same
 * expansion the next workflows fetch would deliver.
 */
export async function setDocResponsibles(
  docId: DocId,
  locale: string,
  userIds: string[],
  groupIds: string[],
): Promise<boolean> {
  const entry = S.docWorkflow[docId];
  try {
    const res = await Writes.putDocResponsibles(docId, locale, userIds, groupIds);
    if (entry && entry.locale === locale) {
      const expanded = new Set(res.responsibles);
      for (const gid of res.responsibleGroups) {
        const g = S.groupDirectory.find((x) => x.id === gid);
        for (const uid of g?.memberIds ?? []) {
          if (S.userDirectory.find((u) => u.id === uid)?.canBeDocResponsible) expanded.add(uid);
        }
      }
      S.docWorkflow = {
        ...S.docWorkflow,
        [docId]: { ...entry, responsibles: [...expanded].sort(), responsibleGroups: res.responsibleGroups },
      };
    }
    render();
    return true;
  } catch (e) {
    const msg = (e as { body?: { error?: string } } | null)?.body?.error;
    alert(msg || 'Could not update the responsibles for this document.');
    render();
    return false;
  }
}

export const createNodeOnServer = (fields: Writes.NewNodeFields): Promise<NodeId> =>
  Writes.createNodeOnServer(S.currentLocale, fields);
export const createTagOnServer = (fields: Record<string, unknown>): Promise<TagId> =>
  Writes.createTagOnServer(S.currentLocale, fields);
export const deleteNodeOnServer = (id: NodeId): Promise<void> => Writes.deleteNodeOnServer(id);
export const deleteTagOnServer = (id: TagId): Promise<void> => Writes.deleteTagOnServer(id);

/* ======================================================================== the map data === */

/** Server-shaped node -> internal shape, with the four slot URLs derived from the doc ids. */
function mapNodeFromServer(n: MapNode): MapNode {
  return {
    ...n,
    link: n.docId ? docUrlFor(n.docId) : '',
    linkInternal: n.internalDocId ? docUrlFor(n.internalDocId) : '',
    linkCourse: n.courseDocId ? docUrlFor(n.courseDocId) : '',
    linkDemo: n.demoDocId ? docUrlFor(n.demoDocId) : '',
    customDocs: n.customDocs || [],
  };
}

interface MapPayload {
  lang?: string;
  docTitles?: Record<string, string>;
  docTypes?: Record<string, Docs.DocTypeDef['key']>;
  docUrls?: Record<string, string>;
  docSlugs?: Record<string, string>;
  /** Sparse, V0.45.1 — only the ids whose address no longer follows their name. */
  slugDrift?: Record<string, true>;
  docSlugDrift?: Record<string, true>;
  /** Former addresses that still resolve, slug -> id. Absent when there are none. */
  nodeSlugAliases?: Record<string, string>;
  docSlugAliases?: Record<string, string>;
  docAiGenerated?: Record<string, boolean>;
  nodes: Record<NodeId, MapNode>;
  edges?: Record<NodeId, NodeId>;
  links?: CrossLink[];
  tags?: Record<TagId, { id: TagId; name: string; [k: string]: unknown }>;
  tagEdges?: Record<TagId, TagId>;
  dataVersion?: number;
  /** Present and true when the server withheld nodes from this payload (V0.43). */
  mapFiltered?: boolean;
}

export async function loadMapFromServer(): Promise<boolean> {
  const d = await apiRequest<MapPayload>('/api/map', {
    query: { lang: S.currentLocale },
    cache: 'no-store',
  });
  // The server's RESOLVED locale, which may differ from the one asked for.
  S.currentLocale = d.lang || S.currentLocale;
  S.docTitles = d.docTitles || {};
  S.docTypes = (d.docTypes || {}) as typeof S.docTypes; // before mapNodeFromServer: docUrlFor reads both
  S.docUrls = d.docUrls || {};
  S.docSlugs = d.docSlugs || {}; // deep links: /{locale}/doc/{slug} resolves through this
  S.docSlugDrift = d.docSlugDrift || {};
  // REPLACED whole, like every other container: `router.ts` caches its reverse lookups on
  // the identity of what it was given, and these feed them.
  S.nodeSlugAliases = d.nodeSlugAliases || {};
  S.docSlugAliases = d.docSlugAliases || {};

  S.docAiGenerated = d.docAiGenerated || {};

  // BUILT LOCALLY, THEN ASSIGNED ONCE. The map is replaced by two whole-container writes
  // rather than emptied and refilled, so there is no moment — not even one an `await` added
  // later could land in — where `S.nodes` exists and `S.edges` does not yet agree with it. A
  // quiet refresh does this under a popup that is open on one of these nodes.
  const nextNodes: typeof S.nodes = {};
  const drift = d.slugDrift || {};
  for (const id in d.nodes) {
    nextNodes[id] = mapNodeFromServer(d.nodes[id]);
    // Sparse on the wire, a plain flag on the node — the popup has the node, not the map.
    nextNodes[id].slugDrifted = drift[id] === true;
  }
  // EVERY EDGE MUST LAND ON A NODE THIS PAYLOAD CONTAINS.
  //
  // The server projects a connected tree — a node is only sent if it was reached through its
  // parent — so on a current server this drops nothing. It is here for the cases that are not
  // that: a server one release older, a row whose `parentId` dangles, and the next projection
  // somebody adds. An edge to an absent parent does not crash anything; it produces a node the
  // layout never reaches, which then renders detached and has its stored y rewritten on every
  // realign. Silent, and visible only to restricted users. One line is a cheap belt.
  const nextEdges: typeof S.edges = {};
  for (const child in d.edges || {}) {
    const parent = d.edges![child];
    if (d.nodes[parent]) nextEdges[child] = parent;
  }
  S.nodes = nextNodes;
  S.edges = nextEdges;
  S.links = d.links || [];
  S.tags = (d.tags || {}) as typeof S.tags;
  S.tagEdges = d.tagEdges || {};
  S.dataVersion = d.dataVersion || 1;
  S.mapFiltered = !!d.mapFiltered;
  S.activeTags = new Set();
  S.forceOpen = new Set();
  // Every session starts fully collapsed (one level deep from the root) regardless of what is
  // stored — collapse and expand are personal, transient view state, never synced. Positions
  // remain shared, and are server state.
  for (const id in S.nodes) if (!S.nodes[id].root) S.nodes[id].collapsed = childrenOf(id).length > 0;
  // Purely local counters now, for cycling colours on new entities: ids are server-assigned.
  S.nid = Object.keys(S.nodes).length;
  S.tid = Object.keys(S.tags).length;
  Undo.clearUndo(); // stale entries would reference pre-reload state
  // THE BASELINE AND THE VERSION, TOGETHER. A delta is a diff against "what the server has
  // because of us", and this is the moment that becomes exactly "what the server has".
  // Without it the first save after any load diffs against an empty baseline and re-sends
  // the whole map — the behaviour this release exists to remove.
  Writes.rebaseStructural(structuralSnapshot(), S.dataVersion);
  return true;
}

/** Keep the local type/URL maps in step with what the server just said about one document. */
export function noteDocMeta(docId: DocId, docType?: string, sourceUrl?: string | null): void {
  if (docType && docType !== 'standard') S.docTypes[docId] = docType as (typeof S.docTypes)[string];
  else delete S.docTypes[docId];
  if (sourceUrl) S.docUrls[docId] = sourceUrl;
  else delete S.docUrls[docId];
}

/* ============================================================== documentation basics === */

export const docTypeOf = (docId: DocId) => Docs.docTypeOf(S.docTypes, docId);
/** The one place a documentation URL is built. Every consumer treats `link*` as opaque. */
export const docUrlFor = (docId: DocId): string =>
  Docs.docUrlFor(docId, { docUrls: S.docUrls, locale: S.currentLocale });
export const pipelineStageOf = (n: MapNode) => Docs.pipelineStageOf(n);
export const firstVisibleDocOf = (n: MapNode) => Docs.firstVisibleDocOf(n, canDocBtn);

/* ============================================================= dotted-link menu (edit) === */

let linkMenuEl: HTMLElement | null = null;

function linkMenuOutside(e: Event): void {
  if (linkMenuEl && !linkMenuEl.contains(e.target as Node)) closeLinkMenu();
}

export function closeLinkMenu(): void {
  if (!linkMenuEl) return;
  linkMenuEl.remove();
  linkMenuEl = null;
  document.removeEventListener('pointerdown', linkMenuOutside, true);
}

/** Recolour or delete one cross-link. A floating menu, owned by nothing else. */
export function openLinkMenu(i: number, clientX: number, clientY: number): void {
  closeLinkMenu();
  const lk = S.links[i];
  if (!lk) return;
  const cur = typeof lk[2] === 'string' && /^#[0-9a-fA-F]{6}$/.test(lk[2]) ? lk[2] : '#5b8cff';
  const m = document.createElement('div');
  m.className = 'linkmenu';
  linkMenuEl = m;
  m.innerHTML =
    `<span class="lm-title">Dotted link</span>` +
    `<label class="lm-row"><span>Colour</span><input type="color" class="lm-color" value="${cur}"></label>` +
    `<div class="lm-btns"><button class="lm-reset">Default</button><button class="lm-del">Delete</button></div>`;
  document.body.appendChild(m);
  const r = m.getBoundingClientRect();
  m.style.left = Math.max(8, Math.min(clientX + 6, window.innerWidth - r.width - 8)) + 'px';
  m.style.top = Math.max(8, Math.min(clientY + 6, window.innerHeight - r.height - 8)) + 'px';
  const ci = m.querySelector('.lm-color') as HTMLInputElement;
  ci.oninput = () => {
    lk[2] = ci.value;
    drawWires();
  };
  ci.onchange = () => {
    lk[2] = ci.value;
    drawWires();
    save();
  };
  (m.querySelector('.lm-reset') as HTMLElement).onclick = () => {
    lk.length = 2; // back to the default colour
    drawWires();
    save();
    closeLinkMenu();
  };
  (m.querySelector('.lm-del') as HTMLElement).onclick = () => {
    S.links.splice(i, 1);
    closeLinkMenu();
    drawWires();
    save();
  };
  m.addEventListener('pointerdown', (e) => e.stopPropagation());
  setTimeout(() => document.addEventListener('pointerdown', linkMenuOutside, true), 0);
}

/* ======================================================================== edit mode === */

/**
 * The global Edit toggle — THE ONE EDIT MODE (V0.53.5).
 *
 * The doc bar's Edit switch is this same control: it calls `toggleEditing` exactly as the top
 * bar's does, and everything the doc view shows in edit mode — the editor, the create pills,
 * the ownership box with the workflow chip — is DERIVED from this flag and the scope
 * (`lib/mode.ts`). Until V0.53.5 the bar flipped a second flag (`docMode`) that the box did not
 * read, so a page opened from a view-mode map could not show its chip and the switch could
 * not help; the top bar that could was under the overlay.
 *
 * The six edit-mode buttons are the React top bar's, gated on `editing` in one place. What is
 * here is everything that is NOT the buttons: the cursor, the hint, the body class and the
 * presence broadcast. The add-a-tag footer used to be hidden from here too, while it was
 * sidebar markup; it is the tags dropdown's now (plan 2.3), and its visibility is DERIVED
 * from `editing` where it renders — an imperative write into a React root would be a second
 * writer.
 */
export function setEditing(on: boolean): void {
  const was = S.editing;
  // Leaving the mode with a page in the editor ends the sitting: it records as a recent as it
  // ends, exactly as closing the page does — see `openDoc` for why not at open.
  if (was && !on && S.docCurrentId && docEditorActive()) recordRecent(S.docCurrentId);
  S.editing = on;
  if (!on) {
    S.linkArmed = false;
    S.ctrlAdd = false;
    S.movingId = null; // a pending Move to… (V0.54) is an edit-mode gesture too
    setAiDocMode(false); // leaving edit mode closes the AI-doc tools
    setHeatmap(false); // …and the clearance heatmap: the toggle only exists in edit mode
  }
  document.body.classList.toggle('viewonly', !on);
  document.getElementById('stage')?.classList.toggle('editing', on);
  refreshCursor();
  updateHint();
  renderTags();
  render();
  // Edit mode adds the dotted PLANNED pills to every node this user may create on, so the
  // rows' content changed even though `docsExpanded` did not — re-run the layout or the new
  // pills draw over the nodes below (V0.42.3).
  syncDocsExpand();
  sendLoc(); // view/edit mode changes my presence
  requestTopbarFit(); // edit adds and removes left buttons, so re-fit the bar
  // With a page open, its shape, its `/edit` segment and the presence socket's "editing" all
  // follow this flag, so the flip is a doc-view change too.
  if (was !== on && S.docOpen) docStateChanged();
}

/** Both Edit switches — the top bar's and the doc bar's (V0.53.5) — land here. */
export function toggleEditing(): void {
  setEditing(!S.editing);
  saveLocal(); // which mode this browser is in is a durable PREFERENCE, not map content
}

/** Arm or disarm the Link tool. The half-made link is the map region's own state. */
export function armLink(): void {
  if (!S.editing || !canEditLinks()) return;
  S.linkArmed = !S.linkArmed;
  announce(); // the Link button's highlight reads linkArmed
  refreshCursor();
  updateHint();
}

export function releaseCtrl(): void {
  if (!S.ctrlAdd) return;
  S.ctrlAdd = false;
  refreshCursor();
  updateHint();
}

export function toggleTheme(): void {
  S.isLight = !S.isLight;
  render();
  saveLocal(); // a theme is this browser’s, and always was
}

/**
 * The hint steps aside (V0.53.4). Called by the two pan gestures — the stage's drag in
 * `MapStage`, the frame's drag in the minimap — at their first movement, through the actions
 * registry like every other thing a region asks of the chrome. The rule lives in `chrome.ts`.
 */
export const hideHint = (): void => dismissHint();

/** Show or hide every dotted cross-link. Whether they are DRAWN is a per-user preference;
 *  the links themselves are map content and are not touched here. */
/**
 * Pin the documentation rows open, or let the zoom decide again (V0.42.2).
 *
 * The corner toggle beside the zoom stack. OFF is the behaviour that has always been:
 * the rows appear past the zoom threshold and hide below it. ON shows them at any zoom.
 * It never writes a canonical y — `evaluateDocsExpand()` re-derives `docsExpanded` from
 * this flag and the zoom, and the ephemeral display pass does the spacing, so a pinned
 * map still restores its compact layout on reload.
 */
export function setDocsPinned(on: boolean): void {
  const want = !!on;
  if (want === S.docsPinned) return;
  S.docsPinned = want;
  // The button is plain chrome markup, not a React region, so its state is written here —
  // the same way setAiDocMode() toggles #aidoc-toolbar's .show class.
  const btn = document.getElementById('btn-docs');
  if (btn) {
    btn.classList.toggle('on', want);
    btn.setAttribute('aria-pressed', want ? 'true' : 'false');
    btn.setAttribute('title', want ? 'Hide documents on the map' : 'Show documents on the map');
  }
  announce();
  syncDocsExpand();    // re-derive `docsExpanded`; the layout pass follows from there
  saveViewState();     // day-scoped, beside heatmapOn
}

export const toggleDocsPinned = (): void => setDocsPinned(!S.docsPinned);

/**
 * Show or hide the minimap (V0.53.4) — the first row of the zoom stack.
 *
 * A DURABLE preference, saved like the theme and unlike `docsPinned` above (day-scoped) or
 * `toggleLinks` below (not saved at all): whether a reader wants the overview on their screen
 * does not change with the day or the session. The card itself is a React region over
 * `#minimap` and follows `S.minimapOn` through the store; the button is plain chrome markup,
 * so its state is written here the way `setDocsPinned` writes `#btn-docs`.
 */
export function setMinimapOn(on: boolean): void {
  const want = !!on;
  if (want === S.minimapOn) return;
  S.minimapOn = want;
  syncMinimapButton();
  announce();
  saveLocal(); // a preference somebody changed: it badges, as the theme does
}

export const toggleMinimap = (): void => setMinimapOn(!S.minimapOn);

export function toggleLinks(): void {
  S.showLinks = !S.showLinks;
  if (!S.showLinks) closeLinkMenu();
  drawWires();
  // No save (V0.48.1). `showLinks` is not persisted anywhere any more, so `saveLocal()` here
  // would write the OTHER prefs unchanged and flash "Saved" about nothing — the pretend write
  // the role page's Save button has a paragraph swearing never to become.
}

/** One button, acting on whatever state the tree is currently in. */
export function toggleExpandAll(): void {
  // ONE open branch is enough to make this Collapse all (V0.48.1) — see `anyExpanded`.
  if (anyExpanded()) collapseAll();
  else expandAll();
}

/**
 * The two halves of the top bar's button, as verbs of their own since V0.54: the map's
 * right-click menu offers BOTH, each when it would change something, where the button can only
 * offer whichever the tree's state implies.
 */
export function expandAll(): void {
  for (const id in S.nodes) if (childrenOf(id).length) S.nodes[id].collapsed = false;
  afterExpandAll();
}
export function collapseAll(): void {
  for (const id in S.nodes) if (!S.nodes[id].root && childrenOf(id).length) S.nodes[id].collapsed = true;
  afterExpandAll();
}
/** Some branch is folded with something to show — the map menu's cue for Expand all. */
export const anyFolded = (): boolean =>
  Object.keys(S.nodes).some((id) => S.nodes[id].collapsed && !S.nodes[id].root && childrenOf(id).length > 0);

function afterExpandAll(): void {
  autoLayout('view'); // expanding is personal: the coordinates it produces are nobody else's
  render();
  saveLocal({ quiet: true }); // navigation, like every other collapse — see `saveLocal`
  // "Show me everything" is answered by showing everything (V0.46). There is no sensible
  // "centre on what just appeared" when what appeared is the whole map, so this one frames
  // the lot — in both directions, because collapsing leaves a small map stranded at the zoom
  // the big one needed. The save is `saveLocal`'s above; the animated fit adds only the view
  // record, so the badge flashes once.
  fit({ animate: true });
}

/**
 * The manual "Realign": the same auto-layout, plus a save. Edit mode only.
 *
 * THE canonical structural layout request. Somebody pressed a button whose entire meaning
 * is "tidy the map", so the positions it derives are meant for everyone.
 */
export function realign(): void {
  // EDIT_MAP: the positions it derives are published to everyone, which is a nodes-family
  // delta. The button carries the same gate; this is the backstop for a programmatic call.
  if (!S.editing || !canEditMap()) return;
  // ...and it must be laid out over the WHOLE tree, because the result is everybody's. An
  // editor whose map has branches withheld would tidy the visible siblings into the gap the
  // hidden ones leave and publish that as canonical. Refuse, and say so: doing nothing at all
  // in response to a button press is the version of this that gets reported as a bug.
  if (S.mapFiltered) {
    S.routeNotice = 'realign-partial';
    S.noticeDetail = null;
    announce();
    return;
  }
  autoLayout('structural');
  render();
  save();
}

/* ==================================================================== the map's edits === */

/**
 * Collapse or expand a branch.
 *
 * VIEW-ONLY on both counts (plan Part 1). `collapsed` was already kept out of every payload,
 * but that was necessary and not sufficient: the relayout it triggers rewrites `x/y/side/depth`
 * on every sibling, and THOSE are shared. One person collapsing a branch used to move
 * everybody's map. Now the pass is `'view'` and the save is local, so nothing leaves this
 * browser — and everyone re-derives their own layout at their next boot, as they already did.
 */
export function toggleCollapsed(id: NodeId): void {
  const n = S.nodes[id];
  if (!n) return;
  n.collapsed = !n.collapsed;
  autoLayout('view');
  render();
  // Opening or closing a branch is navigation: the view record is written, the badge is
  // not raised. See `saveLocal`.
  saveLocal({ quiet: true });
  // Opening a branch, the camera takes in what just appeared (V0.46).
  //
  // THE PARENT WITH ITS CHILDREN, not the children alone: the point of expanding is to see
  // the relationship, and a frame that cropped the parent out would have thrown away the
  // thing being explained. `zoomIn: false` because the map just grew — pulling back to take
  // in a group is help, lunging at one is not. `onlyIfOutside` because a branch that opened
  // entirely within view was never hidden, and moving the map to celebrate that would make
  // the same gesture feel different depending on where you happened to be looking.
  //
  // Collapsing frames nothing. Nothing appeared.
  //
  // The positions are fresh: the layout queued two lines up commits before this op is
  // reached, in the same drain.
  if (!n.collapsed) {
    centerOnNodes([id, ...childrenOf(id)], {
      animate: true,
      zoomIn: false,
      onlyIfOutside: true,
      onEnd: (completed) => {
        if (completed) saveViewState();
      },
    });
  }
}

/**
 * Join two nodes with a dotted cross-link.
 *
 * Refused when they are already joined, or already parent and child — a dotted line
 * duplicating a wire says nothing.
 */
export function linkNodes(a: NodeId, b: NodeId): void {
  if (!a || !b || a === b) return;
  const exists =
    S.links.some(([x, y]) => (x === a && y === b) || (x === b && y === a)) ||
    S.edges[b] === a ||
    S.edges[a] === b;
  if (!exists) S.links.push([a, b]);
  S.showLinks = true; // make sure a newly created link is visible
  drawWires();
  save();
}

/** Ctrl-click on empty space: a new node where the pointer is, opened for naming. */
export function createNodeAt(x: number, y: number, side: number): void {
  const color = COLORS[S.nid % COLORS.length];
  const fields = { label: 'New node', x, y, color, side, depth: 1 };
  createNodeOnServer(fields)
    .then((id) => {
      S.nid++;
      S.nodes[id] = { id, ...fields } as MapNode;
      // BYPASSES commitPositions: x/y/side/depth are written straight onto the node here.
      // Marked explicitly, or a node created by Ctrl-click would render where the pointer
      // was and be stored wherever the server happened to place it. One of the four sites
      // named in the plan; a missed mark is the only silent-loss path in the design.
      Writes.markNodePosition(id);
      resolveCollisions(id, 'structural');
      render();
      openPopup(id);
      save();
      pushUndo('create "' + fields.label + '"', async () => {
        delete S.nodes[id];
        render();
        renderTags();
        try {
          await deleteNodeOnServer(id);
        } catch {
          /* already gone */
        }
        save();
      });
    })
    .catch((err) => {
      console.error(err);
      alert('Could not create the new node on the server.');
    });
}

/**
 * Commit an in-place label edit.
 *
 * The typed text is the map region's STATE — it used to live in an `<input>` the renderer had
 * swapped into the node and nowhere else, which is the shape CLAUDE.md calls the most likely
 * source of silent data loss.
 */
export function renameNode(id: NodeId, text: string | null): void {
  const n = S.nodes[id];
  if (!n) return;
  const prevLabel = n.label;
  n.label = String(text == null ? '' : text).trim() || 'Untitled';
  Writes.dirtyNodeTranslations.add(id);
  if (n.label !== prevLabel) {
    pushUndo('rename "' + prevLabel + '"', async () => {
      const cur = S.nodes[id];
      if (!cur) return;
      cur.label = prevLabel;
      Writes.dirtyNodeTranslations.add(id);
      render();
      save();
    });
  }
  render();
  save();
}

/**
 * Undo a drag, in one place, because it has to put back MORE THAN POSITION.
 *
 * A drop can reparent, and `reparent()` re-colours the whole moved subtree to its new branch,
 * re-bases every depth and mirrors it horizontally if the side flipped. An undo restoring only
 * x and y would leave a branch the right shape and the wrong colour, one column too deep, on
 * the wrong side. The region hands over what each node looked like before the drag and lets
 * this side apply it, rather than writing colour itself: colour is not a position, and
 * splitting the restore would make two writers of one field.
 */
export function undoDrag(
  nodeId: NodeId,
  prevParent: NodeId | null | undefined,
  group: { id: NodeId; x: number; y: number; side: number; depth: number; color: string }[],
): void {
  for (const g of group ?? []) {
    const cur = S.nodes[g.id];
    if (!cur) continue;
    cur.x = g.x;
    cur.y = g.y;
    cur.side = g.side;
    cur.depth = g.depth;
    cur.color = g.color;
    // BYPASSES commitPositions. Undoing a drag is as structural as the drag was, and
    // without this mark the drag would be saved and its undo would not — the map on screen
    // and the map on the server permanently disagreeing about where a subtree is.
    Writes.markNodePosition(g.id);
  }
  S.edges = withParent(S.edges, nodeId, prevParent !== undefined && prevParent !== null ? prevParent : null);
  render();
  save();
}

export async function addChild(parentId: NodeId): Promise<void> {
  const p = S.nodes[parentId];
  if (!p) return;
  if (p.collapsed) p.collapsed = false; // expand so the new child is visible
  const side = p.root ? 1 : (p.side ?? 1); // the root's children default to the right
  const color = p.root ? COLORS[S.nid % COLORS.length] : p.color;
  const sibs = childrenOf(parentId).map((c) => S.nodes[c]);
  const x = p.root ? side * COLW : side >= 0 ? (p.x ?? 0) + COLW : (p.x ?? 0) - COLW;
  // y: just below the lowest existing sibling, else aligned with the parent.
  const y = sibs.length ? Math.max(...sibs.map((s) => s.y ?? 0)) + ROW : (p.y ?? 0);
  const depth = (p.depth || 0) + 1;
  await createNamedLater(parentId, { x, y, color, side, depth });
}

/**
 * A new sibling, right below this node (V0.54, the right-click menu). `addChild`'s shape one
 * level up: same parent, same side and depth, the parent's colour — or, beside a top-level
 * branch, the next branch colour, as the root's own children get.
 *
 * One pixel below it, not below the lowest sibling: the point is to land NEXT TO the node that
 * was clicked. The canonical order is by centre, so a newcomer whose top sits just under this
 * node's top orders straight after it, and the separation pass opens the gap.
 */
export async function addSibling(id: NodeId): Promise<void> {
  const n = S.nodes[id];
  const parentId = S.edges[id];
  if (!n || n.root || !parentId || !S.nodes[parentId] || !canEditMap()) return;
  const p = S.nodes[parentId];
  await createNamedLater(parentId, {
    x: n.x ?? 0,
    y: (n.y ?? 0) + 1,
    color: p.root ? COLORS[S.nid % COLORS.length] : p.color,
    side: n.side ?? 1,
    depth: n.depth ?? (p.depth || 0) + 1,
  });
}

/**
 * Create "New node" under `parentId` where the app decided, and open its name for typing — the
 * shared half of `addChild` and `addSibling`, and the undo that takes it away again.
 */
async function createNamedLater(
  parentId: NodeId,
  place: { x: number; y: number; color: string | null | undefined; side: number; depth: number },
): Promise<void> {
  const { x, y, color, side, depth } = place;
  let id: NodeId;
  try {
    id = await createNodeOnServer({ label: 'New node', x, y, color, side, depth, parentId });
  } catch (e) {
    console.error(e);
    alert('Could not create the new node on the server.');
    return;
  }
  S.nid++;
  S.nodes[id] = { id, label: 'New node', x, y, color, side, depth } as MapNode;
  // REPLACED, not mutated — see withParent()'s header. `childrenOf` reads a graph index
  // memoised on this object's identity, and editing it in place is what made a second
  // Ctrl-click land on top of the first new child instead of below it.
  S.edges = withParent(S.edges, id, parentId);
  // BYPASSES commitPositions: the placement just computed above is written directly.
  Writes.markNodePosition(id);
  // UNANCHORED, unlike reparent and createNodeAt below. Those place a node where the user
  // chose to put it, so the node holds still and everything else moves. Here the app chose
  // the position — "below the lowest sibling" — so the newcomer is the one that should give
  // way. Anchoring it pushed a two-line sibling BELOW the fresh one-line node, and the next
  // canonical pass (centre-ordered) then cemented that order.
  resolveCollisions(null, 'structural');
  render();
  // Asked for before React has rendered the node, and that is fine: this is state, so the node
  // arrives already in its editor and focuses itself when it does.
  handles.map?.beginRename(id);
  save();
  pushUndo('create "New node"', async () => {
    delete S.nodes[id];
    S.edges = withParent(S.edges, id, null);
    render();
    renderTags();
    try {
      await deleteNodeOnServer(id);
    } catch {
      /* already gone */
    }
    save();
  });
}

export function reparent(childId: NodeId, newParentId: NodeId): void {
  if (childId === newParentId) return;
  if (descendants(childId).includes(newParentId)) return; // cannot drop onto own descendant
  if (!S.nodes[childId] || !S.nodes[newParentId]) return;

  S.edges = withParent(S.edges, childId, newParentId);
  placeSubtree(childId, newParentId);

  resolveCollisions(childId, 'structural');
  render();
  save();
}

/**
 * Put a subtree that now hangs from `parentId` where that parent's children go: `reparent`'s
 * geometry, shared since V0.54 with a pasted or duplicated copy, which arrives already attached
 * and needs exactly the same treatment.
 *
 * `at` places the top node somewhere chosen (a click on the map, the spot under an original)
 * instead of below the parent's lowest child; `side` is which side of the ROOT a top-level
 * branch goes (the root has none of its own to hand down); `keepColour` is a duplicate's — it
 * sits under the same parent it came from, so repainting it would only erase a colour somebody
 * chose.
 */
function placeSubtree(
  childId: NodeId,
  parentId: NodeId,
  opts: { at?: { x: number; y: number }; side?: number; keepColour?: boolean } = {},
): void {
  const child = S.nodes[childId];
  const parent = S.nodes[parentId];
  if (parent.collapsed) parent.collapsed = false;
  const side = parent.root ? (opts.side ?? 1) : (parent.side ?? 1);
  const color = parent.root || opts.keepColour ? child.color || COLORS[S.nid % COLORS.length] : parent.color;

  // Target position: one column out from the new parent, below its existing children.
  const sibs = childrenOf(parentId)
    .filter((c) => c !== childId)
    .map((c) => S.nodes[c]);
  const tx = opts.at ? opts.at.x : parent.root ? side * COLW : side >= 0 ? (parent.x ?? 0) + COLW : (parent.x ?? 0) - COLW;
  const ty = opts.at ? opts.at.y : sibs.length ? Math.max(...sibs.map((s) => s.y ?? 0)) + ROW : (parent.y ?? 0);

  // Translate the whole moved subtree so the dragged node lands at (tx,ty), keeping its shape.
  const dx = tx - (child.x ?? 0);
  const dy = ty - (child.y ?? 0);
  const moved = [childId, ...descendants(childId)];
  for (const i of moved) {
    S.nodes[i].x = (S.nodes[i].x ?? 0) + dx;
    S.nodes[i].y = (S.nodes[i].y ?? 0) + dy;
    // BYPASSES commitPositions, for the whole moved subtree — and the mark covers the
    // depth re-basing and the side mirroring below as well, which write the same nodes.
    Writes.markNodePosition(i);
  }

  // Adopt the new branch colour and re-base the depth for the moved subtree.
  const baseDepth = (parent.depth || 0) + 1;
  const childOldDepth = child.depth || 1;
  for (const i of moved) {
    const n = S.nodes[i];
    n.color = color;
    n.depth = baseDepth + ((n.depth || 1) - childOldDepth);
  }

  // If the branch side flipped, mirror the subtree horizontally around the drop x.
  if ((child.side || 1) !== side) {
    const pivot = tx;
    for (const i of moved) {
      const n = S.nodes[i];
      n.x = pivot - ((n.x ?? 0) - pivot);
      n.side = side;
    }
  }
}

export async function delNode(id: NodeId): Promise<void> {
  const node = S.nodes[id];
  if (!node) return;
  // Snapshot everything needed to fully restore this node on undo.
  const snap = {
    fields: {
      label: node.label,
      desc: node.desc,
      x: node.x,
      y: node.y,
      color: node.color,
      side: node.side,
      depth: node.depth,
      docId: node.docId || null,
      internalDocId: node.internalDocId || null,
      courseDocId: node.courseDocId || null,
    },
    tags: [...(node.tags || [])],
    parentId: S.edges[id],
    childIds: Object.keys(S.edges).filter((c) => S.edges[c] === id),
    removedLinks: S.links
      .filter(([a, b]) => a === id || b === id)
      .map(([a, b, c]) => ({ other: a === id ? b : a, color: c || null })),
  };
  // Reparent children to the grandparent. ONE copy for the whole rewrite rather than one
  // per child — the rule is that the container is replaced, not that it is replaced n times.
  const par = S.edges[id];
  const nextEdges: typeof S.edges = { ...S.edges };
  for (const c in nextEdges) {
    if (nextEdges[c] === id) {
      if (par !== undefined) nextEdges[c] = par;
      else delete nextEdges[c];
    }
  }
  delete nextEdges[id];
  S.edges = nextEdges;
  delete S.nodes[id];
  S.links = S.links.filter(([a, b]) => a !== id && b !== id);
  render();
  try {
    await pushStructuralSync(); // commit the reparented children first…
    await deleteNodeOnServer(id); // …so the server has no children still pointing here
  } catch (e) {
    console.error('Failed to delete node on server:', e);
  }
  save();
  pushUndo('delete "' + snap.fields.label + '"', async () => {
    let newId: NodeId;
    try {
      newId = await createNodeOnServer({
        ...snap.fields,
        parentId: snap.parentId !== undefined ? snap.parentId : null,
      } as Writes.NewNodeFields);
    } catch {
      alert('Could not restore this node.');
      return;
    }
    S.nodes[newId] = { id: newId, ...snap.fields, tags: [...snap.tags] } as MapNode;
    // The fifth direct position write, and it bypasses commitPositions like the other four.
    // The POST above already carried these coordinates, so the mark is belt-and-braces —
    // but a restored node whose position is only ever implicit is exactly the shape the
    // "a missing mark is silent data loss" rule exists to keep out of this file.
    Writes.markNodePosition(newId);
    // One copy for the restore, same rule as the delete above.
    const restored: typeof S.edges = { ...S.edges };
    if (snap.parentId !== undefined) restored[newId] = snap.parentId;
    // Re-adopt the children that were reparented away above.
    for (const c of snap.childIds) if (S.nodes[c]) restored[c] = newId;
    S.edges = restored;
    for (const l of snap.removedLinks) if (S.nodes[l.other]) S.links.push([newId, l.other, l.color]);
    render();
    renderTags();
    try {
      await pushStructuralSync(); // position, tags, links and children together
    } catch {
      /* the debounced sync will carry it */
    }
    save();
  });
}

/* ============================================================ the right-click menus (V0.54) === */

/*
 * What the five right-click menus DO. What they OFFER — which items, in which order, to whom —
 * is `lib/contextMenu.ts`, and the menu itself is `features/contextmenu/ContextMenu.tsx`. Most
 * items are verbs the app already had under another control (the collapse toggle, the popup's
 * star, the zoom stack's buttons) and call those directly; what is here is only what was new:
 * the branch-sized navigation, the clipboard, Move to…, the link and page conveniences.
 */

export const openContextMenu = (target: MenuTarget, clientX: number, clientY: number): void =>
  handles.contextMenu?.open(target, clientX, clientY);
export const closeContextMenu = (): void => handles.contextMenu?.close();

/** Rename: the node's label becomes its in-place editor on the map, as a new node's does. */
export const beginRename = (id: NodeId): void => handles.map?.beginRename(id);

/** A screen point as a world point — the camera's inverse, for Ctrl+V onto the empty map. */
export function screenToWorld(clientX: number, clientY: number): { x: number; y: number } | null {
  const stage = document.getElementById('stage');
  if (!stage) return null;
  const r = stage.getBoundingClientRect();
  const v = mapGetCamera();
  return { x: (clientX - r.left - v.x) / v.k, y: (clientY - r.top - v.y) / v.k };
}

/** The branch below `id` as far as it is OPEN — the nodes a frame around it can see. */
function openBranch(id: NodeId): NodeId[] {
  const out: NodeId[] = [id];
  const walk = (i: NodeId): void => {
    if (S.nodes[i]?.collapsed && !S.nodes[i]?.root) return;
    for (const c of childrenOf(i)) {
      out.push(c);
      walk(c);
    }
  };
  walk(id);
  return out;
}

/**
 * Unfold everything in this branch — Expand all, for one branch. The same camera answer as
 * opening one branch (V0.46): take in what appeared, without magnifying, and only when it did
 * not already fit.
 */
export function expandBeneath(id: NodeId): void {
  let changed = false;
  for (const i of [id, ...descendants(id)]) {
    const n = S.nodes[i];
    if (n && n.collapsed && !n.root && childrenOf(i).length) {
      n.collapsed = false;
      changed = true;
    }
  }
  if (!changed) return;
  autoLayout('view');
  render();
  saveLocal({ quiet: true }); // navigation, like every collapse
  centerOnNodes(openBranch(id), {
    animate: true,
    zoomIn: false,
    onlyIfOutside: true,
    onEnd: (completed) => {
      if (completed) saveViewState();
    },
  });
}

/**
 * Fold every other branch and keep this one — "collapse everything else". The path from the
 * root stays open so the node is still reachable, the node itself opens one level, and the
 * camera frames what is left.
 */
export function collapseAllBut(id: NodeId): void {
  if (!S.nodes[id]) return;
  const keep = new Set<NodeId>([id]);
  for (let p = S.edges[id]; p; p = S.edges[p]) keep.add(p);
  for (const i in S.nodes) {
    const n = S.nodes[i];
    if (n.root || !childrenOf(i).length) continue;
    n.collapsed = !keep.has(i);
  }
  autoLayout('view');
  render();
  saveLocal({ quiet: true });
  centerOnNodes(openBranch(id), { zoomIn: false });
}

/** Frame the open part of this branch. A view change only; the camera is not saved as content. */
export function frameBranch(id: NodeId): void {
  if (!S.nodes[id]) return;
  centerOnNodes(openBranch(id), { zoomIn: false });
  saveViewState();
}

/**
 * Recolour one node from the menu's swatches — the popup's colour field, one click away. The
 * NODE's own colour only, exactly as the popup sets it: the branch below keeps its colours.
 */
export function setNodeColour(id: NodeId, color: string): void {
  const n = S.nodes[id];
  if (!n || n.root || !canEditMap() || n.color === color) return;
  const prev = n.color;
  n.color = color;
  render();
  save();
  pushUndo(`recolour "${n.label}"`, async () => {
    const cur = S.nodes[id];
    if (!cur) return;
    cur.color = prev;
    render();
    save();
  });
}

/**
 * Link to…: the Link tool, armed with this node already chosen as the first end, so the next
 * node clicked finishes the link. Everything after that is the tool's own behaviour.
 */
export function startLinkFrom(id: NodeId): void {
  if (!S.editing || !canEditLinks() || !S.nodes[id]) return;
  if (S.movingId) cancelMove();
  if (!S.linkArmed) armLink();
  getStoreState().setLinkFromId(id);
}

/**
 * Move to…: carry this node until the next click names its new parent — the menu form of
 * dragging it onto a node, for a target that is far away or behind a fold. A click on empty
 * map, or Escape, puts it down where it was.
 */
export function startMove(id: NodeId): void {
  const n = S.nodes[id];
  if (!S.editing || !canEditMap() || !n || n.root) return;
  if (S.linkArmed) armLink();
  S.movingId = id;
  refreshCursor();
  updateHint();
}

/**
 * The map's next click while a "Move to…" is pending, handed over by `MapStage`: a node
 * finishes the move, anything else puts the node down. True when the click was taken, so the
 * map does not also start a drag or a pan with it.
 */
export function takeMoveClick(nodeId: NodeId | null): boolean {
  if (!S.movingId) return false;
  if (nodeId && S.nodes[nodeId]) finishMove(nodeId);
  else cancelMove();
  return true;
}

export function cancelMove(): void {
  if (!S.movingId) return;
  S.movingId = null;
  refreshCursor();
  updateHint();
}

/**
 * The click that ends a Move to…: reparent onto the node clicked, and push the same undo a
 * drag does, because a drop onto a node is exactly what this is. Refused silently where the
 * drag refuses it — its own branch, its own parent, itself.
 */
export function finishMove(targetId: NodeId): void {
  const id = S.movingId;
  cancelMove();
  if (!id || !S.nodes[id] || !S.nodes[targetId] || id === targetId) return;
  if (S.edges[id] === targetId || descendants(id).includes(targetId)) return;
  const prevParent = S.edges[id];
  const group = [id, ...descendants(id)].map((i) => ({
    id: i,
    x: S.nodes[i].x ?? 0,
    y: S.nodes[i].y ?? 0,
    side: S.nodes[i].side ?? 1,
    depth: S.nodes[i].depth ?? 1,
    color: S.nodes[i].color as string,
  }));
  const label = S.nodes[id].label;
  reparent(id, targetId);
  pushUndo(`move "${label}"`, () => undoDrag(id, prevParent, group));
}

/* ---- the clipboard ---- */

/**
 * Copy (and Copy with its branch): put the node on the clipboard. Nothing is written — the
 * copy happens at Paste, on the server, from the node as it is then (see `S.clipboard`).
 */
export function copyNode(id: NodeId, branch: boolean): void {
  const n = S.nodes[id];
  if (!n || n.root || !canEditMap()) return;
  const count = branch ? 1 + descendants(id).length : 1;
  S.clipboard = { id, branch, label: n.label, count };
  updateHint();
  announce();
}

export function clearClipboard(): void {
  if (!S.clipboard) return;
  S.clipboard = null;
  updateHint();
  announce();
}

/** What a pasted copy takes, as the user chose in the proof of concept. */
const COPY_WITH_PAGES = true;
const COPY_SUFFIX = ' (copy)';

/**
 * Write a copy of `sourceId` (and its branch) under `parentId`, adopt what the server wrote,
 * and place it. The one path Paste and Duplicate share, so "what a copy is" cannot mean two
 * things — and it is the server's route that says what that is.
 *
 * Placement is `reparent`'s (`placeSubtree`): the copy arrives already attached, and hangs
 * exactly where a node dropped onto that parent would. The positions the server copied from the
 * original are the starting point the subtree is translated from, and the placed ones are
 * MARKED and saved through the ordinary structural path, as `addChild`'s are.
 */
async function copyUnder(
  sourceId: NodeId,
  parentId: NodeId,
  branch: boolean,
  opts: { at?: { x: number; y: number }; side?: number; keepColour?: boolean; anchored: boolean; verb: string },
): Promise<NodeId | null> {
  if (!S.nodes[parentId] || !canEditMap()) return null;
  let res: { nodes: Writes.CopiedNode[] };
  try {
    res = await Writes.copyNodeOnServer(S.currentLocale, sourceId, {
      parentId,
      branch,
      withPages: COPY_WITH_PAGES,
      suffix: COPY_SUFFIX,
    });
  } catch (e) {
    const status = (e as { status?: number } | null)?.status;
    if (status === 404 && S.clipboard?.id === sourceId) clearClipboard();
    alert(
      status === 404
        ? 'The node you copied no longer exists, or is no longer visible to you — there is nothing to paste.'
        : 'Could not paste the copy.',
    );
    return null;
  }
  const made = res.nodes;
  if (!made.length || !S.nodes[parentId]) return null;
  const nextEdges: typeof S.edges = { ...S.edges };
  for (const c of made) {
    const { sourceId: _from, parentId: parent, ...row } = c;
    S.nodes[c.id] = mapNodeFromServer(row as unknown as MapNode);
    if (parent) nextEdges[c.id] = parent;
  }
  S.edges = nextEdges;
  S.nid += made.length;
  const topId = made[0].id;
  placeSubtree(topId, parentId, { at: opts.at, side: opts.side, keepColour: opts.keepColour });
  for (const c of made) Writes.markNodePosition(c.id);
  // A paste lands where the user pointed, so it holds still and the neighbours move; a
  // duplicate lands where the APP chose, so it is the one that gives way — `addChild`'s rule.
  resolveCollisions(opts.anchored ? topId : null, 'structural');
  render();
  renderTags();
  save();
  centerOnNodes([topId], { zoomIn: false, onlyIfOutside: true });
  const ids = made.map((c) => c.id);
  pushUndo(`${opts.verb} "${S.nodes[topId]?.label ?? ''}"`, async () => {
    const back: typeof S.edges = { ...S.edges };
    for (const i of ids) {
      delete S.nodes[i];
      delete back[i];
    }
    S.edges = back;
    render();
    renderTags();
    // Leaves first, so the server never holds a child whose parent it has just deleted.
    for (const i of [...ids].reverse()) {
      try {
        await deleteNodeOnServer(i);
      } catch {
        /* already gone */
      }
    }
    save();
  });
  return topId;
}

/** Paste the clipboard as a child of `parentId` — as often as you like; the clipboard stays. */
export async function pasteInto(parentId: NodeId): Promise<void> {
  const c = S.clipboard;
  if (!c) return;
  await copyUnder(c.id, parentId, c.branch, { anchored: true, verb: 'paste' });
}

/** Paste the clipboard onto the empty map: a top-level branch where the click was. */
export async function pasteAt(x: number, y: number): Promise<void> {
  const c = S.clipboard;
  const rootId = Object.keys(S.nodes).find((id) => S.nodes[id].root);
  if (!c || !rootId) return;
  await copyUnder(c.id, rootId, c.branch, {
    at: { x, y: y - 21 },
    side: x < 0 ? -1 : 1,
    anchored: true,
    verb: 'paste',
  });
}

/**
 * Duplicate: copy and paste in one step, directly below the original, under the same parent,
 * keeping its colour — and leaving the clipboard alone. Always with the branch (the user's
 * choice: a duplicate of a node is a duplicate of what it holds).
 */
export async function duplicateNode(id: NodeId): Promise<void> {
  const n = S.nodes[id];
  const parentId = S.edges[id];
  if (!n || n.root || !parentId || !canEditMap()) return;
  await copyUnder(id, parentId, true, {
    at: { x: n.x ?? 0, y: (n.y ?? 0) + 1 },
    side: n.side ?? 1,
    keepColour: true,
    anchored: false,
    verb: 'duplicate',
  });
}

/* ---- tasks and tags: the popup, opened at the right fold ---- */

/**
 * Show tasks / Add a task…: open the node's popup with its Tasks fold open — and, for Add, with
 * the new-task box focused (`focusTodoAdd`, consumed once by the section, `revealTodo`'s
 * handover). The menu offers these on project nodes only; the gate here is the permission.
 */
export function openNodeTasks(id: NodeId, compose: boolean): void {
  if (!S.nodes[id] || !canViewTodosFor(S.perm)) return;
  setSectionOpen(SECTION_TODOS, true);
  S.focusTodoAdd = compose ? id : null;
  focusPopup(id);
}

export function consumeFocusTodoAdd(): void {
  if (S.focusTodoAdd === null) return;
  S.focusTodoAdd = null;
  render();
}

export function openNodeTags(id: NodeId): void {
  if (!S.nodes[id]) return;
  setSectionOpen(SECTION_TAGS, true);
  focusPopup(id);
}

/* ---- links, addresses and pages ---- */

/** The page's shareable address, `/{locale}/node/{slug}` — the router's own URL builder. */
export function nodeAddress(id: NodeId): string | null {
  const slug = S.nodes[id]?.slug;
  if (!slug) return null;
  return window.location.origin + toUrl({ locale: S.currentLocale, view: 'node', nodeSlug: slug }, ROUTER_BASENAME);
}

export function docAddress(docId: DocId): string | null {
  const slug = S.docSlugs[docId];
  if (!slug) return null;
  return window.location.origin + toUrl({ locale: S.currentLocale, view: 'doc', docSlug: slug }, ROUTER_BASENAME);
}

/** Put text on the system clipboard. Resolves false where the browser refuses (no permission, no secure context). */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * Open one page of a node from outside the popup — a pill's click (`MapStage`) and its menu's
 * Open. The camera centres the node and the node crosses into the page (V0.47).
 */
export function openNodeDoc(nodeId: NodeId, docId: DocId): void {
  const n = S.nodes[nodeId];
  if (!n) return;
  const perm = Docs.docPermOnNode(n, docId);
  if (!canDocBtn(perm)) return;
  const fallback = perm === 'custom' ? 'Untitled' : Docs.kindLabelOf(perm);
  const title = S.docTitles[docId] || fallback;
  openDocFromNode(nodeId, { docId, title: `${n.label} — ${title}` }, 'current');
}

export function openDocInNewTab(docId: DocId): void {
  window.open(docUrlFor(docId), '_blank', 'noopener');
}

/** The node at one end of a dotted link, centred without magnifying (a PAN — V0.47's rule). */
export function goToNode(id: NodeId): void {
  if (!S.nodes[id]) return;
  centerOnNodes([id], { zoomIn: false });
  saveViewState();
}

const linkIndexOf = (a: NodeId, b: NodeId): number =>
  S.links.findIndex(([x, y]) => (x === a && y === b) || (x === b && y === a));

/** Recolour a dotted link from the swatches — the link menu's colour field, with an undo. */
export function setLinkColour(i: number, color: string): void {
  const lk = S.links[i];
  if (!lk || !canEditLinks()) return;
  const [a, b, prev] = lk;
  S.links = S.links.map((l, j) => (j === i ? [a, b, color] : l)) as typeof S.links;
  drawWires();
  save();
  pushUndo('recolour a link', async () => {
    const j = linkIndexOf(a, b);
    if (j < 0) return;
    S.links = S.links.map((l, k) => (k === j ? (prev ? [a, b, prev] : [a, b]) : l)) as typeof S.links;
    drawWires();
    save();
  });
}

/** Remove a dotted link — the link menu's Delete, with an undo that puts it back. */
export function removeLink(i: number): void {
  const lk = S.links[i];
  if (!lk || !canEditLinks()) return;
  const [a, b, color] = lk;
  S.links = S.links.filter((_, j) => j !== i);
  closeLinkMenu();
  drawWires();
  save();
  pushUndo('remove a link', async () => {
    if (!S.nodes[a] || !S.nodes[b] || linkIndexOf(a, b) >= 0) return;
    S.links = [...S.links, (color ? [a, b, color] : [a, b]) as CrossLink];
    drawWires();
    save();
  });
}

/** Clear the search text, the tag filter and the stage filter together, and return the camera. */
export function clearFilters(): void {
  const wasActive = filterActive();
  if (S.activeDocStatus) setActiveDocStatus('');
  if (S.activeTags.size) setActiveTags([]);
  if (S.searchText) setSearchText('');
  frameForFilter(wasActive);
}

/** The minimap keeps its bright level (V0.54). A preference, so it badges like the theme. */
export function setMinimapBright(on: boolean): void {
  if (S.minimapBright === !!on) return;
  S.minimapBright = !!on;
  announce();
  saveLocal();
}

/* ====================================================================== the node popup === */

export const openPopup = (id: NodeId): void => handles.popup?.open(id);
/**
 * Re-read an OPEN popup's shape without remounting it, so an unsaved draft survives.
 *
 * `openPopup(id)` on the node already showing is a remount and resets the draft — faithful to
 * the vanilla `innerHTML` rebuild, and still the right verb when the popup should start over.
 * But four call sites used it to mean "something behind this popup changed, redraw it", and
 * for those the reset was the bug: naming a node and toggling AI-doc mode lost the name. Two
 * of the four are gone (the targets screen and a live-verification approval both read live
 * state); these are the two that genuinely need to re-derive the SHAPE.
 */
export const refreshPopup = (id: NodeId): void => handles.popup?.refresh(id);
export const closePopup = (opts?: { fly?: boolean }): void => handles.popup?.close(opts);

/** The node a `focusPopup` is travelling towards, so a double-click's second press is not a second journey. */
let pendingFocus: NodeId | null = null;

/**
 * Open a node's popup THE WAY A READER OPENS IT (V0.47): centre the node, then let it lift off
 * the map and take its seat at the top of the popup.
 *
 * Two moves, deliberately unequal. The camera's 600 ms is carrying you somewhere and can be
 * unhurried; the node's 240 ms crossing is only keeping one object identifiable between two
 * surfaces, and once the eye has caught it there is nothing left to see. Both were settled in
 * a prototype, not by argument.
 *
 * `openPopup` is unchanged and stays the instant verb: the router's deep links (boot), a node
 * just created (the reader is about to type), the band's "+N more" (no node on screen to
 * depart from) and the page global the suite calls all keep it. This is the verb for a person
 * pointing at a node.
 *
 * Every wait here has a deadline. The camera's `onEnd` fires on every path — but this sequence
 * must open the popup even if some later change breaks that promise, because a click that
 * opens nothing is the one outcome worse than any animation.
 */
export function focusPopup(id: NodeId): void {
  if (!S.nodes[id]) return;
  // A re-open of the node already showing is not a journey — it is today's refresh.
  if (S.popupFor === id) {
    openPopup(id);
    return;
  }
  if (pendingFocus === id) return;
  const alive = beginSequence();
  pendingFocus = id;
  let arrived = false;
  const arrive = (): void => {
    if (arrived) return;
    arrived = true;
    if (pendingFocus === id) pendingFocus = null;
    if (!alive()) return;
    handles.popup?.open(id, {
      onSeat: (seat) => {
        if (!alive()) return;
        const from = anchors.mapNode(id);
        // No fresh seat (a refresh), or no node on the stage to depart from: the popup is
        // simply there, the node simply in it — today's behaviour, and reduced motion's.
        if (!seat || !from) {
          restPerch();
          return;
        }
        const popup = document.getElementById('popup');
        fly({
          id,
          from,
          to: () => anchors.seat(id),
          land: 'seat',
          fades: popup ? [{ el: popup, from: 0, to: 1 }] : [],
        });
      },
    });
  };
  // `zoomIn: false`: a PAN, as the prototype centred. Without it `runCenter` fits a single
  // node at the 1.4 ceiling, so every click zoomed the whole map in as well — a far bigger
  // move than the reader asked for, and most of the "lag" before the popup appeared.
  centerOnNodes([id], { animate: true, zoomIn: false, onEnd: arrive });
  setTimeout(arrive, CAMERA_MS + 300);
}
/**
 * The region telling this side which node it is on.
 *
 * ANNOUNCED, not inferred: `myPresenceLoc()` reports it to the presence socket and the
 * Ctrl-hold "add child" guard suppresses itself while a popup is open, so it has to keep
 * meaning exactly what it always meant.
 */
export function setPopupFor(id: NodeId | null): void {
  S.popupFor = id || null;
  sendLoc();
  // A focused node IS a place (`/{locale}/node/{slug}`), and this announcement is the one
  // choke point every open AND close path reaches — the scrim click and the region's own
  // Escape never go near openPopup()/closePopup(). Re-opening the same node never lands here
  // (the region announces only on change), so a REFRESH re-open writes nothing.
  Router.syncUrl('nav');
}

/** Every AI-panel field of a node as one comparable string — i.e. what "did anything change" means. */
function aiSnapshot(n: MapNode): string {
  return JSON.stringify([
    n.aiPrompt,
    n.liveProbeEnabled,
    n.appTargetId,
    n.appPath,
    ...CONTEXT_SOURCE_KEYS.map((k) => (n as unknown as Record<string, unknown>)[k]),
  ]);
}

export interface AiDraft {
  aiPrompt?: string;
  ctx?: Record<string, boolean | null>;
  liveProbeEnabled?: boolean;
  appTargetId?: string | null;
  appPath?: string | null;
  /**
   * Whether this draft came from a panel that DREW the mechanics (V0.37). A responsible's
   * prompt-only panel sets it false, and `applyAiFields` then leaves the mechanics fields
   * alone — their draft values are defaults over a payload that never carried the real ones,
   * and writing those back would both corrupt local state and mark the node dirty for a
   * family this user cannot flush.
   */
  mechanics?: boolean;
}

/** Write the popup's AI panel onto the node. Returns whether anything changed. */
export function applyAiFields(n: MapNode, ai: AiDraft | undefined): boolean {
  if (!ai) return false;
  const before = aiSnapshot(n);
  n.aiPrompt = ai.aiPrompt;
  if (ai.mechanics !== false) {
    for (const k of CONTEXT_SOURCE_KEYS) {
      (n as unknown as Record<string, unknown>)[k] = ai.ctx && k in ai.ctx ? ai.ctx[k] : null;
    }
    n.liveProbeEnabled = !!ai.liveProbeEnabled;
    n.appTargetId = ai.appTargetId || null;
    n.appPath = (ai.appPath || '').trim() || null;
  }
  return before !== aiSnapshot(n);
}

/**
 * What the popup's Save hands over. EVERY FIELD IS OPTIONAL, and absence means "this form did
 * not draw that affordance" (V0.37): the popup renders each section only behind its own brick
 * or scope gate, so a draft from a brick-poor participant simply does not carry the fields
 * they may not write — which is what keeps `savePopup` from enqueuing a delta family the
 * server would 403. Absent must always leave the stored value ALONE.
 */
export interface PopupDraft {
  /** With `desc` and `color`: the STRUCTURAL half — drawn only behind `canEditMap`. */
  label?: string;
  desc?: string;
  color?: string;
  /** Drawn only behind `canAssignTags`. */
  tags?: TagId[];
  ai?: AiDraft;
  /**
   * The node's DIRECT responsibles, full-replace (V0.37). Drawn only behind `canAssign` —
   * effective responsibles delegate down their own subtree, EDIT_ALL_DOCS anywhere. Not part
   * of the undo entry: assignment is an immediate act on its own endpoint, not a map edit.
   */
  responsibles?: string[];
  /** The node's DIRECT group responsibles, full-replace alongside `responsibles` above
   * (V0.37.1) — always present together, since the picker draws one combined list. */
  responsibleGroups?: string[];
  /**
   * The documentation PLAN (V0.38), wrapped so "the editor was not drawn" (absent) and "no
   * plan" (`{ plan: null }`) stay two different sentences. Drawn behind `canAssign`, saved
   * through its own immediate route like the responsibles.
   */
  docPlan?: { plan: DocPlan | null };
  /**
   * The node's documentation location inside its application (V0.46), behind `EDIT_MAP`.
   *
   * WRAPPED, AND IT CARRIES WHAT IT CAME FROM. `docPath` is not on the `/api/map` payload — the
   * popup's field FETCHES it, lazily, on the first interaction with it — so absent here means
   * "never loaded", which covers both "this viewer never saw the field" and "they never touched
   * it". `from` is what the server held when it loaded, so a value typed and then put back
   * sends nothing: without it, opening the field and pressing Save would write on every save of
   * every node whose location nobody changed.
   */
  docPath?: { path: string | null; from: string | null };
}

/**
 * The popup's Save.
 *
 * The region owns the form; this is the WRITE. It pushes an undo entry, marks two dirty sets
 * and — since V0.45 — FLUSHES, rather than leaving the write in the 800 ms debounce. Pressing
 * Save is a person saying "that's done"; closing the tab a second later used to lose it, with
 * the browser's "Leave site?" prompt as the only clue that anything was pending.
 *
 * The debounce stays for drags and renames, where a keystroke should not be a request.
 *
 * ASYNC, and the awaits are load-bearing: `setNodeResponsibles` and `commitDocPlan` write
 * through their own routes and both bump `dataVersion`, so a structural PUT built before
 * their `noteGranularVersion` landed is a 409. The debounce hid that race by being slower
 * than the two requests; flushing immediately does not, so they are waited for first.
 */
export async function savePopup(id: NodeId, draft: PopupDraft): Promise<void> {
  const n = S.nodes[id];
  if (!n) return;
  // The two immediate routes, collected as they are fired so the flush below can wait for
  // them. Nothing here changes WHICH of them run.
  const immediate: Promise<unknown>[] = [];
  const prev: Record<string, unknown> = {
    label: n.label,
    desc: n.desc,
    color: n.color,
    tags: [...(n.tags || [])],
    aiPrompt: n.aiPrompt,
  };
  for (const k of CONTEXT_SOURCE_KEYS) prev[k] = (n as unknown as Record<string, unknown>)[k];
  prev.liveProbeEnabled = n.liveProbeEnabled;
  prev.appTargetId = n.appTargetId;
  prev.appPath = n.appPath;

  // The STRUCTURAL half — name, description, colour — only when the form drew it (V0.37):
  // a draft from a popup without `canEditMap` carries none of these, and applying defaults
  // in their place would both erase real values and enqueue a delta family the server would
  // refuse. Same rule for the tag chips below.
  const structuralDrawn = draft.label !== undefined;
  if (structuralDrawn) {
    n.label = (draft.label || '').trim() || 'Untitled';
    n.desc = draft.desc;
    if (!n.root && draft.color) n.color = draft.color;
    // Doc-link fields are display-only in the popup — attaching goes through the picker.
    Writes.dirtyNodeTranslations.add(n.id);
  }
  const tagsDrawn = draft.tags !== undefined;
  if (tagsDrawn) n.tags = [...(draft.tags || [])];

  let aiChanged = false;
  if (draft.ai) {
    aiChanged = applyAiFields(n, draft.ai);
    // (V0.42) The "No doc" tag no longer clears the prompt — the tag is retired. Exempting a
    // node ("nothing expected") is now a documentation-plan act, not a tag side effect.
  }
  if (aiChanged) Writes.dirtyAiFields.add(n.id);

  // The responsibles (V0.37, groups added V0.37.1). An immediate act on its own endpoint —
  // not a dirty set, not an undo leg (undoing an assignment is re-assigning, which the picker
  // already offers), and absent means the picker was not drawn (or its seed was never
  // trustworthy — the directories had not loaded; see NodePopup's `respSeedTrusted`).
  // Fired beside the other flushes, not awaited: the popup has closed, and the action itself
  // reports a refusal.
  if (draft.responsibles !== undefined) {
    const nextUsers = [...draft.responsibles].sort();
    const nextGroups = [...(draft.responsibleGroups ?? [])].sort();
    // `n.responsibles` is the server's EXPANDED list (group members folded in), while the
    // draft holds only the direct individuals — compare like with like, or an untouched save
    // on a group-assigned node "changes" every time and bumps dataVersion for the whole room.
    const covered = new Set<string>();
    for (const gid of n.responsibleGroups ?? []) {
      const g = S.groupDirectory.find((x) => x.id === gid);
      for (const uid of g?.memberIds ?? []) covered.add(uid);
    }
    const curUsers = [...(n.responsibles ?? [])].filter((uid) => !covered.has(uid)).sort();
    const usersChanged = JSON.stringify(nextUsers) !== JSON.stringify(curUsers);
    const groupsChanged =
      JSON.stringify(nextGroups) !== JSON.stringify([...(n.responsibleGroups ?? [])].sort());
    if (usersChanged || groupsChanged) {
      immediate.push(setNodeResponsibles(id, nextUsers, nextGroups));
    }
  }

  // The documentation plan (V0.38) — the responsibles' contract exactly: an immediate act on
  // its own endpoint, no dirty set, no undo leg, fired only when the drawn editor changed it.
  // The "did it change?" test is `commitDocPlan`'s now (V0.43.2), shared with the node panel's
  // document rows, which have to record a plan before acting on it.
  if (draft.docPlan !== undefined) immediate.push(commitDocPlan(id, draft.docPlan.plan));

  // The documentation location (V0.46) — the same immediate-route contract, and the comparison
  // is against what the FIELD loaded rather than against anything in `S.nodes`, because the map
  // payload does not carry this column. See `PopupDraft.docPath`.
  if (draft.docPath !== undefined && draft.docPath.path !== draft.docPath.from) {
    immediate.push(setDocPath(id, draft.docPath.path));
  }

  const changed =
    (structuralDrawn &&
      (prev.label !== n.label || prev.desc !== n.desc || prev.color !== n.color)) ||
    (tagsDrawn && JSON.stringify(prev.tags) !== JSON.stringify(n.tags)) ||
    aiChanged;

  if (changed) {
    pushUndo('edit "' + prev.label + '"', async () => {
      const cur = S.nodes[id];
      if (!cur) return;
      if (structuralDrawn) {
        cur.label = prev.label as string;
        cur.desc = prev.desc as string;
        cur.color = prev.color as string;
        Writes.dirtyNodeTranslations.add(id);
      }
      if (tagsDrawn) cur.tags = [...(prev.tags as TagId[])];
      if (aiChanged) {
        cur.aiPrompt = prev.aiPrompt as string;
        for (const k of CONTEXT_SOURCE_KEYS) (cur as unknown as Record<string, unknown>)[k] = prev[k];
        cur.liveProbeEnabled = prev.liveProbeEnabled as boolean;
        cur.appTargetId = prev.appTargetId as string | null;
        cur.appPath = prev.appPath as string | null;
        Writes.dirtyAiFields.add(id);
      }
      render();
      renderTags();
      // REFRESH, not re-open: an undo landing behind an open popup must not discard what is
      // being typed in it.
      if (S.popupFor === id) refreshPopup(id);
      save();
    });
  }
  render();
  renderTags();
  // saveLocal() rather than save(): the durable prefs and the view record are this browser's,
  // and the server half is flushed explicitly below instead of being scheduled.
  saveLocal();
  // allSettled, not all: a refused responsible assignment already reports itself, and it must
  // not stop the map edit beside it from reaching the server.
  await Promise.allSettled(immediate);
  await flushNow();
}

/**
 * Detach one custom doc.
 *
 * `render()` where the original re-drew the popup's own list by hand and left the map's "+N"
 * bean count stale until something else happened to render — a distinction that stops existing
 * the moment the popup and the map read one state.
 */
export function removeCustomDoc(nodeId: NodeId, docId: DocId): void {
  const n = S.nodes[nodeId];
  if (!n) return;
  n.customDocs = (n.customDocs || []).filter((d) => d !== docId);
  save();
  render();
}

/**
 * "Run live verification".
 *
 * Pending edits are written first, and AWAITED: the server resolves the target from the
 * node's STORED state, so an unsaved target or path is not merely late, it is ignored — the
 * run would visit whatever the node used to point at. Going through the debounce meant
 * pressing this within 800 ms of choosing a target did exactly that.
 *
 * The same rule `generateDocForNode` already follows, and the caller shows a busy state for
 * as long as this takes, so the flush is visible rather than silent.
 */
export async function runLiveVerifyFromPopup(id: NodeId, ai: AiDraft): Promise<void> {
  const n = S.nodes[id];
  if (!n) return;
  if (applyAiFields(n, ai)) {
    Writes.dirtyAiFields.add(n.id);
    saveLocal();
  }
  await flushDirtyAiFields();
  startLiveVerify(n.id);
}

/* ============================================== my items (favorites + recents, 2.4) === */

/**
 * Load this user's favorites and recently-viewed docs. Called from `enterApp()` after
 * `applyPermissions()` and deliberately NOT awaited there: the pane is additive chrome, so a
 * slow or failing request means an empty pane, never a blocked boot. On failure `loaded`
 * stays false, which is how the pane knows to keep its empty-state hints to itself.
 */
export async function loadMyItems(): Promise<void> {
  // Reset first: a second user signing in on this browser must not see the first's items
  // while their own request is in flight.
  S.myItems = { favorites: [], recents: [], loaded: false };
  try {
    const items = await Me.fetchMyItems();
    S.myItems = { favorites: items.favorites, recents: items.recents, loaded: true };
  } catch {
    // Signed out mid-flight, offline, or a server that predates /api/me: empty pane.
  }
  render();
}

/**
 * Load everything assigned to this user (V0.38) — `loadMyItems()`'s contract exactly: called
 * beside it from `enterApp()`, never awaited, reset first, failure means an empty section.
 * Bare refs only; MyItems resolves labels, titles and visibility from state at render time.
 */
export async function loadMyAssignments(): Promise<void> {
  S.myAssignments = { nodes: [], docs: [], loaded: false };
  try {
    const a = await Me.fetchAssignments();
    S.myAssignments = { nodes: a.nodes ?? [], docs: a.docs ?? [], loaded: true };
  } catch {
    // Signed out mid-flight, offline, or a server that predates V0.38: no section.
  }
  // If the eye is already on, its highlight set is derived from these — keep it in step.
  if (S.eyeOn) {
    recomputeAssignHighlight();
    applyReveal();
  }
  render();
}

/* ---------------------------------------------------------- foldable sections (V0.50) */

/**
 * Is this section folded shut? Default OPEN — absence from the set is the answer.
 *
 * The keys are the components' own (`'todos'`, `'myitems:assignments'`, …) and are never listed
 * anywhere central: a second list of which sections exist is free to disagree with the
 * components that draw them, and nothing here needs to know.
 */
export function isSectionCollapsed(key: string): boolean {
  return S.collapsedSections.has(key);
}

/**
 * Fold a section shut, or open it. STATE IN, NOT A FLIP.
 *
 * The flip version was wrong, and wrong in a way only one caller could show. The popup's
 * To-dos section derives its default from its CONTENTS — folded when empty, open when there is
 * something — so an empty section is shut while ABSENT from this set. A flip would then read
 * "not in the set" and ADD it, leaving the section shut and the click doing nothing visible.
 * The caller knows what it is currently drawing; this takes the answer rather than guessing it.
 *
 * `saveViewState()` and nothing else: it writes the day-scoped per-user record to localStorage
 * and deliberately does not touch the server or raise the "Saved" badge. That is the right
 * instrument here for V0.49.2's reason — the badge answers "the thing you changed has been
 * kept", and folding a heading changes nothing anybody needs told about.
 */
export function setSectionCollapsed(key: string, collapsed: boolean): void {
  const next = new Set(S.collapsedSections);
  if (collapsed) next.add(key);
  else next.delete(key);
  if (next.size === S.collapsedSections.size) return; // nothing moved; do not write or render
  S.collapsedSections = next;
  saveViewState();
  render();
}

/**
 * The NODE POPUP's folds (V0.51), whose default is the other way round: shut, so what is stored
 * is what this user has OPENED.
 *
 * Why remembered at all: a fold opened on one node is open on the NEXT node opened. Somebody
 * working through documentation wants that section on every node they visit, and re-opening it
 * per node is the friction this rule exists to remove. Per user, day-scoped, `saveViewState()`
 * and nothing else — folding a section is not a save (V0.49.2's badge rule).
 *
 * STATE IN, NOT A FLIP, for `setSectionCollapsed`'s reason above: `<details>` is a controlled
 * element here and the caller already knows which way it just moved.
 */
export function setSectionOpen(key: string, open: boolean): void {
  const next = new Set(S.openSections);
  if (open) next.add(key);
  else next.delete(key);
  if (next.size === S.openSections.size) return; // nothing moved; do not write or render
  S.openSections = next;
  saveViewState();
  render();
}

/** Is this popup fold open? Default SHUT — absence from the set is the answer. */
export function isSectionOpen(key: string): boolean {
  return S.openSections.has(key);
}

/* ================================================================= to-dos (V0.50) === */

/**
 * Load this user's open to-dos. `loadMyItems()`'s contract exactly — called beside it from
 * `enterApp()`, never awaited, reset first, and a failure means an empty section rather than a
 * blocked boot.
 *
 * ROWS, NOT REFS, unlike every other list in this family: a to-do's title exists nowhere but
 * its own row, so there is nothing for the client to resolve it from. Visibility is still the
 * server's — a to-do on a branch this user cannot see is never sent, even when it names them.
 */
export async function loadMyTodos(): Promise<void> {
  // Reset first: a second user signing in on this browser must not see the first's work while
  // their own request is in flight.
  S.myTodos = { todos: [], loaded: false };
  if (!canViewTodosFor(S.perm)) {
    // No permission is a complete answer, not a failure: `loaded` stays false and the section
    // is not drawn at all, so an empty hint never flashes for somebody who has no to-dos
    // feature. Skipping the request also keeps a guaranteed 403 out of everybody's console.
    render();
    return;
  }
  try {
    const { todos } = await Todos.fetchMyTodos(S.currentLocale);
    S.myTodos = { todos, loaded: true };
  } catch {
    // Signed out mid-flight, offline, or a server that predates V0.50: no section.
  }
  render();
}

/**
 * Say that some to-do, somewhere, changed.
 *
 * ONE counter rather than each surface telling the others. The pane reads `S.myTodos`; the node
 * popup's section keeps its own node's list, because that list is not a subset of this user's
 * work and re-deriving it from the pane would be wrong for every to-do belonging to somebody
 * else. Both watch this number, so a row ticked in either place refreshes the other without
 * either knowing the other exists.
 */
/**
 * Open the node a to-do lives on, with that to-do EXPANDED (V0.50.1).
 *
 * What a row in the "My items" pane does when clicked. Three things have to be true when the
 * popup lands and none of them is the popup's to arrange: the branch must be revealed (the node
 * may be under a fold), the section must not be shut (this reader may have folded it days ago),
 * and the section must know which row to open. So the first two happen here and the third is
 * handed over as `focusTodoId`, which the section consumes once and clears.
 *
 * `revealNodeOnMap` does the rest — expand ancestors, lay out, centre, and fly the node into
 * its seat — because a to-do has no place on the map of its own; its node does.
 */
export function revealTodo(nodeId: NodeId, todoId: string): void {
  S.focusTodoId = todoId;
  // Un-fold the section rather than asking the reader to. They clicked the to-do; being shown
  // the heading it lives under would be a worse answer than the one they asked for.
  setSectionOpen(SECTION_TODOS, true);
  revealNodeOnMap(nodeId);
}

/** The section says "I have used it". One-shot, so a later popup is not haunted by it. */
export function consumeFocusTodo(): void {
  if (S.focusTodoId === null) return;
  S.focusTodoId = null;
  render();
}

export function notifyTodosChanged(): void {
  S.todosVersion += 1;
  void loadMyTodos();
  // The heatmap's project bars are rollups of the same to-dos (V0.50.3).
  void loadProjectProgress();
}

/**
 * Tick or un-tick, from wherever.
 *
 * NOT OPTIMISTIC, deliberately, unlike `toggleFavorite` beside it. A star is this user's own
 * low-stakes bookkeeping; a to-do is a claim about shared work that a colleague may be reading,
 * and a tick that appeared and then silently reverted would be worse than one that took a beat.
 * The 403 path is real here too — the server refuses a row this user is not the assignee of.
 */
export async function setTodoDone(id: string, done: boolean): Promise<void> {
  await Todos.setTodoState(id, done ? 'done' : 'open');
  notifyTodosChanged();
}

export function isFavorite(targetType: Me.FavTargetType, targetId: string): boolean {
  return S.myItems.favorites.some((f) => f.targetType === targetType && f.targetId === targetId);
}

/**
 * Star or un-star a node or a document. OPTIMISTIC: the list moves immediately and rolls
 * back if the server refuses — the star is a low-stakes toggle and a round-trip delay on
 * every click would make it feel broken.
 */
export function toggleFavorite(targetType: Me.FavTargetType, targetId: string): void {
  if (!S.currentUser) return;
  const prev = S.myItems.favorites;
  const was = isFavorite(targetType, targetId);
  S.myItems.favorites = was
    ? prev.filter((f) => !(f.targetType === targetType && f.targetId === targetId))
    : [...prev, { targetType, targetId }];
  render();
  const req = was ? Me.deleteFavorite(targetType, targetId) : Me.putFavorite(targetType, targetId);
  req.catch(() => {
    // Roll back to the exact container we replaced — everything since was derived from it.
    S.myItems.favorites = prev;
    render();
  });
}

/**
 * Note that a document was viewed. Docs only, by design — node popups open far too often to
 * mean "viewed". Called from `openDoc()` (every view open) and from `closeDoc()` when an
 * EDITOR session ends there — see the note in `openDocEditor` for why the editor records at
 * session end rather than open. Optimistic bump + fire-and-forget POST; the server dedupes
 * with an upsert, so a locale switch re-opening the same page bumps rather than duplicates
 * (and the client filter below does the same for the list on screen). Callers announce —
 * every doc-open and doc-close path ends in `docStateChanged()` — so this does not publish
 * on its own.
 */
export function recordRecent(docId: DocId): void {
  if (!docId || !S.currentUser) return;
  S.myItems.recents = [
    { targetType: 'doc' as const, targetId: docId, lastAt: new Date().toISOString() },
    ...S.myItems.recents.filter((r) => r.targetId !== docId),
  ].slice(0, 20);
  void Me.postRecent(docId).catch(() => {
    // Nothing to roll back: "recently viewed" is a fact about this session either way, and
    // the next loadMyItems() re-syncs the list to what the server kept.
  });
}

/* ================================== the bell: subscriptions + the feed (plan Phase 3) === */

const subKey = (targetType: Me.FavTargetType, targetId: string) => `${targetType}:${targetId}`;

/**
 * Load what this user watches. `loadMyItems()`'s contract exactly: called from `enterApp()`
 * after `applyPermissions()`, never awaited, reset-first so a second user signing in on this
 * browser cannot see the first's bells while their own request is in flight.
 */
export async function loadSubscriptions(): Promise<void> {
  S.subscriptions = new Set();
  S.subscriptionsLoaded = false;
  try {
    const res = await Me.fetchSubscriptions();
    S.subscriptions = new Set(res.subscriptions.map((r) => subKey(r.targetType, r.targetId)));
    S.subscriptionsLoaded = true;
  } catch {
    // Signed out mid-flight, offline, or a server that predates the feed: no toggles drawn.
  }
  render();
}

export function isSubscribed(targetType: Me.FavTargetType, targetId: string): boolean {
  return S.subscriptions.has(subKey(targetType, targetId));
}

/**
 * Watch or un-watch a node or a document. OPTIMISTIC, with rollback — `toggleFavorite`'s
 * shape, for the same reason. The Set is REPLACED, never mutated: the snapshot adopts it by
 * reference, so identity is what carries "this changed".
 */
export function toggleSubscription(targetType: Me.FavTargetType, targetId: string): void {
  if (!S.currentUser) return;
  const prev = S.subscriptions;
  const key = subKey(targetType, targetId);
  const was = prev.has(key);
  const next = new Set(prev);
  if (was) next.delete(key);
  else next.add(key);
  S.subscriptions = next;
  render();
  const req = was ? Me.deleteSubscription(targetType, targetId) : Me.putSubscription(targetType, targetId);
  req.catch(() => {
    // Roll back to the exact container we replaced.
    S.subscriptions = prev;
    render();
  });
}

/**
 * Re-ask the server how many unread things the bell should show. Called from `enterApp()`
 * (sign-in is the "mount" that matters — the region exists before boot does), on window
 * focus (the bell's own listener), after a doc save (`afterDocSaved`), and after a rename whose
 * response says it moved the feed (`adoptNodeSlug`). NEVER from a timer: no polling is a design
 * rule, not an optimisation.
 */
export async function refreshFeedBadge(): Promise<void> {
  if (!S.currentUser) return;
  try {
    const f = await Me.fetchFeed(S.currentLocale);
    S.feedUnread = f.unread.subscribed + f.unread.changes;
  } catch {
    return; // keep the count we had; a transient failure must not blank a real badge
  }
  render();
}

/**
 * Move the watermark. 'changes' is the panel OPENING (the All-changes count clears, unread
 * notifications stay bold and counted); 'all' is the "Mark all read" button. The caller
 * knows what the badge shows next — the panel just fetched the feed — so it passes the
 * remainder rather than this refetching to learn it.
 */
export function markFeedSeen(scope: 'changes' | 'all', remainingUnread: number): void {
  if (!S.currentUser) return;
  void Me.postFeedSeen(scope).catch(() => {});
  S.feedUnread = Math.max(0, remainingUnread);
  render();
}

/** One notification clicked: read on the server, one less on the badge. */
export function markNotificationRead(id: string): void {
  if (!S.currentUser) return;
  void Me.postNotificationRead(id).catch(() => {});
  S.feedUnread = Math.max(0, S.feedUnread - 1);
  render();
}

/* ================================= the documentation workflow + assignments (V0.38) === */

/**
 * The bulk workflow map, resolved for the CURRENT locale.
 *
 * `loadSubscriptions()`'s contract exactly — called from `enterApp()` after
 * `applyPermissions()`, never awaited, RESET FIRST so a second user signing in on this browser
 * cannot see the first's answers while their own request is in flight — plus one gate of its
 * own: `GET /api/docs/workflows` is participant-gated (any brick, or the AI operator — V0.37),
 * so asking without one is a guaranteed 403 and a console error on every sign-in of every
 * reader.
 *
 * PER LOCALE, and that is why `setLocale()` calls it again — with the requested → source
 * fallback resolved SERVER-side (V0.38): each entry says which locale it actually describes
 * (`locale`, `fallback`), so a page with no current-locale row still has an entry, honestly
 * labelled as the source-language stand-in.
 *
 * The entries are built fresh here and never mutated afterwards — every patch below replaces
 * one — which is what lets the store compare them by identity.
 */
export async function loadDocWorkflows(): Promise<void> {
  S.docWorkflow = {};
  S.docWorkflowLoaded = false;
  if (!S.currentUser || !canEnterEdit()) {
    render();
    return;
  }
  try {
    const res = await apiRequest<{ lang: string; byDoc: Record<DocId, RawWorkflowEntry> }>(
      '/api/docs/workflows',
      { query: { lang: S.currentLocale } },
    );
    const next: Record<DocId, DocWorkflowEntry> = {};
    for (const [docId, r] of Object.entries(res.byDoc || {})) {
      next[docId] = {
        workflowState: Docs.asWorkflowState(r?.workflowState),
        locale: r?.locale || S.currentLocale,
        fallback: !!r?.fallback,
        reviewedAt: r?.reviewedAt ?? null,
        // The eligible doc responsibles at the entry's locale (groups expanded server-side) —
        // they ride this fetch and never the map payload, which is the documented latency of
        // doc-level assignment.
        responsibles: Array.isArray(r?.responsibles) ? r.responsibles : [],
        responsibleGroups: Array.isArray(r?.responsibleGroups) ? r.responsibleGroups : [],
      };
    }
    S.docWorkflow = next;
    S.docWorkflowLoaded = true;
  } catch {
    // Signed out mid-flight, offline, or a server that predates the workflow: no dots.
  }
  render();
}

/** What `GET /api/docs/workflows` puts in `byDoc`, before it is normalised. */
interface RawWorkflowEntry {
  workflowState?: string;
  locale?: string;
  fallback?: boolean;
  reviewedAt?: string | null;
  responsibles?: string[];
  responsibleGroups?: string[];
}

/**
 * The account directory, for the responsibles surfaces — and for every to-do surface.
 *
 * `loadDocWorkflows()`'s contract, gate included. `GET /api/users/directory` opens to the same
 * two audiences the server names: PARTICIPANTS, because assigning a responsible means choosing
 * a person, and — since V0.50.2 — anyone holding `VIEW_TODOS`, because a to-do list whose
 * assignees read as account ids is not a list anybody can act on, and the to-do manager the
 * Projects tab is for holds no brick at all. It is the one place this app lets a user enumerate
 * other accounts, a real privacy widening recorded in `PRIVACY.md` §1; asking for it as anyone
 * else would be both a 403 and a wrong question, so the gate here mirrors the server's exactly.
 */
export async function loadUserDirectory(): Promise<void> {
  S.userDirectory = [];
  S.userDirectoryLoaded = false;
  S.groupDirectory = [];
  S.groupDirectoryLoaded = false;
  if (!S.currentUser || !(canEnterEdit() || canViewTodosFor(S.perm))) {
    render();
    return;
  }
  try {
    const [users, groups] = await Promise.all([
      apiRequest<DirectoryUser[]>('/api/users/directory'),
      apiRequest<DirectoryGroup[]>('/api/users/directory/groups'),
    ]);
    S.userDirectory = users;
    S.userDirectoryLoaded = true;
    S.groupDirectory = groups;
    S.groupDirectoryLoaded = true;
  } catch {
    // The responsibles rows fall back to showing raw ids, which is honest about not knowing.
  }
  render();
}

/* ------------------------------------------------------- the clearance heatmap (V0.39) */

/** Load-ordering latch: a slow answer must never overwrite a newer one's. */
let mapHealthSeq = 0;

/**
 * Ask the server how the map's documentation stands — `GET /api/map/health`, per-node
 * workflow counts for the CURRENT locale. `loadDocWorkflows()`'s contract, gate included:
 * the endpoint is participant-gated, so a non-participant never spends a guaranteed 403.
 *
 * DELIBERATELY NOT CACHED, and refetched from exactly three places: `setHeatmap(true)`,
 * the quiet refresh completing while the mode is on (`mapSync.refreshMapInPlace`), and this
 * tab's own doc save / workflow verb landing (`noteDocWorkflow`, plus the generation
 * completion beside its `loadDocWorkflows()`). NEVER on window focus — that is a poll in
 * disguise, and this codebase's anti-polling stance is explicit.
 *
 * Answers whether it worked, so the EXPLICIT toggle can speak a failure; every other caller
 * stays quiet. On failure the mode stays wherever it is over an EMPTY index — the nodes keep
 * their own colours, which is honest about not knowing.
 */
export async function loadMapHealth(): Promise<boolean> {
  const seq = ++mapHealthSeq;
  if (!S.currentUser || !canEnterEdit()) {
    S.mapHealth = { byNode: {}, loaded: false };
    render();
    return false;
  }
  try {
    const res = await apiRequest<{ locale: string; byNode: Record<NodeId, NodeHealthCounts> }>(
      '/api/map/health',
      { query: { locale: S.currentLocale }, cache: 'no-store' },
    );
    if (seq !== mapHealthSeq) return true; // superseded — the newer request owns the answer
    S.mapHealth = { byNode: res.byNode || {}, loaded: true };
    render();
    return true;
  } catch {
    if (seq === mapHealthSeq) {
      S.mapHealth = { byNode: {}, loaded: false };
      render();
    }
    return false;
  }
}

/**
 * The clearance heatmap's mode switch — `setAiDocMode`'s shape: self-gated on
 * `editing && canEnterEdit()` (the same participant population the map's assignment fields
 * open to), part of the day-scoped view record, and cleared by `setEditing(false)`.
 *
 * `announceFailure` is the EXPLICIT-toggle path only (`toggleHeatmap`): a restore, a deep
 * link and a refetch all stay quiet, because an alert nobody's click caused is noise.
 */
export function setHeatmap(on: boolean, opts: { announceFailure?: boolean } = {}): void {
  const want = !!on && S.editing && canEnterEdit();
  if (want === S.heatmapOn) return;
  S.heatmapOn = want;
  // The paint is colour-only, so an announce repaints the nodes either way: the heat index is
  // rebuilt per publish, and with the mode off it is empty by construction.
  announce();
  // Heatmap mode wants the documentation rows at any zoom, so it SWITCHES THE TOGGLE ON
  // (V0.42.2) rather than special-casing the layout — and, since V0.44.1, leaving heatmap
  // switches it OFF again. That REVERSES V0.42.2's decision, which read "leaving heatmap does
  // not force it off: the toggle is the user's state from then on", and the reversal is
  // deliberate, asked for after living with it: the rows the heatmap needed are noise once the
  // colours are gone, and having to un-pin them by hand every time was the complaint. A user who
  // wants the rows without the heatmap still has the corner button; it is simply no longer left
  // on by a mode they have just left. (`setHeatmap(false)` when already off returns above, so
  // nothing un-pins a toggle the heatmap never touched.)
  setDocsPinned(want);
  // On or off, the heatmap changes which dotted planned pills are drawn, so the rows have to
  // be laid out again even when `docsExpanded` itself does not move (V0.42.3).
  syncDocsExpand();
  if (want) {
    void loadMapHealth().then((ok) => {
      if (!ok && opts.announceFailure && S.heatmapOn) {
        alert('Could not load the documentation standing — the map keeps its own colours.');
      }
    });
  }
  saveViewState(); // day-scoped, beside aiDocMode; no-op until the boot restore has run
  Router.syncUrl('filter'); // ?heat=clearance while on, absent while off
}

export const toggleHeatmap = (): void => setHeatmap(!S.heatmapOn, { announceFailure: true });

/**
 * Record what the server now says about one page+locale's workflow state.
 *
 * The writer of `S.docWorkflow`'s STATE after the bulk load, and both state sources funnel
 * through it: a transition's response, and the translation PUT's response — which carries the
 * post-demotion state, so a save that un-approved a page says so WITHOUT a refetch. (The one
 * other entry writer is `setDocResponsibles`, which touches only the assignment lists.)
 *
 * IT IGNORES ANY LOCALE BUT THE CURRENT ONE. A verb never fires on a fallback page any more
 * (the popover shows those read-only), so what lands here is always about the language being
 * shown — including the save that CREATES the missing translation, which is exactly when a
 * `fallback: true` entry flips to a real current-locale one. The responsibles carry over only
 * when the entry already described this locale; a flip resets them to empty, because the old
 * lists were the SOURCE locale's and the next workflows fetch owns the truth.
 */
export function noteDocWorkflow(
  docId: DocId,
  locale: string,
  entry:
    | { workflowState?: string; reviewedAt?: string | null; slugDrifted?: boolean }
    | null
    | undefined,
): void {
  /**
   * THE MARK, from the save's own response — the doc bar's half of what `adoptNodeSlug` does
   * for a node. `docSlugDrift` normally arrives on `GET /api/map`, which a client never
   * refetches for its own write, so retitling a page left the "!" absent until a reload.
   *
   * Applied BEFORE the workflow guard below, deliberately: a save answering no workflow state
   * (the e2e write guard's `{ok:true}`) still must not have its drift answer thrown away —
   * and, more to the point, the two facts are independent and one missing is no reason to drop
   * the other.
   */
  if (docId && typeof entry?.slugDrifted === 'boolean') {
    const had = S.docSlugDrift[docId] === true;
    if (had !== entry.slugDrifted) {
      S.docSlugDrift = { ...S.docSlugDrift };
      if (entry.slugDrifted) S.docSlugDrift[docId] = true;
      else delete S.docSlugDrift[docId];
      render();
    }
  }

  // A response that names no state is not a record of one. The write guard the e2e suite
  // installs answers `{ok:true}` to every non-GET, and inventing a 'draft' from that would
  // have the app report a workflow state the server never mentioned.
  if (!docId || typeof entry?.workflowState !== 'string' || locale !== S.currentLocale) return;
  const prev = S.docWorkflow[docId];
  const sameLocale = !!prev && prev.locale === locale && !prev.fallback;
  S.docWorkflow = {
    ...S.docWorkflow,
    [docId]: {
      workflowState: Docs.asWorkflowState(entry.workflowState),
      locale,
      fallback: false,
      reviewedAt: entry.reviewedAt ?? null,
      // Transition and save responses do not carry the responsibles — keep what the bulk
      // load knew rather than blanking a list this patch was never talking about.
      responsibles: sameLocale ? prev.responsibles : [],
      responsibleGroups: sameLocale ? prev.responsibleGroups : [],
    },
  };
  render();
  // This tab just moved a page's standing — a save's demotion, or a workflow verb. The heat
  // is per NODE, so the counts are re-asked rather than guessed at from one page's state.
  if (S.heatmapOn) void loadMapHealth();
}

/** What one of the four verbs answers with. */
export interface WorkflowTransitionResponse {
  ok?: boolean;
  docPageId: DocId;
  locale: string;
  from: WorkflowState;
  workflowState: WorkflowState;
  reviewedAt: string | null;
  reviewedByUserId?: string | null;
  reviewedByName: string | null;
  eventId?: string;
}

/**
 * Move one page+locale's workflow state — the ONE place a transition happens.
 *
 * OPTIMISTIC WITH ROLLBACK TO THE EXACT CONTAINER, `toggleSubscription`'s shape: the map is
 * replaced, never mutated, so identity carries "this changed", and a refusal restores the
 * object that was replaced rather than reconstructing what it probably held.
 *
 * `locale` is the caller's, and it must be the RESOLVED one — the locale the page is actually
 * being READ in, which both `GET /api/docs/:id` and the workflows entry report and which is
 * not always `currentLocale`. Passing the requested locale instead is how an editor reading an
 * English fallback approves a French page that does not exist; the server 404s that, but the
 * honest fix is at this end — WorkflowControl offers no verbs on a fallback at all.
 */
export async function setDocWorkflow(
  docId: DocId,
  locale: string,
  verb: 'request' | 'approve' | 'reject' | 'flag',
  note?: string,
): Promise<WorkflowTransitionResponse | null> {
  const prev = S.docWorkflow;
  const optimistic = Docs.WORKFLOW_VERBS.find((v) => v.verb === verb)?.to ?? 'draft';
  const entry = prev[docId];
  if (locale === S.currentLocale && (!entry || (entry.locale === locale && !entry.fallback))) {
    S.docWorkflow = {
      ...prev,
      [docId]: {
        workflowState: optimistic,
        locale,
        fallback: false,
        reviewedAt: new Date().toISOString(),
        responsibles: entry?.responsibles ?? [],
        responsibleGroups: entry?.responsibleGroups ?? [],
      },
    };
    render();
  }
  try {
    const res = await apiRequest<WorkflowTransitionResponse>(`/api/docs/${docId}/review/${verb}`, {
      method: 'POST',
      body: { locale, note: note?.trim() ? note.trim() : null },
    });
    noteDocWorkflow(docId, locale, res);
    return res;
  } catch (e) {
    // Roll back to the exact container we replaced.
    S.docWorkflow = prev;
    render();
    console.error('Workflow transition failed:', e);
    return null;
  }
}

/**
 * Translate the open page into `targetLocale` and show the result (V0.41). The doc bar offers
 * this on a FALLBACK page — one the viewer is reading in another language because theirs does
 * not exist yet — so on success the page now HAS a `targetLocale` version: refresh the workflow
 * map (the `fallback: true` entry flips to a real one) and re-open the document so the overlay
 * loads what was just written. The wire is `Writes.translateDoc`, the same request the AI
 * console's bulk loop makes; only the follow-up differs. Server-gated (AI_ADMIN ∨ the language's
 * edit scope, plus the `translate` capability), so this is refused for anyone the button should
 * not have been drawn for.
 */
export async function translateDoc(docId: DocId, targetLocale: string): Promise<boolean> {
  try {
    await Writes.translateDoc(docId, targetLocale);
    await loadDocWorkflows();
    if (S.docCurrentId === docId) {
      openDocInCurrentMode(docId, S.docCurrentTitle || '', S.docNavNodeId);
    }
    return true;
  } catch (e) {
    const msg = (e as { body?: { error?: string } } | null)?.body?.error;
    alert(msg || 'Could not translate this document.');
    return false;
  }
}

/* ===================================================================== the doc view === */

/**
 * One place to announce a change: React re-renders, and the presence socket learns where this
 * person now is. Every entry point below ends with it.
 */
function docStateChanged(): void {
  announce();
  sendLoc();
  // The doc view is a navigation: opening, closing or switching mode moves the URL (plan
  // 1.6). Re-stating the same page is a no-op inside syncUrl, so the REFRESH-style re-open
  // call sites cost nothing here.
  Router.syncUrl('nav');
}

/** The node that owns a docId in any of its slots — the fallback when a caller has no nodeId. */
export function ownerNodeOfDoc(docId: DocId | null | undefined): NodeId | null {
  if (!docId) return null;
  for (const id in S.nodes) {
    const n = S.nodes[id];
    if (
      n.docId === docId ||
      n.internalDocId === docId ||
      n.courseDocId === docId ||
      n.demoDocId === docId ||
      (n.customDocs || []).includes(docId)
    ) {
      return id;
    }
  }
  return null;
}

/**
 * Open a page — THE opener (V0.53.5). The URL is opaque here; `docUrlFor()` is where it is built.
 *
 * There is no "for reading" any more: the page arrives in whatever mode the app is in, and
 * whether the editor mounts is DERIVED (`docEditorActive`) from the one flag, this page and
 * this user's scope on it. Every entry point — a pill, the band, a search result, a feed row,
 * a deep link, a colleague's avatar — ends here, so the answer cannot differ by where you
 * clicked.
 */
export function openDoc(url: string, title?: string, docId?: DocId | null, nodeId?: NodeId | null): void {
  const u = (url || '').trim();
  if (!u) return;
  S.docCurrent = u;
  S.docCurrentId = docId || null;
  S.docCurrentTitle = title || 'Documentation';
  S.docNavNodeId = nodeId && S.nodes[nodeId] ? nodeId : ownerNodeOfDoc(docId);
  S.docOpen = true;
  S.docOpenSeq++;
  // A page that opens INTO THE EDITOR records as a recent when the sitting ends
  // (`finishCloseDoc`, `setEditing(false)`), not now: an at-open beacon lands inside the editor
  // specs' "nothing reached the server" windows (33-doc-picker, 34-doc-overlay), and the
  // acceptance gate is not editable to accommodate a write it has no reason to expect.
  if (docId && !docEditorActive()) recordRecent(docId);
  docStateChanged();
}

/**
 * Open a page WITH THE EDITOR (V0.53.5): asking for the editor is asking for edit mode.
 *
 * The editor is derived from the one flag (`docEditorActive`), so this enters the mode — for
 * an account that has it, quietly and WITHOUT saving the preference: a `+` pill, a deep link
 * and an "Edit" row in the picker are not the person's own toggle, which is `toggleEditing`
 * and does save — and then opens the page through the one opener. A reader with no editing
 * brick gets the page read-only, exactly as `/edit` links always did.
 *
 * The edit SESSION id (one per sitting, so repeated saves collapse into one log entry) is no
 * longer minted here: the sitting is derived too, so the overlay says when the editor mounts
 * and unmounts (`beginDocEditSession` / `endDocEditSession`) — which is what keeps a session
 * honest when the editor arrives by the switch, by a permission landing, or by a restore.
 */
export function openDocEditor(docId: DocId, nodeId?: NodeId | null): void {
  if (!docId) return;
  if (document.getElementById('docpickmodal')?.classList.contains('show')) closeDocPickModal();
  if (!S.editing && canEnterEdit()) setEditing(true);
  openDoc(docUrlFor(docId), S.docTitles[docId] || S.docCurrentTitle || 'Documentation', docId, nodeId);
}

/**
 * The editor mounted / unmounted (V0.53.5) — the overlay's report, through which the sitting
 * gets its id. `endDocEditSession` is idempotent: the close path nulls the id too.
 */
export function beginDocEditSession(): void {
  S.docEditSessionId = crypto.randomUUID
    ? crypto.randomUUID()
    : 's' + Date.now() + Math.random().toString(16).slice(2);
  announce();
}

export function endDocEditSession(): void {
  if (S.docEditSessionId === null) return;
  S.docEditSessionId = null;
  closeLogDetail();
  announce();
}

/**
 * Leave the editor — which is leaving edit mode (V0.53.5). The router's Back from `/edit` to
 * the plain address; the doc bar's switch is `toggleEditing` itself.
 */
export function leaveDocEditor(): void {
  if (!S.docCurrentId) return;
  if (S.editing) setEditing(false);
}

/**
 * Open a document in the mode you are in — which, since V0.53.5, is what `openDoc` does.
 *
 * The name stays: fourteen call sites and the suite say it, and it still states the rule the
 * V0.44-era bug taught (the map's pills followed the Edit toggle while the band forced view
 * mode, so stepping to a neighbouring node silently dropped you out of editing). With one flag
 * the rule cannot be broken, because there is no second source to read.
 */
export function openDocInCurrentMode(docId: DocId, title: string, nodeId?: NodeId | null): void {
  if (!docId) return;
  openDoc(docUrlFor(docId), title, docId, nodeId);
}

/**
 * Open a page FROM ITS NODE (V0.47): the node crosses into the document view's own drawing of
 * it — the bubble above the page's top-left — and the page arrives around it.
 *
 * Two departures, one arrival. From the map (a pill on the node, a search result), the camera
 * centres the node first and the page does not appear during the travel; then the node flies.
 * From an open popup (a pill inside it), the node is already sitting in the popup's seat, so
 * there is no camera move at all: it leaves the seat for the bubble, and the popup stays open
 * underneath the page — `openDoc()` never closes it, and closing the page will hand the node
 * back to the seat it left.
 *
 * "Open at once and line the map up behind the page" was built before this and rejected once
 * it could be felt: a node that is somewhere else when the page closes is a jump cut, however
 * carefully the map was arranged. The page is asked for; the node is what the eye follows.
 *
 * No flight when there is nothing to fly from (the node is in a folded branch, or panned off
 * the stage) — the page simply opens, as it always did.
 */
export function openDocFromNode(
  nodeId: NodeId,
  doc: { docId: DocId | null; url?: string; title?: string },
  mode: 'current' | 'edit' = 'current',
): void {
  const openIt = (): void => {
    if (mode === 'edit' && doc.docId) openDocEditor(doc.docId, nodeId);
    else if (doc.docId) openDocInCurrentMode(doc.docId, doc.title ?? '', nodeId);
    else if (doc.url) openDoc(doc.url, doc.title, null, nodeId);
  };
  if (!S.nodes[nodeId]) {
    openIt();
    return;
  }
  const alive = beginSequence();
  const overlay = document.getElementById('docmodal');

  const cross = (from: Anchor | null): void => {
    if (!from) {
      openIt();
      restPerch();
      return;
    }
    // Transparent BEFORE React shows it, so the page's first painted frame is the flight's.
    const clear = prefade(overlay, 0);
    openIt();
    awaitElement(
      () => document.querySelector<HTMLElement>('#docnav-current > .docnav-node'),
      { alive },
      (el) => {
        const dest = el ? anchors.bubble(nodeId) : null;
        if (!dest) {
          clear();
          restPerch();
          return;
        }
        fly({
          id: nodeId,
          from,
          to: () => anchors.bubble(nodeId),
          land: 'bubble',
          fades: overlay ? [{ el: overlay, from: 0, to: 1 }] : [],
          onEnd: clear,
        });
      },
    );
  };

  const seat = S.popupFor === nodeId ? anchors.seat(nodeId) : null;
  if (seat) {
    cross(seat);
    return;
  }
  let arrived = false;
  const arrive = (): void => {
    if (arrived) return;
    arrived = true;
    if (!alive()) return;
    cross(anchors.mapNode(nodeId));
  };
  // A pan, never a zoom — see `focusPopup`.
  centerOnNodes([nodeId], { animate: true, zoomIn: false, onEnd: arrive });
  setTimeout(arrive, CAMERA_MS + 300);
}

/**
 * Open the overlay on a node that has nothing to show.
 *
 * Leaving the previously opened document on screen made it look like this node's own
 * documentation, which it never was. Edit mode is KEPT if that is where the user already was:
 * adding documentation to several bare nodes in a row should not drop back to read-only at
 * every step.
 */
export function showDocMissing(n: MapNode): void {
  // Its edit shape — the "create" pills — is DERIVED (`docCreateShape`, V0.53.5): edit mode
  // AND EDIT_MAP, because creation from the band always wires a slot.
  S.docCurrent = '';
  S.docCurrentId = null;
  S.docCurrentTitle = n.label || 'Documentation';
  S.docNavNodeId = n.id;
  S.docOpen = true;
  S.docOpenSeq++;
  docStateChanged();
}

/** Click a node bubble in the band -> open the first documentation it shows, or say it has none. */
export function navOpen(n: MapNode): void {
  const first = firstVisibleDocOf(n);
  if (!first) {
    showDocMissing(n);
    return;
  }
  const label = first.key ? Docs.DOC_KINDS.find((d) => d.key === first.key)!.label : 'Untitled';
  const title = S.docTitles[first.docId] || label;
  openDocInCurrentMode(first.docId, (n.label ? n.label + ' — ' : '') + title, n.id);
}

export function navOpenNode(id: NodeId): void {
  const n = S.nodes[id];
  if (n) navOpen(n);
}

/**
 * Leave the documentation overlay.
 *
 * `frame` (V0.46) is the reader's own close — the button and Escape — and it puts the map back
 * on the node that holds the page, so stepping out of a document lands you where that document
 * belongs rather than wherever you happened to be looking before you opened it. Every OTHER
 * caller leaves it off deliberately: `showDocNodeOnMap` closes and then centres itself,
 * `forgetDoc` is a deletion, and the router's closes are one half of a navigation that is about
 * to move the camera for its own reasons.
 *
 * It CENTRES; it does not reveal. `centerOnNodes` declines silently when the node sits inside a
 * collapsed branch, and that is the right answer here — closing a document is not permission to
 * spring open somebody's folded tree and then persist it.
 */
export function closeDoc(opts: { frame?: boolean } = {}): void {
  if (!opts.frame) {
    beginSequence();
    finishCloseDoc(null);
    restPerch();
    return;
  }
  leaveDoc();
}

/** The page being left, so a second Close or Escape during its fade is not a second leave. */
let leaving: NodeId | null = null;

/**
 * The reader's own close (V0.47): the node leaves the page's bubble the way it arrived.
 *
 * Three ways back, decided by where the node came from. If its popup is still open underneath
 * — it always is when the page was opened from one — the node returns to the seat it left and
 * the popup is revealed as the page fades, with NO camera move: the map is behind the popup
 * and nobody is looking at it. If it came from the map and the node is still on the stage, it
 * flies back to it — the camera is where the open left it, centred. Otherwise (a deep link,
 * a node meanwhile folded away) there is nothing to fly to, and the camera re-centres the node
 * as it did before any of this existed.
 *
 * The page's STATE stays open for the 240 ms of the flight, so the URL and presence lag by
 * that much; closing the state first is not an option because it unmounts the bubble the node
 * is departing from. Accepted, and guarded: the state change runs whatever becomes of the
 * animation, because a Close that leaves the page open is the one failure worse than a jump.
 */
function leaveDoc(): void {
  const owner = S.docNavNodeId;
  if (owner && leaving === owner) return;
  const bubble = owner ? anchors.bubble(owner) : null;
  const alive = beginSequence();
  let done = false;
  const finish = (centre: NodeId | null): void => {
    if (done) return;
    done = true;
    leaving = null;
    finishCloseDoc(alive() ? centre : null);
  };
  if (!owner || !bubble) {
    finish(owner);
    return;
  }
  leaving = owner;
  const overlay = document.getElementById('docmodal');
  const fades = overlay ? [{ el: overlay, from: 1, to: 0 }] : [];
  if (S.popupFor === owner && anchors.seat(owner)) {
    fly({ id: owner, from: bubble, to: () => anchors.seat(owner), land: 'seat', fades, onEnd: () => finish(null) });
    return;
  }
  if (anchors.mapNode(owner)) {
    fly({ id: owner, from: bubble, to: () => anchors.mapNode(owner), land: null, fades, onEnd: () => finish(null) });
    return;
  }
  finish(owner);
}

/**
 * Leave the documentation overlay — the state half.
 *
 * `centre` is the node to re-centre the camera on afterwards, or null: the flights above land
 * the node themselves and want no camera move; the fallback and `showDocNodeOnMap`-style closes
 * pass the owner, which is the behaviour every close had before V0.47.
 *
 * It CENTRES; it does not reveal. `centerOnNodes` declines silently when the node sits inside a
 * collapsed branch, and that is the right answer here — closing a document is not permission to
 * spring open somebody's folded tree and then persist it.
 */
function finishCloseDoc(centre: NodeId | null): void {
  const owner = centre;
  // The other half of "recently viewed": a doc opened straight into its EDITOR was never
  // recorded by openDoc(), so the sitting records as it ends. (Leaving the editor by leaving
  // edit mode records in `setEditing`, which sees the editor active at that moment; by the
  // time a close follows, it is not — no double bump.)
  if (S.docCurrentId && docEditorActive()) recordRecent(S.docCurrentId);
  S.docOpen = false;
  S.docCurrent = '';
  S.docCurrentId = null;
  S.docCurrentTitle = '';
  S.docNavNodeId = null;
  S.docEditSessionId = null;
  // Created from a `+` pill and left empty? Take it back out (V0.43.2). Fire-and-forget: the
  // close must not wait on the network, and the server refuses anything that is not a
  // genuinely untouched draft.
  void discardProvisionalDoc();
  renderDocLogs();
  closeLogDetail();
  docStateChanged();
  if (owner && S.nodes[owner]) {
    centerOnNodes([owner], {
      animate: true,
      onEnd: (completed) => {
        if (completed) saveViewState();
      },
    });
  }
}

/**
 * The title the bar shows.
 *
 * Set by whoever opened the page; the editor re-states it once the page it loaded says what it
 * is really called, and a save states the new one.
 */
export function setDocCurrentTitle(t: string): void {
  S.docCurrentTitle = t || '';
  docStateChanged();
}

export function noteDocTitle(docId: DocId, title: string): void {
  S.docTitles[docId] = title;
  if (S.docCurrentId === docId) S.docCurrentTitle = title;
  docStateChanged();
}

/* ================================================================= the doc-page picker === */

export const openDocPicker = (nodeId: NodeId, key: string): void => handles.docPicker?.open(nodeId, key);
export const closeDocPickModal = (): void => handles.docPicker?.close();

/**
 * Attach a page to one of a node's four fixed slots, or append it to the unbounded custom list.
 *
 * `link*` is derived from the id and never persisted — `docUrlFor()` is the one place a
 * documentation URL is built.
 */
export function attachDocToTarget(
  nodeId: NodeId,
  key: string,
  docId: DocId,
  fallbackTitle?: string,
): void {
  const n = S.nodes[nodeId];
  if (!n) return;
  if (key === 'custom') {
    n.customDocs = n.customDocs || [];
    if (!n.customDocs.includes(docId)) n.customDocs.push(docId);
    if (!S.docTitles[docId]) S.docTitles[docId] = (fallbackTitle || '').trim() || docId;
  } else {
    const field = Docs.DOC_ID_FIELD_BY_KEY[key as keyof typeof Docs.DOC_ID_FIELD_BY_KEY];
    (n as unknown as Record<string, unknown>)[field] = docId;
    (n as unknown as Record<string, unknown>)[key] = docUrlFor(docId);
  }
  save();
  render();
  // NO RE-OPEN (V0.42.6). This used to end `if (S.popupFor === nodeId) openPopup(nodeId)`,
  // which bumps the region's `seq`, changes `PopupBody`'s key and REMOUNTS it — discarding a
  // half-typed name, the description, the colour, the tags, the responsibles picker, the AI
  // draft and the documentation plan, all at once. In the vanilla app that line was how the
  // popup learned anything, because it rebuilt the markup; here `render()` already publishes a
  // new snapshot and `PopupDocs` reads the live node, so the pill updates without it.
  // What it also did and no longer does: re-derive `OpenState.ai` through `aiModeAtOpen`, so a
  // viewer who is a doc-responsible of the page they just attached, holds EDIT_MAP, and is not
  // the AI operator no longer gains the prompt panel until the next open. `ro` could not
  // change here at all. That is the whole cost, and it is smaller than eating the form.
}

/**
 * Create a document ON a node through the scoped route and wire it there — the click behind a
 * dotted PLANNED pill (V0.42), and the one place a NODE RESPONSIBLE (not just a map editor)
 * creates the documents their branch owes.
 *
 * The server does the creation AND the slot wiring in one transaction and BUMPS the version,
 * so the client adopts the result and REBASES rather than pushing a structural delta (which
 * would re-send a slot the server already holds and 409 on the version this route just moved).
 * Then straight into the editor — a written page has nothing to read yet.
 */
/**
 * The dotted `+ Documentation` pill's click: RECORD THE PROMISE, THEN KEEP IT (V0.43.2).
 *
 * The node panel's rows are drawn from the plan DRAFT, so a pill can exist for a kind the
 * server has not been told about yet — you tick Course and the row appears before any save.
 * Acting on that row is the moment the two views have to agree, so the draft is written first
 * and the page created second.
 *
 * A PRODUCT CHOICE, NOT A SERVER REQUIREMENT, and the distinction matters to the next reader:
 * `POST /api/nodes/:id/docs` never looks at `expectedDocs`. The two existing flush-then-act
 * actions (`runLiveVerifyFromPopup`, `generateDocFromPopup`) do it because the server resolves
 * their target from STORED state and would silently ignore an unsaved one. That reason does
 * not transfer; this one is about the plan and the page not disagreeing.
 *
 * ORDER IS LOAD-BEARING, three times over:
 *  - the permission check comes FIRST, hoisted out of `createDocForNode`, so a refused create
 *    cannot leave a plan write behind it;
 *  - a refused plan write stops the create, because a page must not be created against a
 *    promise the server declined to record;
 *  - the commit is AWAITED. `setDocPlan` adopts a bumped `dataVersion` through
 *    `noteGranularVersion`, and `createDocForNode` rebases on its own; firing both at once
 *    risks the trailing structural sync carrying a superseded version and 409ing.
 *
 * `plan` absent means the plan editor was not drawn for this viewer, so there is no draft and
 * nothing to record — not that the plan is null.
 */
export async function createPlannedDocForNode(
  nodeId: NodeId,
  kind: DocPerm,
  plan?: { plan: DocPlan | null },
): Promise<void> {
  if (!S.nodes[nodeId]) return;
  if (!canCreateDocOn(nodeId)) {
    alert('You cannot create documentation here — a branch responsible or a map editor can add it.');
    return;
  }
  if (plan && !(await commitDocPlan(nodeId, plan.plan))) return;
  await createDocForNode(nodeId, kind);
}

export async function createDocForNode(nodeId: NodeId, kind: DocPerm): Promise<void> {
  const n = S.nodes[nodeId];
  if (!n) return;
  if (!canCreateDocOn(nodeId)) {
    alert('You cannot create documentation here — a branch responsible or a map editor can add it.');
    return;
  }
  const kindLabel = kind === 'custom' ? 'Custom' : (Docs.DOC_KINDS.find((k) => k.perm === kind)?.label ?? 'Document');
  const title = (n.label ? `${n.label} — ` : '') + kindLabel;
  try {
    const res = await apiRequest<{ doc: { id: DocId }; slot: string | null; dataVersion?: number }>(
      `/api/nodes/${encodeURIComponent(nodeId)}/docs`,
      { method: 'POST', body: { kind, title, locale: S.currentLocale } },
    );
    const docId = res.doc.id;
    S.docTitles[docId] = title;
    noteDocMeta(docId, 'standard', null);
    if (kind === 'custom') {
      n.customDocs = n.customDocs || [];
      if (!n.customDocs.includes(docId)) n.customDocs.push(docId);
    } else {
      const kk = Docs.DOC_KINDS.find((k) => k.perm === kind)!;
      (n as unknown as Record<string, unknown>)[Docs.DOC_ID_FIELD_BY_KEY[kk.key]] = docId;
      (n as unknown as Record<string, unknown>)[kk.key] = docUrlFor(docId);
    }
    // Adopt the server's version WITH the state it describes, so nothing is owed on the wire.
    if (typeof res.dataVersion === 'number') Writes.rebaseStructural(structuralSnapshot(), res.dataVersion);
    // PROVISIONAL until somebody writes in it (V0.43.2). Closing the editor without doing so
    // takes the page back out, so a mis-clicked pill leaves the node owing the document again
    // rather than owning an empty one that reads as satisfied.
    S.provisionalDoc = { docId, nodeId };
    render();
    openDocEditor(docId, nodeId);
  } catch {
    alert('Could not create the document.');
  }
}

export function detachDocFromTarget(nodeId: NodeId, key: string): void {
  const n = S.nodes[nodeId];
  if (!n) return;
  const field = Docs.DOC_ID_FIELD_BY_KEY[key as keyof typeof Docs.DOC_ID_FIELD_BY_KEY];
  (n as unknown as Record<string, unknown>)[field] = null;
  (n as unknown as Record<string, unknown>)[key] = '';
  save();
  render();
  // NO RE-OPEN (V0.42.6). This used to end `if (S.popupFor === nodeId) openPopup(nodeId)`,
  // which bumps the region's `seq`, changes `PopupBody`'s key and REMOUNTS it — discarding a
  // half-typed name, the description, the colour, the tags, the responsibles picker, the AI
  // draft and the documentation plan, all at once. In the vanilla app that line was how the
  // popup learned anything, because it rebuilt the markup; here `render()` already publishes a
  // new snapshot and `PopupDocs` reads the live node, so the pill updates without it.
  // What it also did and no longer does: re-derive `OpenState.ai` through `aiModeAtOpen`, so a
  // viewer who is a doc-responsible of the page they just attached, holds EDIT_MAP, and is not
  // the AI operator no longer gains the prompt panel until the next open. `ro` could not
  // change here at all. That is the whole cost, and it is smaller than eating the form.
}

/**
 * A node's `link*` slot caches the URL `docUrlFor()` produced, so repointing a document has to
 * refresh every slot holding it — otherwise the old target keeps opening until a reload.
 */
function resyncDocUrl(docId: DocId): void {
  for (const nid in S.nodes) {
    const n = S.nodes[nid] as unknown as Record<string, unknown>;
    for (const k of Docs.DOC_KINDS) {
      if (n[Docs.DOC_ID_FIELD_BY_KEY[k.key]] === docId) n[k.key] = docUrlFor(docId);
    }
  }
}

/** The two things a save changes OUTSIDE the document itself. Both change the MAP. */
export function afterDocSaved(docId: DocId, title: string): void {
  keepProvisionalDoc(docId); // a save is the plainest statement that this page is wanted
  resyncDocUrl(docId); // a node's slot caches a URL a repointed page no longer has
  S.docTitles[docId] = title;
  if (S.docCurrentId === docId) S.docCurrentTitle = title;
  render(); // pills pick up a changed type or icon without waiting for a reload
  renderDocLogs(); // the save just added, or folded into, an entry
  // The bell (plan Phase 3): a save is the one moment this tab KNOWS the feed moved, so it
  // is one of the three refresh points (sign-in, window focus, save) — never a timer.
  void refreshFeedBadge();
}

/** The page is gone server-side, unlinked from every node. Mirror that locally, then close. */
/**
 * How many nodes point at this page (V0.43.2).
 *
 * ONE PAGE, MANY NODES is the whole premise of the picker, so deleting one is never local —
 * it takes the page from every node holding it, and the number belongs in the question. Read
 * from the map the client already has, which is the same place `forgetDoc()` clears it from
 * afterwards: no second source, and no request to ask.
 */
/** Somebody wrote in it, named it, staffed it or moved it: it is a real document now. */
export function keepProvisionalDoc(id: DocId): void {
  if (S.provisionalDoc?.docId === id) S.provisionalDoc = null;
}

/**
 * Closing a document created from a `+` pill and never written in takes it back out (V0.43.2).
 *
 * The SERVER decides whether it is really untouched — `DELETE /api/nodes/:id/docs/:docId`
 * refuses anything with content, revisions, responsibles, a moved workflow, a second node
 * holding it, or an hour on the clock. So a bug here cannot destroy work: the worst it can do
 * is ask and be told no. A refusal is deliberately silent — the page is simply real by then,
 * and the ordinary delete is how a real one goes.
 */
export async function discardProvisionalDoc(): Promise<void> {
  const pending = S.provisionalDoc;
  if (!pending) return;
  S.provisionalDoc = null;
  try {
    const res = await apiRequest<{ dataVersion?: number }>(
      `/api/nodes/${encodeURIComponent(pending.nodeId)}/docs/${encodeURIComponent(pending.docId)}`,
      { method: 'DELETE' },
    );
    forgetDoc(pending.docId);
    if (typeof res?.dataVersion === 'number') {
      Writes.rebaseStructural(structuralSnapshot(), res.dataVersion);
    }
  } catch {
    // Kept: it earned its place, or the network did not answer. Either way, say nothing.
  }
}

export function nodesUsingDoc(id: DocId): number {
  let n = 0;
  for (const nid in S.nodes) {
    const node = S.nodes[nid] as unknown as Record<string, unknown>;
    const inSlot = Docs.DOC_KINDS.some((k) => node[Docs.DOC_ID_FIELD_BY_KEY[k.key]] === id);
    const inCustom = Array.isArray(node.customDocs) && node.customDocs.includes(id);
    if (inSlot || inCustom) n++;
  }
  return n;
}

/**
 * Delete a page and forget it everywhere — the second half of the node panel's Remove, and the
 * picker row's trash (V0.43.2).
 *
 * The server unlinks the page from every node; `forgetDoc()` mirrors that locally. Both call
 * sites used to hold their own copy of this pair, which is one copy too many for an act that
 * cannot be undone.
 */
export async function deleteDocEverywhere(id: DocId): Promise<boolean> {
  try {
    await apiRequest(`/api/docs/${encodeURIComponent(id)}`, { method: 'DELETE' });
    forgetDoc(id);
    return true;
  } catch {
    alert('Could not delete the document.');
    return false;
  }
}

export function forgetDoc(id: DocId): void {
  for (const nid in S.nodes) {
    const n = S.nodes[nid] as unknown as Record<string, unknown>;
    for (const k of Docs.DOC_KINDS) {
      if (n[Docs.DOC_ID_FIELD_BY_KEY[k.key]] === id) {
        n[Docs.DOC_ID_FIELD_BY_KEY[k.key]] = null;
        n[k.key] = '';
      }
    }
    const custom = n.customDocs;
    if (Array.isArray(custom) && custom.includes(id)) {
      n.customDocs = custom.filter((d) => d !== id);
    }
  }
  delete S.docTitles[id];
  delete S.docAiGenerated[id];
  delete S.docUrls[id];
  delete S.docTypes[id];
  closeDoc();
  render();
}

/* ==================================================================== tags: the tree === */

export const tagChildren = (pid: TagId | null): TagId[] => TagsLib.tagChildren(S.tags, S.tagEdges, pid);
export const tagDescendants = (tid: TagId): TagId[] => TagsLib.tagDescendants(S.tagEdges, tid);
export const isTagAncestor = (a: TagId, t: TagId): boolean => TagsLib.isTagAncestor(S.tagEdges, a, t);
export const tagCount = (tid: TagId): number => TagsLib.tagCount(tid, S.nodes, permHidden);

/**
 * Move `dragId` relative to `targetId`.
 *
 * `position` is 'inside' (a child of the target) or 'before'/'after' (a sibling). A null target
 * drops at the root level. Cycles are refused, and the affected sibling group is reindexed.
 */
export function moveTag(dragId: TagId, targetId: TagId | null, position: string): void {
  if (!dragId || dragId === targetId) return;
  const parent =
    position === 'inside'
      ? targetId
      : targetId == null
        ? null
        : targetId in S.tagEdges
          ? S.tagEdges[targetId]
          : null;
  if (parent != null && (parent === dragId || isTagAncestor(dragId, parent))) return; // a cycle
  if (parent == null) delete S.tagEdges[dragId];
  else S.tagEdges[dragId] = parent;

  const sibs = tagChildren(parent).filter((t) => t !== dragId);
  let at: number;
  if (position === 'inside' || targetId == null) {
    at = sibs.length;
  } else {
    const idx = sibs.indexOf(targetId);
    at = idx < 0 ? sibs.length : position === 'after' ? idx + 1 : idx;
  }
  sibs.splice(at, 0, dragId);
  sibs.forEach((t, i) => {
    S.tags[t].order = i;
  });
  if (parent != null && S.tags[parent]) S.tags[parent].collapsed = false; // reveal the new location
  applyReveal();
  renderTags();
  render();
  save();
}

export async function addTag(name: string, parent: TagId | null = null): Promise<void> {
  const order = Object.keys(S.tags).length;
  const color = TAG_COLORS[order % TAG_COLORS.length];
  let id: TagId;
  try {
    id = await createTagOnServer({ name: name || 'Tag', color, sortOrder: order, parentTagId: parent });
  } catch (e) {
    console.error(e);
    alert('Could not create the new tag on the server.');
    return;
  }
  S.tid++;
  S.tags[id] = { id, name: name || 'Tag', color, collapsed: false, order } as (typeof S.tags)[string];
  if (parent !== null) S.tagEdges[id] = parent;
  renderTags();
  save();
  pushUndo('create tag "' + (name || 'Tag') + '"', async () => {
    delete S.tags[id];
    delete S.tagEdges[id];
    for (const nid in S.nodes) {
      if (S.nodes[nid].tags) S.nodes[nid].tags = S.nodes[nid].tags!.filter((x) => x !== id);
    }
    renderTags();
    render();
    try {
      await deleteTagOnServer(id);
    } catch {
      /* already gone */
    }
    save();
  });
}

/** Set a tag's name. React owns the field and calls in with the finished value. */
export function setTagName(tid: TagId, name: string): void {
  const t = S.tags[tid];
  if (!t) return;
  const prevName = t.name;
  t.name = String(name || '').trim() || 'Tag';
  Writes.dirtyTagTranslations.add(tid);
  renderTags();
  save();
  if (prevName !== t.name) {
    pushUndo('rename tag "' + prevName + '"', async () => {
      const cur = S.tags[tid];
      if (!cur) return;
      cur.name = prevName;
      Writes.dirtyTagTranslations.add(tid);
      renderTags();
      save();
    });
  }
}

export async function deleteTag(tid: TagId): Promise<void> {
  const toRemove = [tid, ...tagDescendants(tid)]; // parent-before-child order
  // Snapshot every removed tag and which nodes had it directly assigned, so undo can recreate
  // the whole branch (with new ids) with its structure and assignments intact.
  const snapshots = toRemove.map((t) => ({
    id: t,
    name: S.tags[t].name,
    color: S.tags[t].color,
    collapsed: S.tags[t].collapsed,
    order: S.tags[t].order,
    categoryId: S.tags[t].categoryId || null,
    invisible: !!S.tags[t].invisible,
    parentTagId: S.tagEdges[t] !== undefined ? S.tagEdges[t] : null,
  }));
  const nodeAssignments: { nodeId: NodeId; tagId: TagId }[] = [];
  for (const nodeId in S.nodes) {
    for (const tg of S.nodes[nodeId].tags || []) {
      if (toRemove.includes(tg)) nodeAssignments.push({ nodeId, tagId: tg });
    }
  }

  for (const t of toRemove) {
    delete S.tags[t];
    delete S.tagEdges[t];
  }
  for (const id in S.nodes) {
    if (S.nodes[id].tags) S.nodes[id].tags = S.nodes[id].tags!.filter((x) => !toRemove.includes(x));
  }
  if ([...S.activeTags].some((t) => toRemove.includes(t))) {
    S.activeTags = new Set([...S.activeTags].filter((t) => !toRemove.includes(t)));
  }
  applyReveal();
  renderTags();
  render();
  try {
    // Leaves first — the server refuses to delete a tag that still has children.
    for (const t of [...toRemove].reverse()) await deleteTagOnServer(t);
  } catch (e) {
    console.error('Failed to delete tag(s) on server:', e);
  }
  save();

  const undoLabel =
    'delete tag "' +
    snapshots[0].name +
    '"' +
    (snapshots.length > 1
      ? ' (+' + (snapshots.length - 1) + ' sub-tag' + (snapshots.length > 2 ? 's' : '') + ')'
      : '');
  pushUndo(undoLabel, async () => {
    const idMap: Record<TagId, TagId> = {};
    try {
      for (const s of snapshots) {
        // Parent created before child, so the parent's new id is already in idMap.
        const newParentId = s.parentTagId != null ? (idMap[s.parentTagId] ?? s.parentTagId) : null;
        idMap[s.id] = await createTagOnServer({
          name: s.name,
          color: s.color,
          sortOrder: s.order,
          parentTagId: newParentId,
          categoryId: s.categoryId,
          invisible: s.invisible,
        });
      }
    } catch {
      alert('Could not fully restore these tags.');
      return;
    }
    for (const s of snapshots) {
      const newId = idMap[s.id];
      S.tags[newId] = {
        id: newId,
        name: s.name,
        color: s.color,
        collapsed: s.collapsed,
        order: s.order,
        categoryId: s.categoryId,
        invisible: s.invisible,
      } as (typeof S.tags)[string];
      const newParentId = s.parentTagId != null ? idMap[s.parentTagId] : null;
      if (newParentId != null) S.tagEdges[newId] = newParentId;
    }
    for (const a of nodeAssignments) {
      const node = S.nodes[a.nodeId];
      const newTagId = idMap[a.tagId];
      if (node && newTagId) {
        node.tags = node.tags || [];
        if (!node.tags.includes(newTagId)) node.tags.push(newTagId);
      }
    }
    renderTags();
    render();
    try {
      await pushStructuralSync();
    } catch {
      /* the debounced sync will carry it */
    }
    save();
  });
}

/* =========================================================================== search === */

function filterState(): Search.FilterState {
  return {
    nodes: S.nodes,
    edges: S.edges,
    tags: S.tags,
    tagEdges: S.tagEdges,
    searchQuery: S.searchQuery,
    searchScope: S.searchScope,
    searchDocKind: S.searchDocKind,
    contentHits: S.searchContentHits,
    activeTags: S.activeTags,
    activeDocStatus: (S.activeDocStatus || null) as Search.FilterState['activeDocStatus'],
    isPermHidden: permHidden,
    editing: S.editing,
    // The field keeps its historical name (the frozen core-parity oracle builds this shape),
    // but what it MEANS is "may this user see invisible tags", and since V0.37 that is the
    // tag-structure brick: invisible tags are structure, and they are for whoever edits it.
    canEdit: canEditTags(),
    chatHighlight: S.chatHighlight,
    assignHighlight: S.assignHighlight,
  };
}

export const nodeMatchesSearch = (n: MapNode): boolean => Search.nodeMatchesSearch(n, filterState());
export const computeForceOpen = (): Set<NodeId> => Search.computeForceOpen(filterState());

/**
 * Recompute the reveal set and re-lay the tree.
 *
 * Force-opened branches have to expand IN PLACE rather than be left at stale positions, and the
 * tree has to re-pack when nothing is active. Shared by the search and the tag selection.
 */
export function applyReveal(): void {
  S.forceOpen = computeForceOpen();
  // A search opens branches to show what matched, which moves siblings exactly as a collapse
  // does. It is one person looking for something; nobody else's map may move for it.
  autoLayout('view');
}

/* --- the camera's answer to a filter (V0.46) ------------------------------------------- */

/**
 * Is a filter narrowing the map right now?
 *
 * Search and tags read as ONE state because the bookmark below is one bookmark. Clearing the
 * search while a tag is still selected must not haul the reader back to before either was
 * applied — the tag's matches are still the answer on screen.
 *
 * The pipeline-stage filter is deliberately not part of it. See `filterMatchIds`.
 */
const filterActive = (): boolean => S.searchQuery !== '' || S.activeTags.size > 0;

/**
 * Where the camera was before the current filter began — where clearing it returns to.
 *
 * Module-local, and deliberately not on `S`: nothing renders from it, so putting it in the
 * state would mean a field in the snapshot that no read model reads.
 */
let filterReturnCamera: { x: number; y: number; k: number } | null = null;
let filterFrameTimer: ReturnType<typeof setTimeout> | null = null;
/** Has the camera already answered the filter as it currently stands? */
let filterFramed = false;

/**
 * How long the filter has to stop changing before the camera answers it.
 *
 * Comfortably longer than the doc-content fetch's own 300 ms, because this is not a
 * throttle — it is the pause that tells the difference between somebody still typing and
 * somebody who has finished. Shorter, and the camera starts hunting between the letters.
 */
const FILTER_FRAME_MS = 700;

function cancelFilterFrame(): void {
  if (filterFrameTimer) clearTimeout(filterFrameTimer);
  filterFrameTimer = null;
}

/**
 * Frame what the filter matched.
 *
 * DEBOUNCED, AND NOT FOR PERFORMANCE. `applySearch()` runs on every keystroke, and the
 * documentation-content hits land 300 ms later through `setSearchContentHits` — a different
 * function that deliberately never calls back into it. An eager camera would therefore slam to
 * the zoom floor on the first letter (`a` matches most of the map), re-target on every
 * character after it, and then lurch a second time when the content answer arrived. Waiting
 * for the typing and the fetch to stop is what makes it one move, and re-arming from the
 * content answer is what keeps that true when the answer is the slower of the two.
 *
 * NOTHING HERE SAVES. This camera is the answer to a question, not a place the reader chose to
 * be — `saveViewState()` records `filterReturnCamera` while a filter is active, so a reload
 * puts them back where they actually were and the restored filter re-frames from there.
 */
function scheduleFilterFrame(): void {
  // Boot RESTORES filters; it does not answer them. `enterApp()` replays a stored query
  // through `applySearch()` directly, and the router replays one from the URL — neither is a
  // gesture, and both run while the deep-link camera is still being decided.
  if (!S.viewStateReady) return;
  filterFramed = false;
  cancelFilterFrame();
  filterFrameTimer = setTimeout(() => {
    filterFrameTimer = null;
    filterFramed = true;
    if (!filterActive()) return;
    const ids = Search.filterMatchIds(filterState());
    // Matches are scattered across both sides of the map, so this one zooms: panning alone
    // cannot show them.
    if (ids.length) centerOnNodes(ids, { animate: true });
  }, FILTER_FRAME_MS);
}

/**
 * The documentation-content answer landed. Fold it into the frame that is still owed.
 *
 * ONLY WHILE ONE IS OWED. Once the camera has answered this query it stays answered: a
 * late arrival re-framing would be the second move for one question, which is the exact
 * jumping-about this debounce exists to prevent. The content hits still light up and are
 * still revealed — they simply do not get their own journey.
 */
function nudgeFilterFrame(): void {
  if (filterFramed || !filterFrameTimer) return;
  scheduleFilterFrame();
}

/**
 * The camera's half of a filter change the USER made.
 *
 * `wasActive` has to be read by the caller BEFORE it touches the state — the edge from "no
 * filter" to "some filter" is the only moment the return camera can still be captured.
 */
function frameForFilter(wasActive: boolean): void {
  if (filterActive()) {
    if (!wasActive) filterReturnCamera = mapGetCamera();
    scheduleFilterFrame();
    return;
  }
  cancelFilterFrame();
  if (wasActive && filterReturnCamera) {
    const back = filterReturnCamera;
    filterReturnCamera = null;
    mapSetCamera(back, { animate: true });
  }
}

export function applySearch(): void {
  S.searchQuery = S.searchText.trim().toLowerCase();
  // The old content answer is for the old query — clear it SYNCHRONOUSLY so no stale node
  // stays lit while the debounced fetch is in flight. Identity preserved when already empty,
  // so a keystroke with no hits does not re-render every node. setSearchContentHits() must
  // never call back into here: that would be the fetch re-clearing what it just set — a loop.
  if (S.searchContentHits.size) S.searchContentHits = new Set();
  applyReveal();
  render();
  saveViewState(); // localStorage only — never the server sync, this fires per keystroke
  Router.syncUrl('filter'); // debounced replaceState — a keystroke is never a history entry
}

/**
 * Type into the search box.
 *
 * `frame` (V0.46) is "a person just did this", and it is opt-IN for a reason: the other
 * caller is the router replaying a query out of a URL, at boot, while the deep-link camera is
 * still being decided. A default of true would make that the quiet wrong answer.
 */
export function setSearchText(t: string | null, opts: { frame?: boolean } = {}): void {
  const wasActive = filterActive();
  S.searchText = t == null ? '' : String(t);
  applySearch();
  if (opts.frame) frameForFilter(wasActive);
}

/**
 * Correct the search narrowing for what this user may see.
 *
 * A user who may not see a documentation kind must not keep a filter that searches by it,
 * and that is a change to STATE, not to a control — which is why it cannot live in the React
 * panel that draws the radios. Tolerates `perm === null` at boot: `canDocBtn` answers true
 * with no payload, so nothing is cleared before the session is known.
 */
export function syncScopeUI(): void {
  if (S.searchDocKind && !canDocBtn(S.searchDocKind)) S.searchDocKind = '';
  announce();
}

/** Which fields the search looks in. A durable per-user preference, never map content. */
export function setSearchScope(next: SearchScope): void {
  S.searchScope = {
    name: !!next.name,
    desc: !!next.desc,
    tags: !!next.tags,
    content: !!next.content,
  };
  syncScopeUI();
  saveLocal();
  applySearch();
}

/**
 * Narrow the search to one documentation kind, or `''` to stop (plan 2.2).
 *
 * Clears the content hits eagerly — they were fetched for the OLD kind, and the debounced
 * fetch (keyed on this field) refills them for the new one. Deliberately no `save()`:
 * the kind is transient and never persisted.
 */
export function setSearchDocKind(kind: SearchDocKind | ''): void {
  const next = kind && (SEARCH_DOC_KINDS as readonly string[]).includes(kind) ? kind : '';
  if (next === S.searchDocKind) return;
  S.searchDocKind = next;
  syncScopeUI(); // a kind this user may not see is refused, not stored
  if (S.searchContentHits.size) S.searchContentHits = new Set();
  applyReveal();
  render();
  Router.syncUrl('filter');
}

/**
 * The doc-content search answered: these owner nodes' documents contain `forQuery`.
 *
 * STALENESS GUARD: the response rode a 300 ms debounce plus a network round trip, and the
 * query may have moved on — an answer to a question nobody is asking any more is dropped,
 * not applied. Compared by content so the frequent "same answer again" is a no-op, and
 * NEVER calls applySearch() (which clears this set — the loop would eat every answer).
 */
export function setSearchContentHits(ids: readonly NodeId[], forQuery: string): void {
  if (forQuery !== S.searchQuery) return;
  const next = new Set((ids ?? []).filter((id) => S.nodes[id]));
  if (
    next.size === S.searchContentHits.size &&
    [...next].every((id) => S.searchContentHits.has(id))
  ) {
    return;
  }
  S.searchContentHits = next;
  applyReveal();
  render();
  // The match set just grew by everything the content search found — fold it into the
  // frame still owed, rather than starting a second one.
  if (filterActive()) nudgeFilterFrame();
}

/**
 * Toggle one tag in or out of the filter selection (plan 2.3's multi-select).
 *
 * The Set is REPLACED, never mutated: the snapshot adopts it by reference, so identity is
 * what carries "this changed" — the same contract `forceOpen` and `chatHighlight` follow.
 */
export function toggleActiveTag(tid: TagId): void {
  const wasActive = filterActive();
  const next = new Set(S.activeTags);
  if (next.has(tid)) next.delete(tid);
  else next.add(tid);
  S.activeTags = next;
  applyReveal();
  renderTags();
  render();
  saveViewState();
  Router.syncUrl('filter');
  // Its one caller is the tag row, so this is always a gesture — no opt-in needed.
  frameForFilter(wasActive);
}

/**
 * Replace the whole tag selection. `[]` clears it — the clear button's meaning.
 *
 * `frame` is opt-in for the same reason it is on `setSearchText`: the router calls this with
 * whatever the URL carried, which is not somebody pressing a tag.
 */
export function setActiveTags(tids: readonly TagId[], opts: { frame?: boolean } = {}): void {
  const wasActive = filterActive();
  S.activeTags = new Set((tids ?? []).filter((t) => S.tags[t]));
  applyReveal();
  renderTags();
  render();
  saveViewState();
  Router.syncUrl('filter');
  if (opts.frame) frameForFilter(wasActive);
}

export function setActiveDocStatus(v: string): void {
  S.activeDocStatus = v;
  syncAiDocFilterDot();
  applyReveal();
  render();
  saveViewState();
  Router.syncUrl('filter');
}

/**
 * The highlight the chatbot's citations produce.
 *
 * Map state, not chat state: it feeds `computeForceOpen()`, which reveals cited nodes inside
 * collapsed branches, and it re-centres the camera on them.
 */
export function setChatHighlight(ids: NodeId[]): void {
  const next = new Set((ids || []).filter((id) => S.nodes[id]));
  if (!next.size && !S.chatHighlight.size) return; // nothing lit, nothing to clear
  S.chatHighlight = next;
  applyReveal();
  render();
  if (S.chatHighlight.size) {
    requestAnimationFrame(() => centerOnNodes([...S.chatHighlight]));
  }
}

/* ------------------------------------------------ revealing a node on the map (V0.42) === */

/**
 * Expand a node's COLLAPSED ANCESTORS so it is actually on screen — a permanent reveal, unlike
 * the transient `forceOpen` a filter uses.
 *
 * Lifted out of `router.ts`, where it was private and its callers copy-pasted the same
 * reveal → relayout → render → open → centre sequence twice. One implementation now.
 */
export function revealAncestors(id: NodeId): void {
  let cur = S.edges[id];
  let guard = 0;
  while (cur !== undefined && guard++ < 10_000) {
    const n = S.nodes[cur];
    if (n && !n.root && n.collapsed) n.collapsed = false;
    cur = S.edges[cur];
  }
}

/**
 * Put the map on a node: reveal it, lay out, open its popup and centre the camera.
 *
 * THIS FIXES A LIVE BUG for every caller that reached for `centerOnNodes` alone. `runCenter`
 * filters HIDDEN nodes and returns silently, so centring on a node inside a collapsed branch
 * did nothing at all — which is exactly what clicking a "My assignments" row did whenever the
 * assignment sat under a folded parent. Revealing first is what makes the camera land.
 *
 * The relayout is `'view'` intent: a reveal opens branches for THIS reader and is nobody
 * else's business, so the coordinates it produces are never synced.
 */
/**
 * "Show in map" from the doc bar (V0.44.1): leave the document — and the dashboard, when the
 * document was opened over it — and land on the node. One action so the three closes happen in
 * an order that cannot be got wrong per call site: the doc first (its close already lands the URL
 * on whatever is beneath), then the dashboard, then the reveal, which centres and opens the popup.
 */
export function showDocNodeOnMap(id: NodeId): boolean {
  if (!S.nodes[id]) return false;
  if (S.docOpen) closeDoc();
  if (isDashboardScreen(S.adminScreen)) closeDashboard();
  return revealNodeOnMap(id);
}

export function revealNodeOnMap(id: NodeId, opts: { popup?: boolean } = {}): boolean {
  if (!S.nodes[id] || permHidden(id)) return false;
  revealAncestors(id);
  autoLayout('view');
  render();
  // With a popup, the reader's open (V0.47): the camera centres and the node takes its seat.
  // The layout op is already queued, so by the time the camera lands the node is placed and
  // measured — which is exactly when the flight measures it.
  if (opts.popup !== false) focusPopup(id);
  else centerOnNodes([id]);
  return true;
}

/* --------------------------------------------- the eye assignment filter (V0.42) === */

/**
 * The nodes carrying MY assignments — direct node assignments, plus the owning nodes of the
 * documents I am doc-responsible for. The doc→node direction the resolver deliberately lacks,
 * scanned client-side over the four slots + custom list. Built here, off `myAssignments`
 * (every language, as stored rows), so a French assignment lights its node while I read
 * English — the resolved-locale predicate would have missed it.
 */
function assignedNodeSet(): Set<NodeId> {
  const set = new Set<NodeId>();
  for (const a of S.myAssignments.nodes) if (S.nodes[a.nodeId]) set.add(a.nodeId);
  if (S.myAssignments.docs.length) {
    const wanted = new Set(S.myAssignments.docs.map((d) => d.docPageId));
    for (const id in S.nodes) {
      const n = S.nodes[id];
      if (n.root) continue;
      const owns =
        (n.docId && wanted.has(n.docId)) ||
        (n.internalDocId && wanted.has(n.internalDocId)) ||
        (n.courseDocId && wanted.has(n.courseDocId)) ||
        (n.demoDocId && wanted.has(n.demoDocId)) ||
        (n.customDocs ?? []).some((d) => wanted.has(d));
      if (owns) set.add(id);
    }
  }
  return set;
}

/** Recompute the eye's highlight set — empty when the eye is off. */
export function recomputeAssignHighlight(): void {
  S.assignHighlight = S.eyeOn ? assignedNodeSet() : new Set();
}

/**
 * Toggle the eye assignment filter (V0.42) — the "My assignments" reveal. A transient VIEW
 * toggle, never synced to the server: it reveals the branches down to my assigned nodes,
 * highlights them and dims the rest (the house highlight-and-dim, the FIFTH layer). Relayouts
 * through the same `forceOpen` path as every other filter, so branches re-pack when it clears.
 */
export function setEyeAssignments(on: boolean): void {
  const want = !!on;
  if (want === S.eyeOn) return;
  S.eyeOn = want;
  recomputeAssignHighlight();
  applyReveal();
  render();
  if (want && S.assignHighlight.size) {
    requestAnimationFrame(() => centerOnNodes([...S.assignHighlight]));
  }
}

export function toggleEyeAssignments(): void {
  setEyeAssignments(!S.eyeOn);
}

/* ================================================================ the AI-doc surface === */

/** AI-doc mode: the top-left toolbar. Edit-mode and AI-admin only. */
export function setAiDocMode(on: boolean): void {
  const want = !!on && S.editing && canAiAdmin();
  const was = S.aiDocMode;
  S.aiDocMode = want;
  announce(); // the AI doc button's highlight reads aiDocMode
  document.getElementById('aidoc-toolbar')?.classList.toggle('show', want);
  if (want) {
    applySidebar(); // anchor the toolbar right of the Tags toggle for the current sidebar state
    // Both feed the node popup's AI panel — the config for its "Inherit (on/off)" labels, the
    // targets for its live-system dropdown. Fetched on mode entry so the first popup open does
    // not have to wait on them.
    void loadAiDocCfgCache();
    void loadAppTargets();
  } else {
    // Leaving AI-doc mode clears the status filter and its highlight.
    S.activeDocStatus = '';
    syncAiDocFilterDot();
    applyReveal();
    render();
  }
  // NOTHING REFRESHES THE POPUP HERE ANY MORE (V0.50). This used to call `refreshPopup()`,
  // because the popup's AI panel was drawn only while this mode was on and would otherwise
  // sit stale — showing fields that no longer save, or hiding ones that now would. The panel
  // is gated on the AI_ADMIN right alone now (`aiModeAtOpen` in NodePopup.tsx), so this
  // toggle changes nothing inside an open popup, and a refresh from here would be the one
  // place edit-state is re-derived behind a popup that the region deliberately fixes AT OPEN.
  if (was !== want) saveViewState(); // AI-doc mode is part of the restored view
}

export const toggleAiDocMode = (): void => setAiDocMode(!S.aiDocMode);

/**
 * The AI generation console — the dashboard's `'ai-docs'` tab since V0.44.1. The NAME is kept:
 * the AI toolbar's "Generate documentation", the Logs / Targets / AI-context headers and the
 * regression suite all call it, and what they mean has not changed. The gate adds the deployment
 * half (`canAiAdminHere` — role AND `docgen` capability, the topbar button's own rule), because a
 * tab this could open would otherwise not be drawn.
 */
export function openAiDocModal(): boolean {
  if (!(S.editing && canAiAdminHere(S.perm, S.caps))) return false;
  return openDashboard('ai-docs');
}

export function afterAidocClose(): void {
  void flushDirtyAiFields(); // persist prompt edits
  render(); // …and reflect the derived-status changes on the map
}

export const flushAiFields = (): Promise<void> => flushDirtyAiFields();

/**
 * One node's AI prompt.
 *
 * Marked dirty rather than saved immediately: prompts are edited by the paragraph, and the
 * flush runs before every generation and on close.
 */
export function setAiPrompt(id: NodeId, text: string): void {
  const n = S.nodes[id];
  if (!n) return;
  n.aiPrompt = text;
  Writes.dirtyAiFields.add(id);
  announce();
}

/*
 * There was an `applyAidocTransition()` here until V0.38 — the console's per-row state
 * buttons pushing nodes to a stored `docStatus`. Both halves of it died with the workflow
 * unification: the column is gone, and TRANSITIONS HAPPEN ONLY WHERE THE TEXT IS READABLE —
 * the doc bar's WorkflowControl. Bulk approve has no honest replacement, which is the point.
 */

/**
 * "No doc" (V0.42): mark the selected node(s) as EXEMPT — plan = {}, "nothing expected here" —
 * the successor to the retired tag. Each write is `PUT /api/nodes/:id/doc-plan {plan:{}}` via
 * `setDocPlan`, which persists immediately (planning is not a debounced map edit) and updates
 * `expectedDocs` in place. Gated in the console on EDIT_ALL_DOCS, because `mayAssignNode` does
 * not admit a plain AI_ADMIN — without that gate the operator would get a wall of 403s.
 */
export async function applyAidocExempt(ids: NodeId[]): Promise<void> {
  for (const id of ids) {
    if (!S.nodes[id]) continue;
    await setDocPlan(id, {});
  }
  render();
  renderTags();
}

/** A page the generator just produced. In-memory only — the server has already stored it. */
export function applyGeneratedDoc(id: NodeId, docId: DocId | null, title: string | null): void {
  const n = S.nodes[id];
  if (!n) return;
  // The generation landed the page on 'generated' server-side (the mechanical transition);
  // mirror the derived stage locally so the console's tabs move without a map refetch. Only
  // where the viewer could see a stage at all — inventing one would invent knowledge.
  if (n.pipelineStage !== undefined) n.pipelineStage = 'generated';
  if (docId) {
    n.docId = docId;
    n.link = docUrlFor(docId);
    if (title) S.docTitles[docId] = title;
    S.docAiGenerated[docId] = true;
  }
  render();
}

/* ------------------------------------------------- single-node generation (V0.38) --- */

/** The body of the regeneration guard's 409, or null for any other failure. */
function regenGuardOf(err: unknown): { count: number; lastAt: string | null; lastBy: string | null } | null {
  const e = err as { status?: number; body?: { count?: unknown; lastAt?: unknown; lastBy?: unknown } } | null;
  if (!e || e.status !== 409) return null;
  return {
    count: typeof e.body?.count === 'number' ? e.body.count : 0,
    lastAt: typeof e.body?.lastAt === 'string' ? e.body.lastAt : null,
    lastBy: typeof e.body?.lastBy === 'string' ? e.body.lastBy : null,
  };
}

const GENERATE_POLL_MS = 1500;
const GENERATE_POLL_CAP = 400; // ~10 minutes — a hung job must not poll forever

/**
 * Generate ONE node's documentation — the responsible's relaunch loop (V0.38), and the ONE
 * implementation of the 409/confirm/force flow: the AiPanel's Generate button and the
 * WorkflowControl's Regenerate both come here, so the guard can never grow two wordings.
 *
 * THE GUARD IS THE POINT. Generation rewrites the primary page wholesale, so the server
 * refuses when human edits postdate the last generation — 409 `{count, lastAt, lastBy}` —
 * and this confirms with exactly those facts before retrying with `force: true`. Recovery
 * from a wrong confirm is the revision log, which V0.38 opened to the doc's edit scope.
 *
 * Dirty AI fields are flushed FIRST, so the prompt being relaunched is the one on screen.
 * The await covers the whole job: the button that called this shows busy until the page
 * exists (or failed), and the map/workflow state is refreshed from the result.
 */
export async function generateDocForNode(id: NodeId): Promise<boolean> {
  const n = S.nodes[id];
  if (!n) return false;
  await flushDirtyAiFields();

  let jobId: string;
  try {
    const r = await apiRequest<{ jobId: string }>('/api/ai/generate-docs', {
      method: 'POST',
      body: { nodeIds: [id], lang: S.currentLocale },
    });
    jobId = r.jobId;
  } catch (err) {
    const guard = regenGuardOf(err);
    if (!guard) {
      const msg = (err as { body?: { error?: string } } | null)?.body?.error;
      alert(msg || 'Could not start the generation.');
      return false;
    }
    const when = guard.lastAt ? new Date(guard.lastAt).toLocaleString() : null;
    const last = guard.lastBy ? ` — last by ${guard.lastBy}${when ? ` on ${when}` : ''}` : when ? ` — last on ${when}` : '';
    const ok = confirm(
      `${guard.count} human edit${guard.count === 1 ? '' : 's'} since the last generation${last}.\n\n` +
        `Regenerating overwrites them (the revision log keeps the history). Regenerate anyway?`,
    );
    if (!ok) return false;
    try {
      const r = await apiRequest<{ jobId: string }>('/api/ai/generate-docs', {
        method: 'POST',
        body: { nodeIds: [id], lang: S.currentLocale, force: true },
      });
      jobId = r.jobId;
    } catch (err2) {
      const msg = (err2 as { body?: { error?: string } } | null)?.body?.error;
      alert(msg || 'Could not start the generation.');
      return false;
    }
  }

  // Poll the job to completion, the console's cadence. Sequential awaits, not an interval:
  // the caller's busy state is the whole progress UI here, and it ends when this resolves.
  for (let tick = 0; tick < GENERATE_POLL_CAP; tick++) {
    await new Promise((resolve) => setTimeout(resolve, GENERATE_POLL_MS));
    let data: {
      finished?: boolean;
      nodes?: Record<NodeId, { state: string; docId?: string; title?: string; error?: string }>;
    };
    try {
      data = await apiRequest(`/api/ai/generate-docs/${encodeURIComponent(jobId)}`);
    } catch {
      continue; // a dropped poll is not a failed job; the next tick asks again
    }
    if (!data.finished) continue;
    const st = data.nodes?.[id];
    if (st?.state === 'done') {
      applyGeneratedDoc(id, st.docId ?? null, st.title ?? null);
      // The mechanical transition landed the page on 'generated'; re-ask the bulk map so the
      // chip, the dots and the mirror read the new standing without inventing it here.
      void loadDocWorkflows();
      if (S.heatmapOn) void loadMapHealth(); // a generation is this tab's own doc save too
      return true;
    }
    alert(st?.error ? `Generation failed: ${st.error}` : 'Generation failed.');
    return false;
  }
  alert('The generation is still running — check the AI console for its progress.');
  return false;
}

/**
 * The popup's Generate: write the draft's pending prompt onto the node first (the popup is
 * still open — nothing has saved), then run the one relaunch flow above. The same
 * write-pending-edits-first rule `runLiveVerifyFromPopup` follows, for the same reason: the
 * server reads the node's STORED prompt, and generating from a stale one would silently
 * ignore what is on screen.
 */
export async function generateDocFromPopup(id: NodeId, ai: AiDraft): Promise<boolean> {
  const n = S.nodes[id];
  if (!n) return false;
  if (applyAiFields(n, ai)) Writes.dirtyAiFields.add(n.id);
  return generateDocForNode(id);
}

/**
 * The suite-wide grounding config, cached.
 *
 * The node popup's per-source toggles need it to label their "Inherit" option with what
 * inheriting actually resolves to right now, and re-fetching that on every popup open would be
 * wasteful. Refreshed whenever the Context screen loads or saves, which is the only place these
 * values change.
 */
export async function loadAiDocCfgCache(force?: boolean): Promise<unknown> {
  if (S.aiDocCfgCache && !force) return S.aiDocCfgCache;
  if (!canAiAdmin()) return null;
  try {
    S.aiDocCfgCache = await apiRequest('/api/ai/doc-config');
    render(); // the popup's "Inherit (on/off)" labels
  } catch {
    /* leave the cache alone; callers fall back to "on" */
  }
  return S.aiDocCfgCache;
}

export function openAiDocConfig(): void {
  if (!canAiAdmin()) return; // still gated here: several call sites reach it directly
  handles.aidocConfig?.open();
}
export const closeAiDocConfig = (): void => handles.aidocConfig?.close();

export function setAiDocCfgCache(c: Record<string, unknown> | null): void {
  S.aiDocCfgCache = Object.assign({}, S.aiDocCfgCache || {}, c || {}) as typeof S.aiDocCfgCache;
  render();
}

/* ================================================== live-verification targets & runs === */

let appTargetsInFlight: Promise<unknown> | null = null;

export async function loadAppTargets(): Promise<unknown> {
  if (!canAiAdmin()) return S.appTargets;
  if (appTargetsInFlight) return appTargetsInFlight; // coalesce concurrent callers
  appTargetsInFlight = (async () => {
    try {
      const d = await apiRequest<{ targets?: typeof S.appTargets }>('/api/ai/targets');
      S.appTargets = d.targets ?? [];
      S.appTargetsLoaded = true;
    } catch (e) {
      console.error('Could not load targets', e);
    } finally {
      appTargetsInFlight = null;
    }
    // The node popup's dropdown reads this list, so a fetch landing while it is open has to
    // say so. The original re-opened the whole popup to get the same effect — which also
    // discarded anything typed into it.
    render();
    return S.appTargets;
  })();
  return appTargetsInFlight;
}

/**
 * The targets list, mirrored back from the screen that edits it.
 *
 * `appTargetsLoaded` travels with it because "no targets exist" and "we have not asked yet" are
 * different sentences in the node popup's dropdown.
 */
export function setAppTargets(list: typeof S.appTargets): void {
  S.appTargets = Array.isArray(list) ? list : [];
  S.appTargetsLoaded = true;
  render();
}

export function unmapDeletedTarget(id: string, remaining: typeof S.appTargets): void {
  setAppTargets(remaining);
  // The server un-mapped them (onDelete: SetNull); mirror it locally so an open popup cannot
  // offer a target that no longer exists.
  for (const nid in S.nodes) if (S.nodes[nid].appTargetId === id) S.nodes[nid].appTargetId = null;
  render();
}

export function openTargets(): boolean {
  if (!canAiAdmin()) return false;
  handles.targets?.open();
  Router.reportAdminScreen('targets', true);
  return true;
}
export const closeTargets = (): void => {
  Router.reportAdminScreen('targets', false);
  handles.targets?.close();
};

export function startLiveVerify(nodeId: NodeId): void {
  if (!canAiAdmin()) return;
  handles.liveVerify?.start(nodeId);
  Router.reportAdminScreen('live-verify', true);
}
export const closeLiveVerify = (): void => {
  Router.reportAdminScreen('live-verify', false);
  handles.liveVerify?.close();
};

/**
 * Mirror an approval locally so the popup says "Live-verified" without a reload.
 *
 * The server has already written it; this is the same fact, not a second one — and it is the
 * ONE write in the whole live-verification flow, because a finding never reaches documentation
 * generation without a person approving it.
 */
export function applyLiveVerifyApproval(nodeId: NodeId): void {
  const n = S.nodes[nodeId];
  if (!n) return;
  n.liveObservationAt = new Date().toISOString();
  // NO RE-OPEN (V0.45). `AiPanel` reads `node.liveObservationAt` off the live node, so the
  // publish `render()` performs is what shows the new "Live-verified" line — and re-opening
  // would have discarded whatever was being typed in the popup to get here.
  render();
}

/* ======================================================================= document log === */

/**
 * "The history may have changed."
 *
 * A counter rather than a flag: the timeline needs to know its own list gained an entry, and
 * nothing in the map state would tell it.
 */
export function renderDocLogs(): void {
  S.docRevision++;
  announce();
}

export const openLogDetail = (id: string): void => handles.logDetail?.open(id);
export const closeLogDetail = (): void => handles.logDetail?.close();

/** The page on screen was rendered from the text that has just been replaced. */
export async function afterRevisionRestored(): Promise<void> {
  if (docEditorActive()) {
    // Re-open in place: the sequence is what the editor's fetch (and its sitting) re-run on.
    S.docOpenSeq++;
    docStateChanged();
  } else renderDocLogs();
}

/**
 * An address just changed, or was accepted (V0.45.1).
 *
 * REFETCH RATHER THAN PATCH. Three things move at once — the slug itself, the drift mark, and
 * the alias that keeps the old address working — and all three arrive from `/api/map`. Patching
 * local copies of them would put a second derivation of "what address is this at" beside the
 * one the payload already carries, which is the pair this codebase keeps paying for.
 *
 * A node's popup stays open across it: `render()` publishes and the popup reads the live node,
 * so the mark clears where it is. The URL is re-stated for the same reason `adoptNodeSlug`
 * does it — the address bar is showing the old slug, and replacing beats pushing because it is
 * the same place, newly addressed.
 */
export async function afterReslug(kind: 'node' | 'doc', id: NodeId | DocId, slug: string): Promise<void> {
  try {
    await loadMapFromServer();
  } catch (e) {
    console.error('Failed to reload the map after a re-slug:', e);
    return;
  }
  render();
  renderTags();
  // The doc view holds its own copy of the title bar's facts; the map reload does not touch it.
  if (kind === 'doc' && S.docCurrentId === id) S.docSlugs[id] = slug;
  Router.restateUrl();
}

/**
 * The DOCUMENT LOG, which is a view of the dashboard's Logs tab since V0.49.
 *
 * KEEPS ITS NAME AND ITS GATE. Four buttons and `e2e/25-logs.spec.ts` call `openLogs()`, and
 * the spec asserts it is REFUSED without AI_ADMIN — the document log's own gate, unchanged,
 * because the prompts and coverage reports it shows are operator tooling. Somebody holding
 * only SHOW_LOGS reaches the Actions view and not this one.
 */
export function openLogs(): boolean {
  if (!canAiAdmin()) return false;
  return openDashboard('logs', { logsView: 'history' });
}

/* ============================================================== the remaining screens === */

export const openHelp = (): void => handles.help?.open();

/* No permission gate on purpose: the privacy page is FOR the person who has not signed in
   yet, and it holds nothing but this app's own authored prose. */
export const openPrivacy = (): void => handles.privacy?.open();
export const closePrivacy = (): void => handles.privacy?.close();

/**
 * Open the dashboard — on `tab` when one is named (a deep link, a header button that means a
 * specific tab), otherwise on the tab it last showed (V0.44.1: the region remembers). The tab
 * key IS the admin-screen word, so the URL reported is the tab itself. Idempotent: called while
 * open, it only switches the tab, which is how back/forward between two dashboard URLs never
 * unmounts the region.
 */
export function openDashboard(
  tab?: DashboardScreen,
  opts?: { logsView?: 'actions' | 'history'; nodeId?: NodeId },
): boolean {
  /**
   * THE EDIT-MODE BACKSTOP, AND THE TWO TABS EXEMPT FROM IT (V0.49, V0.50.2).
   *
   * The rule exists because the button that opens this region lives in the edit toolbar, so a
   * caller reaching it outside edit mode is reaching past a control that is not on screen. The
   * Logs tab is not that: it is a READ surface reserved to a role, its own gate is SHOW_LOGS,
   * and `/admin/logs` has been a shareable address since V0.45 — specs 25 and 39 open it
   * without touching Edit. Requiring edit mode there would break an address people already
   * hold, to enforce a rule about a button it does not use.
   *
   * The Projects tab (V0.50.2) is the second: a read surface gated on VIEW_TODOS, shareable as
   * `/admin/projects`, and reached from the popup's Progress button by a to-do manager who may
   * hold no editing brick and therefore never sees the Edit toggle at all.
   */
  if (tab === 'logs') {
    if (!(canViewLogsFor(S.perm) || canAiAdmin())) return false;
  } else if (tab === 'projects') {
    if (!canViewTodosFor(S.perm)) return false;
  } else if (!S.editing) {
    return false;
  }
  const landed = handles.dashboard?.open(tab, opts) ?? tab ?? 'dashboard';
  Router.reportAdminScreen(landed, true); // synchronous, so a URL application sees it at once
  return true;
}

/**
 * "Progress" from a node's To-dos section (V0.50.2): leave the popup and land on the dashboard's
 * Projects tab with THIS node's project selected. One action, `showDocNodeOnMap`'s reason: the
 * closes happen in an order no call site can get wrong — the popup first (its close lands the
 * URL on the map beneath), then the dashboard opens over it on the tab and the node named.
 * Gated exactly as the tab is; a node this reader cannot see is not on their map at all.
 */
export function openProjectProgress(nodeId: NodeId): boolean {
  if (!canViewTodosFor(S.perm) || !S.nodes[nodeId]) return false;
  if (S.popupFor) closePopup();
  return openDashboard('projects', { nodeId });
}
export const closeDashboard = (): void => {
  if (isDashboardScreen(S.adminScreen)) Router.reportAdminScreen(S.adminScreen, false);
  handles.dashboard?.close();
};

/**
 * The tab the permissions page may land on for THIS reader (V0.54.5): the one asked for when
 * they may see it, otherwise their first. Backups is `BACKUP_ADMIN`'s; the other three are
 * `SHOW_USER_PERMISSIONS`'. A shared `/admin/permissions/backups` opened by somebody without
 * the backup role therefore lands on Users & Groups rather than on a tab that is not drawn.
 */
function allowedPermissionTab(want?: PermissionTab): PermissionTab {
  const admin = canUserPerms();
  if (want && (want === 'backups' ? canBackupAdmin() : admin)) return want;
  return admin ? 'users' : 'backups';
}

export function openPermPage(tab?: PermissionTab): boolean {
  // EITHER permission opens the page (V0.48); which TABS it then draws is the page's own
  // decision. A backup administrator who holds nothing else gets one tab and no user list —
  // which is the point of a standalone role, and would be unreachable if this gate were the
  // user-administration one alone.
  if (!canUserPerms() && !canBackupAdmin()) return false;
  const start = allowedPermissionTab(typeof tab === 'string' ? tab : undefined);
  // BEFORE the screen is reported: that report writes the URL, and it must already carry the
  // tab, or opening the page would push `/admin/permissions` and then `/…/users` on top of it.
  S.permissionsTab = start;
  handles.permissions?.open(start);
  Router.reportAdminScreen('permissions', true);
  return true;
}
/** Switch the open permissions page to a tab — the router's way in, for back/forward (V0.54.5). */
export function setPermissionsTab(tab: PermissionTab): void {
  const t = allowedPermissionTab(tab);
  handles.permissions?.setTab(t);
  Router.reportPermissionsTab(t);
}
export const closePermPage = (): void => {
  Router.reportAdminScreen('permissions', false);
  handles.permissions?.close();
};

/** Their own display name shows on their own avatar and user chip. */
export function setCurrentDisplayName(name: string): void {
  if (!S.currentUser) return;
  S.currentUser = { ...S.currentUser, displayName: name };
  announce();
}

/* ==================================================================== undo & presence === */

export const pushUndo = (label: string, undo: () => Promise<void> | void): void =>
  Undo.pushUndo(label, undo);
export const undoLabel = (): string | null => Undo.undoLabel();
export const performUndo = (): Promise<void> => Undo.performUndo();

export const connectPresence = (): void => Presence.connectPresence();
export const disconnectPresence = (): void => Presence.disconnectPresence();
export const sendLoc = (): void => Presence.sendLoc();
/** Grouping the roster by node happens on the React side; this only says the roster moved. */
export const rebuildPresence = (): void => announce();

/**
 * Where am I right now? Since plan 1.6 this is DERIVED from the same `AppLocation` the URL is
 * derived from — `toPresenceLoc(currentAppLocation(), resolver)` — so the presence socket and
 * the address bar are provably one model rather than two hand-kept copies of the same cases.
 */
export function myPresenceLoc(): Presence.PresenceLoc {
  return Router.presenceLoc();
}

/* ================================================================= session & language === */

/** Say the login state may have changed. The overlay DERIVES its visibility from it. */
export const showLogin = (): void => announce();
export const hideLogin = (): void => announce();

/** The confirmation stays on this side so the button keeps its native dialog. */
export function confirmLogout(): void {
  if (confirm('Log out?')) void logout();
}

/**
 * Apply the current permissions to the running app.
 *
 * The buttons are drawn by React from `perm` and `caps`; what belongs here is the CONSEQUENCES,
 * which are state rather than chrome.
 */
export function applyPermissions(): void {
  refreshHiddenSets();
  // Validated tag by tag: losing access to one selected tag must not drop the others.
  if ([...S.activeTags].some((t) => tagHidden(t))) {
    S.activeTags = new Set([...S.activeTags].filter((t) => !tagHidden(t)));
  }
  // A user who may not enter edit mode must not STAY in it.
  if (!canEnterEdit() && S.editing) setEditing(false);
  // The AI-doc button is edit-mode + AI-admin gated; without the role, force the mode and its
  // filter off.
  if (!canAiAdmin()) {
    setAiDocMode(false);
    if (S.activeDocStatus) S.activeDocStatus = '';
  }
  applyLiveVerifyCapability();
  if (S.currentUser && canPresence()) connectPresence();
  else disconnectPresence();
  syncScopeUI(); // the internal/course facets depend on doc-button permissions
  renderTags();
  render();
  // The user chip and the Permissions button are React's, and it re-fits the top bar itself
  // when they change — render() above is what tells it they did.
}

/**
 * Reload the page.
 *
 * A one-line action, and it earns its place for one reason: `location.reload` cannot be
 * replaced. `Location` is unforgeable — the browser refuses to let a script assign over it or
 * redefine its methods — so a test that needs to observe "this would have reloaded" without
 * destroying its own page has nowhere to stand unless the app reloads through a name it can
 * swap. `callAction` resolves through the table on every call and `registerActions()` can
 * replace an entry, so this is that name.
 *
 * ONE CALLER TODAY: the Backups tab, when a restore it started has finished (V0.48). Nothing
 * short of a reload is honest there — every node, document, tag, role and user in memory came
 * from a database that no longer exists, and reconstructing them piecemeal is a worse version
 * of what boot already does properly.
 */
export function reloadPage(): void {
  location.reload();
}

/** Re-resolve the signed-in user's permissions (after an admin edits roles/groups) and re-apply. */
export async function reapplyCurrentUser(): Promise<void> {
  if (!S.currentUser) return;
  const before = hiddenKey(S.perm);
  const ok = await establishSession();
  if (!ok) {
    void logout();
    return;
  }
  applyPermissions();
  // THE MAP ITSELF MAY HAVE CHANGED, and `dataVersion` will not have moved to say so — a
  // permission edit touches no map content. Before V0.43 re-filtering in the browser was
  // enough, because every node was already here; now the server withholds them, so GRANTING
  // access would show nothing at all until a reload, and REVOKING it would leave the branch
  // fully populated in `S.nodes` for the rest of the session. Refetch, but only when the two
  // fields the payload actually depends on moved.
  if (hiddenKey(S.perm) !== before) onPermissionsChanged?.();
}

/* ========================================================================= the table === */

/**
 * Every action a region may call, by name.
 *
 * `callAction('save')` rather than `actions.save()` at ~100 call sites, kept because that is
 * what those call sites already say — but the name is now checked at COMPILE time against this
 * table, where the bridge's string registry could only fail at runtime with a console error and
 * a control that silently did nothing.
 */
export const actions = {
  // --- tag tree ---
  toggleActiveTag,
  setActiveTags,
  addTag,
  toggleTagCollapsed: (tid: TagId) => {
    const t = S.tags[tid];
    if (!t) return;
    t.collapsed = !t.collapsed;
    renderTags();
    // Tag collapse is TAG STRUCTURE (it syncs, and collapses the group for everybody), so
    // persisting it is an EDIT_TAGS write. Anyone else keeps the local fold they always had —
    // absorbed into the delta baseline so it can never ride a LATER delta into a 403.
    if (canEditTags()) {
      save();
    } else {
      Writes.absorbLocalTagFold(tid, !!t.collapsed);
      saveLocal();
    }
  },
  // Live preview while the colour picker is open: repaint the nodes, record nothing.
  previewTagColor: (tid: TagId, color: string) => {
    const t = S.tags[tid];
    if (!t) return;
    t.color = color;
    render();
  },
  setTagColor: (tid: TagId, color: string) => {
    const t = S.tags[tid];
    if (!t) return;
    const prevColor = t.color;
    t.color = color;
    renderTags();
    render();
    save();
    if (prevColor !== color) {
      pushUndo('recolor "' + t.name + '"', async () => {
        const cur = S.tags[tid];
        if (!cur) return;
        cur.color = prevColor;
        renderTags();
        render();
        save();
      });
    }
  },
  setTagName,
  addSubTag: (tid: TagId) => {
    void addTag('New tag', tid);
    const t = S.tags[tid];
    if (t) t.collapsed = false;
    renderTags();
    save();
  },
  toggleTagInvisible: (tid: TagId) => {
    const t = S.tags[tid];
    if (!t) return;
    t.invisible = !t.invisible;
    renderTags();
    save();
  },
  deleteTag,
  moveTag,

  // --- sign-in ---
  attemptLogin,
  enterApp,

  // --- top bar ---
  toggleExpandAll,
  toggleLinks,
  toggleTheme,
  hideHint,
  openHelp,
  openPrivacy,
  closePrivacy,
  openPermPage,
  setPermissionsTab,
  reloadPage,
  setLocale,
  logout: confirmLogout,
  // The name ~5 call sites and one spec have always used, pointing at the overflow-aware
  // fit controller since plan 1.3 retired chrome.ts's search-hiding fitTopbar().
  fitTopbar: requestTopbarFit,
  toggleEditing,
  armLink,
  realign,
  fit,
  performUndo,
  openDashboard,
  openProjectProgress,
  setNodeProject,
  adoptNodeProject,
  loadProjectProgress,
  toggleAiDocMode,
  toggleHeatmap,
  setDocsPinned,
  toggleDocsPinned,
  setSearchText,
  setSearchScope,
  setSearchDocKind,
  setSearchContentHits,

  // --- AI documentation ---
  // The two loaders are here because the node popup's AI panel fetches them itself now
  // (V0.50): it is no longer drawn behind AI-doc mode, whose entry used to do it.
  loadAiDocCfgCache,
  loadAppTargets,
  setAiDocCfgCache,
  setAiPrompt,
  flushAiFields,
  applyAidocExempt,
  applyGeneratedDoc,
  generateDocForNode,
  generateDocFromPopup,
  afterAidocClose,
  openAiDocConfig,
  openAiDocModal,
  setAppTargets,
  unmapDeletedTarget,
  openTargets,
  applyLiveVerifyApproval,

  // --- users, groups & permissions ---
  reapplyCurrentUser,
  setCurrentDisplayName,

  // --- chat ---
  setChatHighlight,
  revealNodeOnMap,
  showDocNodeOnMap,
  setEyeAssignments,
  toggleEyeAssignments,

  // --- my items (plan 2.4) + assignments (V0.38) ---
  toggleFavorite,
  centerOnNodes,
  loadMyAssignments,
  loadMyTodos,
  notifyTodosChanged,
  setTodoDone,
  setSectionCollapsed,
  setSectionOpen,
  revealTodo,
  consumeFocusTodo,

  // --- the bell (plan Phase 3) ---
  toggleSubscription,
  refreshFeedBadge,
  markFeedSeen,
  markNotificationRead,

  // --- the documentation workflow + responsibles (V0.38, over V0.37's scope tables) ---
  loadDocWorkflows,
  loadUserDirectory,
  loadMapHealth,
  setDocWorkflow,
  noteDocWorkflow,
  setNodeResponsibles,
  setDocResponsibles,
  setDocPlan,
  translateDoc,

  // --- routing (plan 1.6) and the presence jump (plan 1.7) ---
  dismissRouteNotice: Router.dismissRouteNotice,
  openPresenceLoc: Router.openPresenceLoc,

  // --- documents ---
  attachDocToTarget,
  detachDocFromTarget,
  createDocForNode,
  createPlannedDocForNode,
  discardProvisionalDoc,
  keepProvisionalDoc,
  deleteDocEverywhere,
  nodesUsingDoc,
  noteDocMeta,
  openDoc,
  openDocEditor,
  openDocInCurrentMode,
  openDocFromNode,
  openDocPicker,
  closeDoc,
  leaveDocEditor,
  beginDocEditSession,
  endDocEditSession,
  navOpenNode,
  setDocCurrentTitle,
  noteDocTitle,
  afterDocSaved,
  forgetDoc,
  openLogDetail,
  afterRevisionRestored,
  afterReslug,

  // --- the map ---
  toggleCollapsed,
  toggleMinimap,
  linkNodes,
  createNodeAt,
  renameNode,
  undoDrag,
  addChild,
  reparent,
  delNode,
  openLinkMenu,
  pushUndo,
  save,

  // --- the right-click menus (V0.54) ---
  openContextMenu,
  closeContextMenu,
  beginRename,
  expandAll,
  collapseAll,
  expandBeneath,
  collapseAllBut,
  frameBranch,
  addSibling,
  setNodeColour,
  startLinkFrom,
  startMove,
  cancelMove,
  takeMoveClick,
  finishMove,
  copyNode,
  clearClipboard,
  pasteInto,
  pasteAt,
  duplicateNode,
  openNodeTasks,
  consumeFocusTodoAdd,
  openNodeTags,
  copyText,
  nodeAddress,
  docAddress,
  openNodeDoc,
  openDocInNewTab,
  goToNode,
  setLinkColour,
  removeLink,
  clearFilters,
  setMinimapBright,
  // The half of `save()` that never reaches the wire — every view-only control calls this.
  saveLocal,
  // And the half of THAT which is only this user's view record: no durable prefs, no "Saved"
  // badge. An animated camera settling is the caller (V0.46) — it has moved nothing but the
  // camera, and the gesture that started it has usually badged its own save already.
  saveViewState,

  // --- the node popup ---
  openPopup,
  // The reader's open: centre, then the node takes its seat (V0.47). `openPopup` is the
  // instant verb for the router, a node just created, and the page global.
  focusPopup,
  setPopupFor,
  savePopup,
  removeCustomDoc,
  runLiveVerifyFromPopup,
};

/* Boot owns these; they are re-exported because regions and the page scope call them by
   name and should not have to know which module they live in. */
export { attemptLogin, enterApp, establishSession, logout, setLocale };

export type ActionName = keyof typeof actions;

/**
 * Call an action by name.
 *
 * The arguments stay `unknown[]`: every call site was written against a string registry, and
 * narrowing them here would be a second change riding along with the flip. The NAME is checked,
 * which is the half that used to fail silently at runtime.
 */
export function callAction(name: ActionName, ...args: unknown[]): unknown {
  const fn = actions[name] as (...a: unknown[]) => unknown;
  return fn(...args);
}

